import { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import conversationEventsChannel, {
  releaseConversationEventsSubscription,
  sendConversationTyping,
} from '../../actionCable/channels/conversationEvents.js';
import mlsGroupChannel, { releaseGroupSubscription } from '../../actionCable/channels/mlsGroup.js';
import { useRealtimeConsumer } from '../../actionCable/useRealtimeConsumer.js';
import {
  convergeMessengerV2ActiveThreadReady,
  fetchMessengerV2Conversation,
  recoverMessengerV2ConversationCatalog,
  synchronizeMessengerV2ActiveThread,
} from './runtime/activeThreadService.js';
import { resolveMessengerV2AuthSnapshot } from './runtime/authSnapshot.js';
import ensureMessengerV2Bootstrap from './runtime/bootstrapRuntime.js';
import {
  MESSENGER_V2_COMPOSER_ACTIVITY_TYPING,
  normalizeMessengerV2ComposerActivity,
} from './runtime/composerActivity.js';
import { createMessengerV2ComposerActivityPublisher } from './runtime/composerActivityPublisher.js';
import {
  createMessengerV2Conversation,
  loadMessengerV2Contacts,
  loadMessengerV2Conversations,
  normalizeMessengerV2Conversation,
  openMessengerV2GroupConversation,
} from './runtime/conversationService.js';
import {
  deleteMessengerV2Message,
  editMessengerV2Message,
  ensureMessengerV2SendReady,
  sendMessengerV2Message,
} from './runtime/messageSendService.js';
import {
  clearMessengerV2Conversation,
  completeMessengerV2MultipartAttachmentUpload,
  createMessengerV2MultipartAttachmentUpload,
  deleteMessengerV2DirectConversation,
  fetchMessengerV2CallTargetClient,
  getMlsDeviceIdentity,
  refreshMessengerV2MultipartAttachmentUpload,
} from './runtime/messengerV2Api.js';
import { resolveMessengerV2Manager } from './runtime/messengerV2MlsGateway.js';
import { isMessengerV2ReadOnlyGroup } from './runtime/groupReadiness.js';
import {
  createMessengerV2AttachmentEnvelope,
  createMessengerV2CallEventEnvelope,
  createMessengerV2CallSignalEnvelope,
  createMessengerV2EditEnvelope,
  createMessengerV2ReactionEnvelope,
  isMessengerV2CallMessage,
  parseMessengerV2Envelope,
  resolveMessengerV2AttachmentSummaryText,
  resolveMessengerV2EnvelopeDisplayText,
} from './runtime/messageEnvelope.js';
import {
  prepareMessengerV2AttachmentUpload,
  revokeMessengerV2AttachmentDraftPreview,
  uploadResumablePreparedMessengerV2Attachment,
} from './runtime/attachmentTransport.js';
import {
  getMessengerV2AttachmentUploadJob,
  getMessengerV2AttachmentUploadPayload,
  listMessengerV2AttachmentUploadJobs,
  removeMessengerV2AttachmentUploadJob,
  removeMessengerV2AttachmentUploadPayload,
  requestMessengerV2AttachmentBackgroundSync,
  saveMessengerV2AttachmentUploadJob,
  updateMessengerV2AttachmentUploadJob,
  updateMessengerV2AttachmentUploadPayload,
} from './runtime/attachmentUploadQueue.js';
import { materializeMessengerV2ImageEdit } from './runtime/imageEditRenderer.js';
import {
  createMessengerV2AttachmentError,
  normalizeMessengerV2AttachmentError,
} from './runtime/attachmentErrors.js';
import {
  createMessengerV2ConversationNotificationKey,
  ensureMessengerV2NotificationServiceWorker,
  getMessengerV2NotificationPermission,
  isMessengerV2ConversationNotificationMuted,
  resolveMessengerV2NotificationBody,
  resolveMessengerV2ConversationNotificationUrl,
  resolveMessengerV2NotificationVisibility,
  shouldShowMessengerV2ConversationNotification,
  traceMessengerV2NotificationEvent,
  showMessengerV2ConversationNotification,
  MESSENGER_V2_CALL_STATE_EVENT,
  MESSENGER_V2_INCOMING_CALL_EVENT,
} from '../../lib/messengerDesktopNotifications.js';
import {
  createInitialMessengerV2State,
  messengerV2Reducer,
} from './runtime/messengerRuntimeReducer.js';
import { createInitialMessengerV2CallState, isMessengerV2CallOwnedElsewhere } from './runtime/messengerCallState.js';
import { decryptHistory, persistHistoryKey, randomUuid } from './runtime/messengerV2Platform.js';
import { leaveMessengerV2Presence, pingMessengerV2Presence } from './runtime/presenceService.js';
import { publishMessengerV2ReadReceipt } from './runtime/readReceiptService.js';
import { createMessengerV2ReadReceiptCoordinator } from './runtime/readReceiptCoordinator.js';
import {
  messengerV2FrontierCovers,
  resolveMessengerV2TimelineFrontier,
  selectMessengerV2AdvancedFrontier,
} from './runtime/receiptFrontier.js';
import { resolveMessengerV2ReactionNotification } from './runtime/reactionNotification.js';
import { createMessengerV2SyncCoordinator } from './runtime/messengerSyncCoordinator.js';
import { createMessengerV2RuntimeRecoveryCoordinator } from './runtime/runtimeRecoveryCoordinator.js';
import {
  persistMessengerV2Runtime,
  restoreMessengerV2Runtime,
} from './runtime/runtimePersistence.js';
import { resolveMessengerV2OutboxFailure } from './runtime/outboundRetryPolicy.js';
import {
  clearMessengerV2WindowActivity,
  isMessengerV2ConversationVisibleInAnyWindow,
  resolveMessengerV2WindowVisibility,
  updateMessengerV2WindowActivity,
} from './runtime/windowActivityRegistry.js';
import {
  loadMessengerV2ThreadHistory,
  loadMessengerV2ThreadTimeline,
} from './runtime/threadTimelineService.js';
import {
  playMessengerV2CallEndedTone,
  startMessengerV2IncomingCallRingtone,
  startMessengerV2OutgoingCallTone,
  startMessengerV2OutgoingRingingTone,
} from './runtime/voiceCallAlerts.js';
import { loadMessengerV2VoiceCallConfig } from './runtime/voiceCallConfigService.js';
import {
  MessengerV2VoiceCallSession,
  isMessengerV2VoiceCallPqE2eeSupported,
  isMessengerV2VoiceCallSupported,
} from './runtime/voiceCallSession.js';
import {
  createMessengerV2SfuCall,
  joinMessengerV2SfuCall,
  loadActiveMessengerV2SfuCalls,
  loadMessengerV2SfuCall,
  loadMessengerV2SfuCallInvite,
  transitionMessengerV2SfuCall,
} from './runtime/sfuCallService.js';
import { MessengerV2SfuCallSession } from './runtime/sfuCallSession.js';
import { requestMessengerV2CallScreenWakeLock } from './runtime/sfuCallMediaPolicy.js';
import { createMessengerV2CallLifecycleCoordinator } from './runtime/callLifecycleCoordinator.js';
import { syncMessengerV2WebPushSubscription } from './runtime/webPushSubscriptionService.js';
import { collectMessengerV2CallParticipantMedia } from './runtime/messengerCallParticipants.js';
import { ensureFreshAuthToken, protectedUrl } from '../../service/request.js';

const DEFAULT_MLS_CIPHERSUITE = 0x004d;
const REMOTE_COMPOSER_ACTIVITY_TTL_MS = 4500;
const PRESENCE_PING_INTERVAL_MS = 15000;
const MLS_KEY_PACKAGE_INVENTORY_REFRESH_INTERVAL_MS = 60000;
const REMOTE_PRESENCE_TTL_MS = 55000;
const VOICE_CALL_RING_TIMEOUT_MS = 60000;
const VOICE_CALL_SETUP_TIMEOUT_MS = 30000;
const VOICE_CALL_START_READINESS_TIMEOUT_MS = 15000;
const VOICE_CALL_SIGNAL_SEND_TIMEOUT_MS = 12000;
const VOICE_CALL_MAX_BUFFERED_REMOTE_ICE = 64;
const VOICE_CALL_SIGNAL_CONTENT_TYPE = 'application/vnd.nettgrav.messenger-v2.call-signal+mls';
const ENCRYPTED_CALL_SIGNAL_RETRY_DELAYS_MS = [
  250, 750, 1500, 3000, 5000, 8000, 12000, 16000, 20000,
];
const ATTACHMENT_UPLOAD_RETRY_DELAYS_MS = [0, 1500, 4000];
const VOICE_CALL_PROCESSED_SIGNAL_CACHE_LIMIT = 500;
const VOICE_CALL_SILENT_SIGNAL_FRONTIER_CACHE_LIMIT = 500;
const VOICE_CALL_SILENT_SIGNAL_FRONTIER_TTL_MS = 5 * 60 * 1000;
const VOICE_CALL_SIGNAL_STALE_TTL_MS =
  2 * Math.max(VOICE_CALL_RING_TIMEOUT_MS, VOICE_CALL_SETUP_TIMEOUT_MS);
const VOICE_CALL_DEVICE_HEARTBEAT_INTERVAL_MS = 25000;
const VOICE_CALL_UNLOAD_WARNING =
  'A call is in progress. Reloading or leaving this page will end the call.';
const LIVEKIT_CALL_PROVIDER = 'livekit';
const LIVEKIT_MEDIA_E2EE_VERSION = 2;
const GROUP_CALL_TARGETED_SIGNAL_TYPES = new Set(['ringing', 'busy', 'reject']);
const VOICE_CALL_TERMINAL_STATUSES = new Set([
  'idle',
  'ended',
  'declined',
  'missed',
  'failed',
  'cancelled',
]);
const VOICE_CALL_MEDIA_ACTIVE_STATUSES = new Set([
  'outgoing',
  'ringing',
  'connecting',
  'active',
  'reconnecting',
]);
const HIDDEN_MLS_MESSAGE_EVENT_TYPES = new Set([
  'hidden_mls_message',
  'hidden:mls_message',
  'mls_hidden_message',
]);

const scheduleMessengerV2BackgroundTask = (callback) => {
  if (typeof callback !== 'function') {
    return () => {};
  }
  if (
    typeof window !== 'undefined' &&
    typeof window.requestIdleCallback === 'function' &&
    typeof window.cancelIdleCallback === 'function'
  ) {
    const handle = window.requestIdleCallback(callback, { timeout: 1200 });
    return () => window.cancelIdleCallback(handle);
  }
  if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
    const handle = window.setTimeout(callback, 80);
    return () => window.clearTimeout(handle);
  }
  callback();
  return () => {};
};

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const createActiveThreadRunKey = ({ conversationId, userId, clientId }) =>
  [toStringOrNull(conversationId), toStringOrNull(userId), toStringOrNull(clientId) || 'no-client']
    .filter(Boolean)
    .join(':');

const isMessengerV2AccessDeniedError = (error = null) => {
  const status = Number(error?.status ?? error?.response?.status ?? error?.cause?.status ?? 0);
  if (status === 401 || status === 403) {
    return true;
  }

  const message = String(error?.message ?? error ?? '').toLowerCase();
  return (
    message.includes('http 401') ||
    message.includes('http 403') ||
    message.includes('forbidden') ||
    message.includes('not authorized') ||
    message.includes('not_authorized')
  );
};

const isMessengerV2ForbiddenError = (error = null) => {
  const status = Number(error?.status ?? error?.response?.status ?? error?.cause?.status ?? 0);
  if (status === 403) return true;

  const message = String(error?.message ?? error ?? '').toLowerCase();
  return (
    message.includes('http 403') ||
    message.includes('forbidden') ||
    message.includes('not authorized') ||
    message.includes('not_authorized')
  );
};

const createMessengerV2SendNotReadyError = (message = 'Active V2 thread is not ready for send') =>
  Object.assign(new Error(message), { code: 'E2EE_NOT_READY' });

const createCallRecipientDeviceUnavailableError = () =>
  Object.assign(new Error('Call recipient has no registered encrypted device'), {
    code: 'CALL_RECIPIENT_DEVICE_UNAVAILABLE',
  });

const createVoiceCallHistoryClientMsgId = (callId = null) => {
  const normalizedCallId = toStringOrNull(callId);
  return normalizedCallId ? `call-event:${normalizedCallId}` : null;
};

const findCanonicalMessengerV2Conversation = (snapshot = null, conversationId = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId || !Array.isArray(snapshot?.conversations)) {
    return null;
  }
  return (
    snapshot.conversations.find(
      (conversation) => toStringOrNull(conversation?.id ?? null) === normalizedConversationId,
    ) ?? null
  );
};

const mergeCanonicalMessengerV2Conversation = ({
  snapshot = null,
  conversationId = null,
  conversation = null,
} = {}) => {
  const normalizedConversationId =
    toStringOrNull(conversationId) ?? toStringOrNull(conversation?.id ?? null);
  const canonicalConversation = findCanonicalMessengerV2Conversation(
    snapshot,
    normalizedConversationId,
  );
  const resultConversation =
    toStringOrNull(conversation?.id ?? null) === normalizedConversationId ? conversation : null;

  if (!canonicalConversation) {
    return resultConversation;
  }

  if (!resultConversation) {
    return canonicalConversation;
  }

  const canonicalGroupId = resolveMessengerV2ConversationGroupId(canonicalConversation);
  const resultGroupId = resolveMessengerV2ConversationGroupId(resultConversation);
  if (canonicalGroupId && resultGroupId && canonicalGroupId !== resultGroupId) {
    return canonicalConversation;
  }

  const canonicalRecordGroupId = resolveConversationRecordGroupId(canonicalConversation);
  const resultRecordGroupId = resolveConversationRecordGroupId(resultConversation);
  if (
    canonicalRecordGroupId &&
    resultRecordGroupId &&
    canonicalRecordGroupId !== resultRecordGroupId
  ) {
    return canonicalConversation;
  }

  const mergedConversation = {
    ...canonicalConversation,
    ...resultConversation,
    meta: {
      ...(canonicalConversation.meta ?? {}),
      ...(resultConversation.meta ?? {}),
    },
  };

  if (
    !isConversationGroupRecord(canonicalConversation) &&
    !isConversationGroupRecord(resultConversation)
  ) {
    return mergedConversation;
  }

  const useResultMembership = shouldUseIncomingMembershipMetadata(
    canonicalConversation,
    resultConversation,
  );
  const membershipSource = useResultMembership ? resultConversation : canonicalConversation;
  const membershipFallback = useResultMembership ? canonicalConversation : resultConversation;
  const participantSource = useResultMembership ? resultConversation : canonicalConversation;
  const participantFallback = useResultMembership ? canonicalConversation : resultConversation;
  const mergedGroup = {
    ...(canonicalConversation.group && typeof canonicalConversation.group === 'object'
      ? canonicalConversation.group
      : {}),
    ...(resultConversation.group && typeof resultConversation.group === 'object'
      ? resultConversation.group
      : {}),
  };

  return {
    ...mergedConversation,
    kind:
      toStringOrNull(canonicalConversation?.kind ?? null) === 'group' ||
      toStringOrNull(resultConversation?.kind ?? null) === 'group'
        ? 'group'
        : (toStringOrNull(resultConversation?.kind ?? null) ??
          toStringOrNull(canonicalConversation?.kind ?? null) ??
          mergedConversation.kind),
    group_id:
      toStringOrNull(
        resultConversation?.group_id ??
          resultConversation?.groupId ??
          canonicalConversation?.group_id ??
          canonicalConversation?.groupId ??
          canonicalConversation?.group?.id ??
          null,
      ) ?? mergedConversation.group_id,
    groupId:
      toStringOrNull(
        resultConversation?.groupId ??
          resultConversation?.group_id ??
          canonicalConversation?.groupId ??
          canonicalConversation?.group_id ??
          canonicalConversation?.group?.id ??
          null,
      ) ?? mergedConversation.groupId,
    membership_updated_at:
      resolveMembershipUpdatedAt(membershipSource) ??
      resolveMembershipUpdatedAt(membershipFallback),
    participants_authoritative:
      readMembershipField(
        membershipSource,
        'participants_authoritative',
        'participantsAuthoritative',
      ) ??
      readMembershipField(
        membershipFallback,
        'participants_authoritative',
        'participantsAuthoritative',
      ),
    current_user_group_membership_status:
      readMembershipField(
        membershipSource,
        'current_user_group_membership_status',
        'currentUserGroupMembershipStatus',
      ) ??
      readMembershipField(
        membershipFallback,
        'current_user_group_membership_status',
        'currentUserGroupMembershipStatus',
      ),
    current_user_group_role:
      readMembershipField(membershipSource, 'current_user_group_role', 'currentUserGroupRole') ??
      readMembershipField(membershipFallback, 'current_user_group_role', 'currentUserGroupRole'),
    current_user_group_member_id:
      readMembershipField(
        membershipSource,
        'current_user_group_member_id',
        'currentUserGroupMemberId',
      ) ??
      readMembershipField(
        membershipFallback,
        'current_user_group_member_id',
        'currentUserGroupMemberId',
      ),
    current_user_group_removed_at:
      readMembershipField(
        membershipSource,
        'current_user_group_removed_at',
        'currentUserGroupRemovedAt',
      ) ??
      readMembershipField(
        membershipFallback,
        'current_user_group_removed_at',
        'currentUserGroupRemovedAt',
      ),
    participants: Array.isArray(participantSource?.participants)
      ? participantSource.participants
      : Array.isArray(participantFallback?.participants)
        ? participantFallback.participants
        : [],
    catalog_trusted:
      canonicalConversation.catalog_trusted === true || resultConversation.catalog_trusted === true,
    catalogTrusted:
      canonicalConversation.catalogTrusted === true || resultConversation.catalogTrusted === true,
    group: Object.keys(mergedGroup).length
      ? {
          ...mergedGroup,
          id:
            toStringOrNull(mergedGroup.id ?? null) ??
            toStringOrNull(canonicalRecordGroupId ?? resultRecordGroupId ?? null),
          membership_updated_at:
            resolveMembershipUpdatedAt({ group: membershipSource?.group }) ??
            resolveMembershipUpdatedAt({ group: membershipFallback?.group }) ??
            resolveMembershipUpdatedAt(membershipSource) ??
            resolveMembershipUpdatedAt(membershipFallback),
        }
      : mergedConversation.group,
    meta: {
      ...(canonicalConversation.meta ?? {}),
      ...(resultConversation.meta ?? {}),
    },
  };
};

const resolveMessengerV2ConversationGroupId = (conversation = null) =>
  toStringOrNull(conversation?.mls_group_id ?? null) ??
  toStringOrNull(conversation?.meta?.mls_group_id ?? null);

const resolveCompatibleMessengerV2ReadyInfo = (readyInfo = null, groupId = null) => {
  if (!readyInfo || typeof readyInfo !== 'object') {
    return null;
  }
  const normalizedGroupId = toStringOrNull(groupId);
  const readyInfoGroupId =
    toStringOrNull(readyInfo.groupId ?? null) ??
    toStringOrNull(readyInfo.group_id ?? null) ??
    toStringOrNull(readyInfo.runtimeHandle ?? null);
  if (normalizedGroupId && readyInfoGroupId && readyInfoGroupId !== normalizedGroupId) {
    return null;
  }
  return readyInfo;
};

const resolveMessengerV2SendContext = (snapshot = null) => {
  const activeConversationId = toStringOrNull(snapshot?.activeConversationId ?? null);
  const activeThread = snapshot?.activeThread ?? null;
  const conversation = findCanonicalMessengerV2Conversation(snapshot, activeConversationId);
  const groupId = resolveMessengerV2ConversationGroupId(conversation);
  const threadConversationId = toStringOrNull(conversation?.id ?? null);

  return {
    activeConversationId,
    activeThread,
    conversation,
    groupId,
    threadConversationId,
  };
};

const resolveConversationKind = (conversation = null) =>
  toStringOrNull(conversation?.kind ?? conversation?.meta?.kind ?? null);

const bytesToBase64 = (bytes) => {
  if (!(bytes instanceof Uint8Array) || bytes.length === 0) return null;
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  if (typeof btoa === 'function') return btoa(binary);
  if (typeof Buffer !== 'undefined') return Buffer.from(bytes).toString('base64');
  return null;
};

const createLiveKitMediaSecretB64 = () => {
  const cryptoRef =
    (typeof window !== 'undefined' ? window.crypto : null) ||
    (typeof global !== 'undefined' ? global.crypto : null);
  if (!cryptoRef || typeof cryptoRef.getRandomValues !== 'function') {
    throw new Error('Secure random media key generation is not available');
  }
  const keyBytes = new Uint8Array(32);
  cryptoRef.getRandomValues(keyBytes);
  const secretB64 = bytesToBase64(keyBytes);
  if (!secretB64) throw new Error('LiveKit media key generation failed');
  return secretB64;
};

const resolveLiveKitMediaSecretB64 = (mediaE2ee = null) =>
  toStringOrNull(mediaE2ee?.secretB64 ?? mediaE2ee?.secret_b64 ?? null);

const createLiveKitMediaE2eeOffer = (secretB64) => ({
  version: LIVEKIT_MEDIA_E2EE_VERSION,
  required: true,
  provider: LIVEKIT_CALL_PROVIDER,
  keyTransport: 'mls-hidden-message',
  algorithm: 'LiveKit E2EE shared key via MLS-PQ encrypted signaling',
  secretB64,
});

const isVoiceCallUnloadRisk = (call = null) => {
  const status = toStringOrNull(call?.status ?? null)?.toLowerCase();
  return Boolean(status && !VOICE_CALL_TERMINAL_STATUSES.has(status));
};

const normalizeVoiceCallSignalType = (value) => toStringOrNull(value)?.toLowerCase() || null;

const resolveVoiceCallSignalTransportContentType = () => VOICE_CALL_SIGNAL_CONTENT_TYPE;

const shouldPersistVoiceCallSignalHistory = () => true;

const resolveVoiceCallMediaStatePayload = (signalPayload = {}) => {
  const remoteMuted =
    typeof signalPayload?.muted === 'boolean'
      ? signalPayload.muted
      : typeof signalPayload?.media?.muted === 'boolean'
        ? signalPayload.media.muted
        : null;
  const remoteCameraEnabled =
    typeof signalPayload?.cameraEnabled === 'boolean'
      ? signalPayload.cameraEnabled
      : typeof signalPayload?.camera_enabled === 'boolean'
        ? signalPayload.camera_enabled
        : typeof signalPayload?.media?.cameraEnabled === 'boolean'
          ? signalPayload.media.cameraEnabled
          : typeof signalPayload?.media?.camera_enabled === 'boolean'
            ? signalPayload.media.camera_enabled
            : null;

  return {
    remoteMuted,
    remoteCameraEnabled,
    hasMediaState: remoteMuted != null || remoteCameraEnabled != null,
  };
};

const resolveMessengerV2CurrentClientId = (stateSnapshot = {}, conversationId = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const threadState = normalizedConversationId
    ? stateSnapshot.threadsByConversation?.[normalizedConversationId]
    : null;
  const isActiveConversation =
    normalizedConversationId &&
    String(stateSnapshot.activeConversationId ?? '') === normalizedConversationId;
  const threadClientId = toStringOrNull(
    threadState?.manager?.identityClientId ??
      threadState?.manager?.identity_client_id ??
      threadState?.readyInfo?.clientId ??
      threadState?.readyInfo?.client_id ??
      threadState?.readyInfo?.identityClientId ??
      threadState?.readyInfo?.identity_client_id ??
      null,
  );
  const activeThreadClientId = isActiveConversation
    ? toStringOrNull(
        stateSnapshot.activeThread?.manager?.identityClientId ??
          stateSnapshot.activeThread?.manager?.identity_client_id ??
          stateSnapshot.activeThread?.readyInfo?.clientId ??
          stateSnapshot.activeThread?.readyInfo?.client_id ??
          stateSnapshot.activeThread?.readyInfo?.identityClientId ??
          stateSnapshot.activeThread?.readyInfo?.identity_client_id ??
          null,
      )
    : null;

  return toStringOrNull(
    threadClientId ??
      activeThreadClientId ??
      stateSnapshot.bootstrap?.clientId ??
      stateSnapshot.bootstrap?.identityClientId ??
      stateSnapshot.bootstrap?.identity_client_id ??
      stateSnapshot.bootstrap?.identityId ??
      null,
  );
};

const resolveVoiceCallPayloadTargetClientId = (payload = {}) =>
  toStringOrNull(
    payload?.targetClientId ??
      payload?.target_client_id ??
      payload?.target?.clientId ??
      payload?.target?.client_id ??
      null,
  );

const resolveVoiceCallPayloadTargetUserId = (payload = {}) =>
  toStringOrNull(
    payload?.targetUserId ??
      payload?.target_user_id ??
      payload?.target?.userId ??
      payload?.target?.user_id ??
      null,
  );

const resolveVoiceCallPayloadSourceClientId = (payload = {}) =>
  toStringOrNull(
    payload?.sourceClientId ??
      payload?.source_client_id ??
      payload?.source?.clientId ??
      payload?.source?.client_id ??
      null,
  );

const isMessengerV2HiddenRecord = (record = null) => {
  if (!record || typeof record !== 'object') return false;
  const value = record.hidden ?? record.is_hidden ?? record.isHidden ?? null;
  return value === true || value === 'true' || value === 1 || value === '1';
};

const pickBase64 = (...candidates) => {
  for (const candidate of candidates) {
    const normalized = toStringOrNull(candidate);
    if (normalized) return normalized;
  }
  return null;
};

const traceMessengerV2Call = (event, details = {}) => {
  if (typeof window === 'undefined') return;
  try {
    const entry = {
      at: new Date().toISOString(),
      event,
      ...details,
    };
    const bucket = Array.isArray(window.__MESSENGER_V2_CALL_TRACE__)
      ? window.__MESSENGER_V2_CALL_TRACE__
      : [];
    bucket.push(entry);
    window.__MESSENGER_V2_CALL_TRACE__ = bucket.slice(-200);
  } catch {}
};

const withMessengerV2Timeout = (promise, timeoutMs, message) =>
  new Promise((resolve, reject) => {
    const normalizedTimeoutMs = Number(timeoutMs);
    if (!Number.isFinite(normalizedTimeoutMs) || normalizedTimeoutMs <= 0) {
      Promise.resolve(promise).then(resolve, reject);
      return;
    }

    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(message || 'Operation timed out'));
    }, normalizedTimeoutMs);

    Promise.resolve(promise).then(
      (value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });

const toServerSeqOrNull = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const resolveMlsRefreshDisposition = ({ payload = null, threadRecord = null } = {}) => {
  const readyInfo = threadRecord?.readyInfo ?? null;
  const hasValidatedRuntime = Boolean(
    threadRecord?.phase === 'ready' &&
      threadRecord?.stale !== true &&
      readyInfo?.ready === true &&
      readyInfo?.sendCapable === true,
  );
  if (!hasValidatedRuntime) {
    return { requiresThreadSync: true, invalidate: true };
  }

  const eventGeneration = toServerSeqOrNull(
    payload?.sessionGeneration ?? payload?.session_generation ?? null,
  );
  const runtimeGeneration = toServerSeqOrNull(
    readyInfo?.runtimeSessionGeneration ??
      readyInfo?.runtime_session_generation ??
      readyInfo?.sessionGeneration ??
      readyInfo?.session_generation ??
      null,
  );
  const eventEpoch = toServerSeqOrNull(payload?.epoch ?? payload?.server_epoch ?? null);
  const runtimeEpoch = toServerSeqOrNull(
    readyInfo?.runtimeEpoch ?? readyInfo?.runtime_epoch ?? readyInfo?.epoch ?? null,
  );

  if (eventGeneration != null || runtimeGeneration != null) {
    if (eventGeneration == null || runtimeGeneration == null) {
      return { requiresThreadSync: true, invalidate: true };
    }
    if (eventGeneration < runtimeGeneration) {
      return { requiresThreadSync: false, invalidate: false };
    }
    if (eventGeneration > runtimeGeneration) {
      return { requiresThreadSync: true, invalidate: true };
    }
  }

  if (eventEpoch == null || runtimeEpoch == null) {
    return { requiresThreadSync: true, invalidate: true };
  }

  return eventEpoch > runtimeEpoch
    ? { requiresThreadSync: true, invalidate: true }
    : { requiresThreadSync: false, invalidate: false };
};

const toTimestamp = (value) => {
  const normalized = toStringOrNull(value);
  if (!normalized) return 0;
  const parsed = new Date(normalized).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveMembershipUpdatedAt = (conversation = null) =>
  toStringOrNull(
    conversation?.membership_updated_at ??
      conversation?.membershipUpdatedAt ??
      conversation?.group?.membership_updated_at ??
      conversation?.group?.membershipUpdatedAt ??
      null,
  );

const resolveConversationRecordGroupId = (conversation = null) =>
  toStringOrNull(
    conversation?.group_id ??
      conversation?.groupId ??
      conversation?.group?.id ??
      conversation?.group?.group_id ??
      conversation?.group?.groupId ??
      null,
  );

const isConversationGroupRecord = (conversation = null) =>
  toStringOrNull(conversation?.kind ?? null) === 'group' ||
  Boolean(resolveConversationRecordGroupId(conversation));

const isAuthoritativeParticipantSnapshot = (conversation = null) =>
  Array.isArray(conversation?.participants) &&
  (conversation?.participants_authoritative === true ||
    conversation?.participantsAuthoritative === true);

const readMembershipField = (source = null, snakeKey, camelKey) =>
  source?.[snakeKey] ??
  source?.[camelKey] ??
  source?.group?.[snakeKey] ??
  source?.group?.[camelKey] ??
  null;

const shouldUseIncomingMembershipMetadata = (existing = null, incoming = null) => {
  if (!incoming || typeof incoming !== 'object') return false;
  if (!existing || typeof existing !== 'object') return true;

  const existingMembershipUpdatedAt = toTimestamp(resolveMembershipUpdatedAt(existing));
  const incomingMembershipUpdatedAt = toTimestamp(resolveMembershipUpdatedAt(incoming));
  const incomingAuthoritative = isAuthoritativeParticipantSnapshot(incoming);
  const existingAuthoritative = isAuthoritativeParticipantSnapshot(existing);

  if (incomingMembershipUpdatedAt > 0 && existingMembershipUpdatedAt === 0) {
    return true;
  }

  if (existingMembershipUpdatedAt > 0 && incomingMembershipUpdatedAt === 0) {
    return false;
  }

  if (existingMembershipUpdatedAt > 0 && incomingMembershipUpdatedAt > 0) {
    if (incomingMembershipUpdatedAt > existingMembershipUpdatedAt) return true;
    if (incomingMembershipUpdatedAt < existingMembershipUpdatedAt) return false;
    return incomingAuthoritative || !existingAuthoritative;
  }

  return incomingAuthoritative || !existingAuthoritative;
};

const resolveScopedGroupMembershipStatus = (conversation = null) =>
  toStringOrNull(
    conversation?.current_user_group_membership_status ??
      conversation?.currentUserGroupMembershipStatus ??
      conversation?.group?.current_user_group_membership_status ??
      conversation?.group?.currentUserGroupMembershipStatus ??
      null,
  );

const withMembershipTimestamp = (conversation = null, fallbackTimestamp = null) => {
  if (!conversation || typeof conversation !== 'object') {
    return conversation;
  }

  const membershipUpdatedAt =
    resolveMembershipUpdatedAt(conversation) ?? toStringOrNull(fallbackTimestamp);
  if (!membershipUpdatedAt) {
    return conversation;
  }

  return {
    ...conversation,
    membership_updated_at: membershipUpdatedAt,
  };
};

const resolveConversationParticipants = (conversation = null) => {
  if (!conversation || typeof conversation !== 'object') return [];
  if (Array.isArray(conversation.participants)) return conversation.participants;
  if (Array.isArray(conversation.users)) return conversation.users;
  return [];
};

const collectConversationParticipantIdentityValues = (participant = null) =>
  [
    participant?.id,
    participant?.user_id,
    participant?.userId,
    participant?.slug,
    participant?.user_slug,
    participant?.userSlug,
    participant?.username,
    participant?.handle,
    participant?.user?.id,
    participant?.user?.slug,
    participant?.user?.user_slug,
    participant?.user?.userSlug,
    participant?.user?.username,
    participant?.user?.handle,
  ]
    .map(toStringOrNull)
    .filter(Boolean);

const resolveConversationMembershipFingerprint = (conversation = null) => {
  if (!conversation || typeof conversation !== 'object') {
    return 'none';
  }

  const participants = resolveConversationParticipants(conversation)
    .map((participant) => {
      const identity = collectConversationParticipantIdentityValues(participant)[0] || 'unknown';
      const role =
        toStringOrNull(
          participant?.group_role ??
            participant?.groupRole ??
            participant?.role ??
            participant?.membership_role ??
            participant?.membershipRole ??
            null,
        ) || 'member';
      const status =
        toStringOrNull(
          participant?.membership_status ??
            participant?.membershipStatus ??
            participant?.group_membership_status ??
            participant?.groupMembershipStatus ??
            participant?.status ??
            null,
        ) || 'active';
      const groupMemberId =
        toStringOrNull(participant?.group_member_id ?? participant?.groupMemberId ?? null) ||
        'no-member-id';
      return [identity, role, status, groupMemberId].join('/');
    })
    .sort()
    .join(',');

  return [
    toStringOrNull(conversation?.id ?? null) || 'no-conversation',
    toStringOrNull(conversation?.group_id ?? conversation?.groupId ?? null) || 'no-group',
    toStringOrNull(conversation?.mls_group_id ?? conversation?.meta?.mls_group_id ?? null) ||
      'no-mls-group',
    resolveMembershipUpdatedAt(conversation) || 'no-membership-time',
    toStringOrNull(
      conversation?.current_user_group_membership_status ??
        conversation?.currentUserGroupMembershipStatus ??
        conversation?.group?.current_user_group_membership_status ??
        conversation?.group?.currentUserGroupMembershipStatus ??
        null,
    ) || 'unknown-status',
    toStringOrNull(
      conversation?.current_user_group_role ??
        conversation?.currentUserGroupRole ??
        conversation?.group?.current_user_group_role ??
        conversation?.group?.currentUserGroupRole ??
        null,
    ) || 'unknown-role',
    toStringOrNull(
      conversation?.current_user_group_member_id ??
        conversation?.currentUserGroupMemberId ??
        conversation?.group?.current_user_group_member_id ??
        conversation?.group?.currentUserGroupMemberId ??
        null,
    ) || 'no-current-member-id',
    participants || 'no-participants',
  ].join('|');
};

const conversationIncludesParticipant = (conversation = null, userId = null) => {
  const normalizedUserId = toStringOrNull(userId);
  if (!normalizedUserId) return false;
  return resolveConversationParticipants(conversation).some((participant) =>
    collectConversationParticipantIdentityValues(participant).includes(normalizedUserId),
  );
};

const resolveVoiceCallSignalRecordKey = ({
  payload = null,
  signal = null,
  groupId = null,
} = {}) => {
  const serverMsgId = toStringOrNull(payload?.server_msg_id ?? payload?.serverMsgId ?? null);
  if (serverMsgId) return `server-msg:${serverMsgId}`;

  const serverSeq = toServerSeqOrNull(payload?.server_seq ?? payload?.serverSeq ?? null);
  const resolvedGroupId = toStringOrNull(payload?.group_id ?? payload?.groupId ?? groupId ?? null);
  if (serverSeq != null) {
    return resolvedGroupId
      ? `group-seq:${resolvedGroupId}:${serverSeq}`
      : `server-seq:${serverSeq}`;
  }

  const clientMsgId = toStringOrNull(payload?.client_msg_id ?? payload?.clientMsgId ?? null);
  if (clientMsgId) return `client-msg:${clientMsgId}`;

  const signalParts = [
    'signal',
    resolvedGroupId,
    toStringOrNull(signal?.conversationId ?? signal?.conversation_id ?? null),
    toStringOrNull(signal?.callId ?? signal?.call_id ?? null),
    toStringOrNull(signal?.fromUserId ?? signal?.from_user_id ?? null),
    toStringOrNull(signal?.fromClientId ?? signal?.from_client_id ?? null),
    toStringOrNull(signal?.signalType ?? signal?.signal_type ?? null)?.toLowerCase(),
    toStringOrNull(signal?.at ?? null),
  ];

  return signalParts.some((part) => part) ? signalParts.map((part) => part || '').join(':') : null;
};

const resolveVoiceCallSignalType = (signal = null) =>
  toStringOrNull(
    signal?.signalType ?? signal?.signal_type ?? signal?.type ?? null,
  )?.toLowerCase() ?? null;

const shouldSuppressVoiceCallSignalNotification = (signalType = null) => {
  const normalizedSignalType = toStringOrNull(signalType)?.toLowerCase();
  return Boolean(
    normalizedSignalType &&
      normalizedSignalType !== 'offer' &&
      normalizedSignalType !== 'invite' &&
      normalizedSignalType !== 'sfu-invite',
  );
};

const normalizeSfuCallPayload = (payload = null) => {
  const call = payload?.conversation_call || payload?.conversationCall || payload?.call || null;
  const livekit = payload?.livekit || {};
  const normalizeCallUser = (user = null) => {
    if (!user || typeof user !== 'object') return null;
    const id = toStringOrNull(user.id ?? user.user_id ?? user.userId ?? null);
    const name = toStringOrNull(
      user.name ?? user.display_name ?? user.displayName ?? user.full_name ?? user.fullName ?? null,
    );
    const avatarUrl = toStringOrNull(
      user.avatar_url ??
        user.avatarUrl ??
        user.avatar ??
        user.display_picture ??
        user.displayPicture ??
        user.image_url ??
        user.imageUrl ??
        null,
    );
    const initials = toStringOrNull(
      user.initials ?? user.avatar_initials ?? user.avatarInitials ?? null,
    );
    const activeClientId = toStringOrNull(user.active_client_id ?? user.activeClientId ?? null);
    if (!id && !name && !avatarUrl && !initials) return null;
    return { id, userId: id, name, avatarUrl, initials, activeClientId, isLocal: false };
  };
  const activeParticipantCount = Number(
    call?.active_participant_count ??
      call?.activeParticipantCount ??
      payload?.active_participant_count ??
      payload?.activeParticipantCount ??
      null,
  );
  const joinedParticipantCount = Number(
    call?.joined_participant_count ??
      call?.joinedParticipantCount ??
      payload?.joined_participant_count ??
      payload?.joinedParticipantCount ??
      null,
  );
  const participants = Array.isArray(call?.participants)
    ? call.participants.map((participant) => normalizeCallUser(participant)).filter(Boolean)
    : [];
  const initiator = normalizeCallUser(
    call?.initiator ??
      call?.initiator_user ??
      call?.started_by ??
      (call?.initiator_user_id || call?.initiatorUserId
        ? {
            id: call.initiator_user_id ?? call.initiatorUserId,
            name: call.initiator_name ?? call.initiatorName,
            avatar_url: call.initiator_avatar_url ?? call.initiatorAvatarUrl,
            initials: call.initiator_initials ?? call.initiatorInitials,
          }
        : null),
  );
  return {
    call,
    action: toStringOrNull(payload?.action ?? null)?.toLowerCase() ?? null,
    callId: toStringOrNull(call?.id ?? payload?.call_id ?? payload?.callId ?? null),
    lifecycleVersion: Number.isFinite(
      Number(
        call?.lifecycle_version ??
          call?.lifecycleVersion ??
          payload?.lifecycle_version ??
          payload?.lifecycleVersion ??
          null,
      ),
    )
      ? Math.max(
          0,
          Math.trunc(
            Number(
              call?.lifecycle_version ??
                call?.lifecycleVersion ??
                payload?.lifecycle_version ??
                payload?.lifecycleVersion,
            ),
          ),
        )
      : null,
    livekitUrl: toStringOrNull(livekit?.url ?? payload?.livekit_url ?? payload?.livekitUrl ?? null),
    token: toStringOrNull(livekit?.token ?? payload?.token ?? null),
    mode: call?.mode === 'video' || payload?.mode === 'video' ? 'video' : 'voice',
    status: toStringOrNull(call?.status ?? payload?.status ?? null)?.toLowerCase() ?? null,
    endedForEveryone:
      payload?.ended_for_everyone === true ||
      payload?.endedForEveryone === true ||
      toStringOrNull(payload?.action ?? null)?.toLowerCase() === 'ended' ||
      toStringOrNull(call?.status ?? null)?.toLowerCase() === 'ended',
    writeHistory: payload?.write_history === true || payload?.writeHistory === true,
    activeParticipantCount: Number.isFinite(activeParticipantCount)
      ? Math.max(0, Math.trunc(activeParticipantCount))
      : null,
    joinedParticipantCount: Number.isFinite(joinedParticipantCount)
      ? Math.max(0, Math.trunc(joinedParticipantCount))
      : null,
    initiator,
    participants,
    conversationId: toStringOrNull(
      call?.conversation_id ?? call?.conversationId ?? payload?.conversation_id ?? null,
    ),
    startedAt: toStringOrNull(call?.started_at ?? call?.startedAt ?? null),
    ringingAt: toStringOrNull(call?.ringing_at ?? call?.ringingAt ?? null),
    endedAt: toStringOrNull(call?.ended_at ?? call?.endedAt ?? null),
    expiresAt: toStringOrNull(call?.expires_at ?? call?.expiresAt ?? null),
    inviteServerMsgId: toStringOrNull(
      call?.invite_server_msg_id ?? call?.inviteServerMsgId ?? null,
    ),
    startedByUserId: initiator?.id ?? null,
    startedByName: initiator?.name ?? null,
    startedByAvatarUrl: initiator?.avatarUrl ?? null,
    startedByInitials: initiator?.initials ?? null,
  };
};

const getMediaStreamTrackFromLiveKitTrack = (track = null) =>
  track?.mediaStreamTrack ?? track?.track?.mediaStreamTrack ?? track?.track ?? null;

const addTrackToMediaStream = (stream = null, mediaTrack = null) => {
  if (!mediaTrack || typeof MediaStream === 'undefined') return stream || null;
  const existingTracks = stream?.getTracks?.() || [];
  if (existingTracks.some((track) => track.id === mediaTrack.id))
    return stream || new MediaStream([mediaTrack]);
  return new MediaStream([...existingTracks, mediaTrack]);
};

const removeTrackFromMediaStream = (stream = null, mediaTrack = null) => {
  if (!stream || !mediaTrack || typeof MediaStream === 'undefined') return stream || null;
  const tracks = stream.getTracks?.().filter((track) => track.id !== mediaTrack.id) || [];
  return tracks.length ? new MediaStream(tracks) : null;
};

const createVoiceCallSignalFrontierKey = ({
  conversationId = null,
  serverMsgId = null,
  serverSeq = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedServerMsgId = toStringOrNull(serverMsgId);
  const normalizedServerSeq = toServerSeqOrNull(serverSeq);
  if (!normalizedConversationId || (!normalizedServerMsgId && normalizedServerSeq == null)) {
    return null;
  }
  return [
    normalizedConversationId,
    normalizedServerMsgId || 'no-msg',
    normalizedServerSeq != null ? String(normalizedServerSeq) : 'no-seq',
  ].join(':');
};

const createMessengerV2WindowActivityWindowId = () => {
  const canUseSecureRandom =
    typeof window !== 'undefined' && typeof window.crypto?.getRandomValues === 'function';

  if (canUseSecureRandom) {
    return randomUuid();
  }

  return `messenger-v2-window-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const findConversationParticipant = (conversation = null, userId = null) => {
  const normalizedUserId = toStringOrNull(userId);
  if (!normalizedUserId || !conversation || !Array.isArray(conversation?.participants)) {
    return null;
  }

  return (
    conversation.participants.find(
      (participant) =>
        toStringOrNull(participant?.id ?? participant?.user_id ?? null) === normalizedUserId,
    ) ?? null
  );
};

const resolveRemoteConversationParticipant = (conversation = null, currentUserId = null) => {
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  const participants = Array.isArray(conversation?.participants) ? conversation.participants : [];
  return (
    participants.find(
      (participant) =>
        toStringOrNull(participant?.id ?? participant?.user_id ?? null) !== normalizedCurrentUserId,
    ) ?? null
  );
};

const resolveParticipantDisplayName = (participant = null, fallback = 'Contact') =>
  toStringOrNull(
    participant?.full_name ??
      participant?.name ??
      participant?.user_name ??
      participant?.username ??
      null,
  ) || fallback;

const resolveParticipantAvatarUrl = (participant = null) =>
  toStringOrNull(
    participant?.avatar_url ??
      participant?.avatarUrl ??
      participant?.avatar ??
      participant?.image_url ??
      participant?.imageUrl ??
      participant?.image ??
      participant?.user?.avatar_url ??
      participant?.user?.avatarUrl ??
      participant?.user?.avatar ??
      null,
  );

const resolveLiveKitParticipantUserId = (participant = null) => {
  const identity = toStringOrNull(participant?.identity ?? null);
  const match = identity?.match?.(/(?:^|:)user:(\d+)(?::|$)/);
  return toStringOrNull(
    match?.[1] ?? participant?.attributes?.user_id ?? participant?.metadata?.user_id ?? null,
  );
};

const buildSfuParticipantSnapshot = ({
  participant = null,
  conversation = null,
  currentUserId = null,
  currentUserName = null,
  isLocal = false,
} = {}) => {
  if (!participant) return null;
  const userId = resolveLiveKitParticipantUserId(participant);
  const conversationParticipant = findConversationParticipant(conversation, userId);
  const media = collectMessengerV2CallParticipantMedia(participant);
  const displayName =
    toStringOrNull(participant.name ?? null) ||
    resolveParticipantDisplayName(
      conversationParticipant,
      isLocal ? currentUserName || 'You' : 'Participant',
    );

  return {
    id: toStringOrNull(participant.sid ?? participant.identity ?? userId ?? null) || randomUuid(),
    identity: toStringOrNull(participant.identity ?? null),
    userId,
    name: displayName,
    avatarUrl: resolveParticipantAvatarUrl(conversationParticipant),
    initials: (displayName || 'U').slice(0, 2).toUpperCase(),
    isLocal,
    isMuted: isLocal
      ? media.audioTracks.every((track) => track.enabled === false)
      : participant.isMicrophoneEnabled === false,
    cameraEnabled: isLocal
      ? media.cameraEnabled
      : participant.isCameraEnabled !== false && media.cameraEnabled,
    screenShareEnabled: media.screenShareEnabled,
    soundMuted: media.soundMuted,
    stream: media.stream,
    audioStream: media.audioStream,
    videoStream: media.videoStream,
    screenShareStream: media.screenShareStream,
    screenShareAudioStream: media.screenShareAudioStream,
    cameraDimensions: media.cameraDimensions,
    screenShareDimensions: media.screenShareDimensions,
    isSpeaking: media.isSpeaking,
    audioLevel: media.audioLevel,
  };
};

const buildSfuParticipantSnapshots = ({
  room = null,
  conversation = null,
  currentUserId = null,
  currentUserName = null,
} = {}) => {
  if (!room) return [];
  const local = buildSfuParticipantSnapshot({
    participant: room.localParticipant,
    conversation,
    currentUserId,
    currentUserName,
    isLocal: true,
  });
  const remoteParticipants = Array.from(room.remoteParticipants?.values?.() ?? [])
    .map((participant) =>
      buildSfuParticipantSnapshot({
        participant,
        conversation,
        currentUserId,
        currentUserName,
        isLocal: false,
      }),
    )
    .filter(Boolean);
  return [local, ...remoteParticipants].filter(Boolean);
};

const normalizeVoiceCallHistoryParticipant = (participant = null) => {
  if (!participant || typeof participant !== 'object') return null;
  const id = toStringOrNull(
    participant.userId ??
      participant.user_id ??
      participant.identity ??
      participant.id ??
      participant.sid ??
      null,
  );
  const name = toStringOrNull(
    participant.name ??
      participant.displayName ??
      participant.display_name ??
      participant.fullName ??
      participant.full_name ??
      null,
  );
  const avatarUrl = toStringOrNull(
    participant.avatarUrl ?? participant.avatar_url ?? participant.avatar ?? null,
  );
  const initials = toStringOrNull(participant.initials ?? null);
  if (!id && !name && !avatarUrl && !initials) return null;
  return {
    id,
    userId: toStringOrNull(participant.userId ?? participant.user_id ?? null),
    identity: toStringOrNull(participant.identity ?? null),
    name,
    avatarUrl,
    initials,
    isLocal: participant.isLocal === true || participant.is_local === true,
  };
};

const mergeVoiceCallParticipantHistory = (previous = null, next = null) => {
  if (!next || typeof next !== 'object') return next;
  if (next.status === 'idle') {
    return { ...next, participantHistory: [] };
  }

  const merged = [];
  const seen = new Set();
  const append = (participant) => {
    const normalized = normalizeVoiceCallHistoryParticipant(participant);
    if (!normalized) return;
    const key = (
      normalized.userId ||
      normalized.identity ||
      normalized.id ||
      normalized.name ||
      normalized.initials ||
      ''
    ).toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(normalized);
  };

  (Array.isArray(previous?.participantHistory) ? previous.participantHistory : []).forEach(append);
  (Array.isArray(next.participantHistory) ? next.participantHistory : []).forEach(append);
  (Array.isArray(next.participants) ? next.participants : []).forEach(append);

  return {
    ...next,
    participantHistory: merged,
  };
};

const resolveConversationNotificationTitle = (conversation = null, currentUserId = null) => {
  const explicitTitle = toStringOrNull(conversation?.title ?? conversation?.name ?? null);
  if (explicitTitle) {
    return explicitTitle;
  }

  const participants = Array.isArray(conversation?.participants) ? conversation.participants : [];
  const remoteParticipant = participants.find(
    (participant) =>
      toStringOrNull(participant?.id ?? participant?.user_id ?? null) !==
      toStringOrNull(currentUserId),
  );

  return (
    toStringOrNull(
      remoteParticipant?.full_name ??
        remoteParticipant?.name ??
        remoteParticipant?.user_name ??
        null,
    ) || (conversation?.kind === 'group' ? 'Group conversation' : 'New message')
  );
};

const resolveConversationNotificationActor = (
  conversation = null,
  currentUserId = null,
  senderUserId = null,
) => {
  const participants = Array.isArray(conversation?.participants) ? conversation.participants : [];
  const normalizedSenderUserId = toStringOrNull(senderUserId);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  const participant =
    participants.find(
      (candidate) =>
        normalizedSenderUserId &&
        toStringOrNull(candidate?.id ?? candidate?.user_id ?? null) === normalizedSenderUserId,
    ) ||
    participants.find(
      (candidate) =>
        toStringOrNull(candidate?.id ?? candidate?.user_id ?? null) !== normalizedCurrentUserId,
    ) ||
    null;

  return {
    name: resolveParticipantDisplayName(
      participant,
      resolveConversationNotificationTitle(conversation, currentUserId),
    ),
    avatarUrl: resolveParticipantAvatarUrl(participant),
  };
};

const timelineCoversConversationFrontier = ({
  conversation = null,
  timelineRecord = null,
  groupId = null,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!conversation || !timelineRecord || timelineRecord.phase !== 'ready') {
    return false;
  }
  if (normalizedGroupId && timelineRecord.groupId !== normalizedGroupId) {
    return false;
  }

  const lastServerMsgId = toStringOrNull(conversation?.last_server_msg_id ?? null);
  const lastServerSeq = toServerSeqOrNull(conversation?.last_server_seq ?? null);
  if (!lastServerMsgId && lastServerSeq == null) {
    return true;
  }

  const latestTimelineFrontier = selectMessengerV2AdvancedFrontier(
    resolveMessengerV2TimelineFrontier(timelineRecord?.items ?? [], {
      renderableOnly: true,
    }),
    {
      serverMsgId: timelineRecord?.applicationFrontierServerMsgId ?? null,
      serverSeq: timelineRecord?.applicationFrontierServerSeq ?? null,
    },
  );

  return (
    (!!latestTimelineFrontier.serverMsgId || latestTimelineFrontier.serverSeq != null) &&
    messengerV2FrontierCovers(latestTimelineFrontier, {
      serverMsgId: lastServerMsgId,
      serverSeq: lastServerSeq,
    })
  );
};

const resolveTimelineOldestServerSeq = (items = []) => {
  const candidates = (Array.isArray(items) ? items : [])
    .map((item) => toServerSeqOrNull(item?.serverSeq ?? item?.server_seq ?? null))
    .filter((seq) => seq != null);
  return candidates.length > 0 ? Math.min(...candidates) : null;
};

export const useMessengerV2Controller = ({
  formatMessage = null,
  initialConversationId = null,
  onOpenConversation,
  readReceiptConversationId = initialConversationId,
} = {}) => {
  const currentUser = useSelector((state) => state.user?.user);
  const cable = useRealtimeConsumer();
  const isAuthorized = useSelector((state) => state.auth?.isAuthorized);
  const [state, dispatch] = useReducer(
    messengerV2Reducer,
    { initialConversationId },
    createInitialMessengerV2State,
  );
  const hydrationReady = state.lifecycle?.hydrationReady === true;
  const setHydrationReady = useCallback((value) => {
    dispatch({ type: 'lifecycle:hydration-ready-changed', value });
  }, []);
  const restoredUserIdRef = useRef(null);
  const runtimePersistenceClientIdRef = useRef(null);
  const stateRef = useRef(state);
  const bootstrapAutoRunKeyRef = useRef(null);
  const contactsAutoRunKeyRef = useRef(null);
  const conversationsAutoRunKeyRef = useRef(null);
  const threadAutoRunKeyRef = useRef(null);
  const timelineAutoRunKeyRef = useRef(null);
  const windowActivityWindowIdRef = useRef(createMessengerV2WindowActivityWindowId());
  const activeThreadSyncRef = useRef(createMessengerV2SyncCoordinator());
  const timelineSyncRef = useRef(createMessengerV2SyncCoordinator());
  const runtimeRecoveryCoordinatorRef = useRef(createMessengerV2RuntimeRecoveryCoordinator());
  const completedMlsRefreshesRef = useRef(new Map());
  const timelineHistorySyncRef = useRef({ key: null, promise: null });
  const runActiveThreadSyncRef = useRef(null);
  const runActiveThreadReadySyncRef = useRef(null);
  const runTimelineSyncRef = useRef(null);
  const maybeNotifyConversationUpdateRef = useRef(null);
  const runPassiveConversationSyncRef = useRef(null);
  const publishReceiptFrontierRef = useRef(null);
  const publishRoutedReadReceiptRef = useRef(null);
  const remoteComposerActivityTimersRef = useRef(new Map());
  const remotePresenceTimersRef = useRef(new Map());
  const localComposerActivityPublisherRef = useRef(null);
  const receiptCoordinatorRef = useRef(null);
  if (!receiptCoordinatorRef.current) {
    receiptCoordinatorRef.current = createMessengerV2ReadReceiptCoordinator({
      publishReceipt: publishMessengerV2ReadReceipt,
    });
  }
  const clearReceiptPublishes = useCallback(() => {
    receiptCoordinatorRef.current?.clear();
  }, []);
  const desktopNotificationFrontierRef = useRef(new Map());
  const conversationCatalogRefreshRef = useRef({ requestId: 0 });
  const passiveConversationSyncRef = useRef(createMessengerV2SyncCoordinator());
  const passiveConversationAutoRunKeyRef = useRef(null);
  const conversationCatalogRecoveryRef = useRef({
    generation: 0,
    attempted: new Set(),
    tail: Promise.resolve(),
  });
  const startConversationFlightRef = useRef({ participantId: null, promise: null });
  const openGroupConversationFlightRef = useRef({ groupId: null, promise: null });
  if (!localComposerActivityPublisherRef.current) {
    localComposerActivityPublisherRef.current = createMessengerV2ComposerActivityPublisher({
      sendActivity: sendConversationTyping,
    });
  }
  const voiceCallRuntimeRef = useRef({
    session: null,
    ringTimer: null,
    setupTimer: null,
    ringtone: null,
    progressTone: null,
    screenWakeLock: null,
    screenWakeLockRequest: null,
    remoteIceBuffer: new Map(),
    pendingMediaState: new Map(),
    state: { status: 'idle' },
    processedSignalKeys: new Set(),
    processedSignalOrder: [],
    silentSignalFrontiers: new Map(),
    pendingEncryptedSignals: new Map(),
    pendingEncryptedSignalTimers: new Map(),
    startPromise: null,
    cleanupPromise: null,
    joinPromise: null,
  });
  const callLifecycleCoordinatorRef = useRef(null);
  const recoverActiveCallsRef = useRef(null);
  const joinSfuCallRef = useRef(null);
  const sendThreadMessageRef = useRef(null);
  const sendReactionOutboxEntryRef = useRef(null);
  const retryOutboxEntryRef = useRef(null);
  const outboundRetryFlightsRef = useRef(new Map());
  const attachmentUploadRuntimeRef = useRef({
    activeJobs: new Map(),
    abortControllers: new Map(),
  });
  const runAttachmentUploadJobRef = useRef(null);
  const voiceCall = state.call ?? createInitialMessengerV2CallState();
  const setVoiceCallState = useCallback((value) => {
    dispatch({ type: 'call:state-updated', value });
  }, []);

  const updateViewField = useCallback((field, value) => {
    dispatch({
      type: 'view:field-updated',
      field,
      value,
    });
  }, []);

  const recordEmojiUse = useCallback((emoji, { closePicker = false, now = Date.now() } = {}) => {
    dispatch({
      type: 'view:emoji-used',
      emoji,
      closePicker,
      now,
    });
  }, []);

  const configureView = useCallback((configuration = {}) => {
    dispatch({
      type: 'view:configured',
      ...configuration,
    });
  }, []);

  const authSnapshot = resolveMessengerV2AuthSnapshot({ currentUser, isAuthorized });
  const currentUserId = authSnapshot.currentUserId;
  const currentUserName = authSnapshot.currentUserName;
  const currentUserSlug = authSnapshot.currentUserSlug;
  if (
    !callLifecycleCoordinatorRef.current ||
    callLifecycleCoordinatorRef.current.userId !== toStringOrNull(currentUserId)
  ) {
    callLifecycleCoordinatorRef.current = createMessengerV2CallLifecycleCoordinator({
      userId: currentUserId,
      loadActiveCalls: loadActiveMessengerV2SfuCalls,
      loadCall: loadMessengerV2SfuCall,
    });
  }
  const normalizedReadReceiptConversationId = toStringOrNull(readReceiptConversationId);
  const bootstrapIdentityKey = state.bootstrap.clientId || state.bootstrap.identityId || null;
  const activeControllerConversation = findCanonicalMessengerV2Conversation(
    state,
    state.activeConversationId,
  );
  const activeThreadConversationId =
    activeControllerConversation?.id != null ? String(activeControllerConversation.id) : null;
  const activeThreadGroupId =
    activeControllerConversation?.mls_group_id ||
    activeControllerConversation?.meta?.mls_group_id ||
    null;
  const activeControllerConversationFingerprint = resolveConversationMembershipFingerprint(
    activeControllerConversation,
  );
  const activeControllerConversationMembershipStatus = resolveScopedGroupMembershipStatus(
    activeControllerConversation,
  );
  const activeControllerConversationIsActiveMember =
    activeControllerConversationMembershipStatus === 'active' ||
    Boolean(
      activeControllerConversation &&
        conversationIncludesParticipant(activeControllerConversation, currentUserId),
    );
  const activeThreadReadyInfoIsReadOnly = isMessengerV2ReadOnlyGroup(
    state.activeThread.readyInfo ?? null,
  );
  const activeThreadIsReady =
    state.activeThread.readyInfo?.ready === true && state.activeThread.stale !== true;
  const activeConversationTimelineRefreshToken =
    toStringOrNull(activeControllerConversation?.last_server_msg_id ?? null) ??
    toStringOrNull(activeControllerConversation?.last_message_at ?? null) ??
    null;

  const setVoiceCall = useCallback(
    (updater) => {
      const snapshot = voiceCallRuntimeRef.current.state || {};
      const optimisticNext =
        typeof updater === 'function'
          ? updater(snapshot)
          : {
              ...snapshot,
              ...(updater || {}),
            };
      const optimisticNextWithHistory = mergeVoiceCallParticipantHistory(snapshot, optimisticNext);
      voiceCallRuntimeRef.current.state = optimisticNextWithHistory;
      setVoiceCallState((current) => {
        const rawNext =
          typeof updater === 'function'
            ? updater(current)
            : {
                ...current,
                ...(updater || {}),
              };
        const next = mergeVoiceCallParticipantHistory(current, rawNext);
        voiceCallRuntimeRef.current.state = next;
        return next;
      });
    },
    [setVoiceCallState],
  );

  const setVoiceCallPresentation = useCallback(
    ({ presentation = 'background', returnPath = undefined } = {}) => {
      const normalizedPresentation = ['foreground', 'background', 'incoming', 'hidden'].includes(
        presentation,
      )
        ? presentation
        : 'background';
      setVoiceCall((current) => ({
        ...current,
        presentation: normalizedPresentation,
        ...(returnPath === undefined ? {} : { returnPath: toStringOrNull(returnPath) }),
      }));
    },
    [setVoiceCall],
  );

  const applyVoiceCallMediaSnapshot = useCallback(
    (callId, snapshot = {}) => {
      const audioOutputDevices = Array.isArray(snapshot.audioOutputDevices)
        ? snapshot.audioOutputDevices
        : [];
      setVoiceCall((current) =>
        current.callId === callId
          ? {
              ...current,
              audioOutputDevices,
              audioOutputDeviceId:
                snapshot.audioOutputDeviceId ??
                current.audioOutputDeviceId ??
                audioOutputDevices[0]?.deviceId ??
                null,
              audioOutputSupported: audioOutputDevices.length > 1,
            }
          : current,
      );
    },
    [setVoiceCall],
  );

  const recoverActiveVoiceCallMedia = useCallback(async () => {
    const session = voiceCallRuntimeRef.current.session;
    const call = voiceCallRuntimeRef.current.state || {};
    if (!session?.recoverMedia || !call.callId || call.status === 'idle') return null;

    try {
      const snapshot = await session.recoverMedia({
        microphoneEnabled: call.muted !== true,
      });
      applyVoiceCallMediaSnapshot(call.callId, snapshot);
      traceMessengerV2Call('media_recovered', {
        callId: call.callId,
        microphoneRecovered: snapshot?.microphoneRecovered === true,
        audioOutputCount: snapshot?.audioOutputDevices?.length ?? 0,
      });
      return snapshot;
    } catch (error) {
      traceMessengerV2Call('media_recovery_failed', {
        callId: call.callId,
        error: String(error?.message || error),
      });
      return null;
    }
  }, [applyVoiceCallMediaSnapshot]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const recoverWhenActive = () => {
      if (document.visibilityState === 'visible') {
        void recoverActiveVoiceCallMedia();
      }
    };

    window.addEventListener('focus', recoverWhenActive);
    window.addEventListener('pageshow', recoverWhenActive);
    window.addEventListener('online', recoverWhenActive);
    document.addEventListener('visibilitychange', recoverWhenActive);
    document.addEventListener('resume', recoverWhenActive);
    navigator.mediaDevices?.addEventListener?.('devicechange', recoverWhenActive);

    return () => {
      window.removeEventListener('focus', recoverWhenActive);
      window.removeEventListener('pageshow', recoverWhenActive);
      window.removeEventListener('online', recoverWhenActive);
      document.removeEventListener('visibilitychange', recoverWhenActive);
      document.removeEventListener('resume', recoverWhenActive);
      navigator.mediaDevices?.removeEventListener?.('devicechange', recoverWhenActive);
    };
  }, [recoverActiveVoiceCallMedia]);

  const voiceCallMediaIsActive = VOICE_CALL_MEDIA_ACTIVE_STATUSES.has(
    toStringOrNull(voiceCall.status)?.toLowerCase(),
  );

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    let disposed = false;
    const releaseSentinel = (sentinel) => {
      try {
        void Promise.resolve(sentinel?.release?.()).catch(() => null);
      } catch {}
    };
    const releaseWakeLock = () => {
      const sentinel = voiceCallRuntimeRef.current.screenWakeLock;
      voiceCallRuntimeRef.current.screenWakeLock = null;
      releaseSentinel(sentinel);
    };
    const acquireWakeLock = async () => {
      const currentSentinel = voiceCallRuntimeRef.current.screenWakeLock;
      if (
        (currentSentinel && currentSentinel.released !== true) ||
        voiceCallRuntimeRef.current.screenWakeLockRequest
      ) {
        return;
      }

      const request = requestMessengerV2CallScreenWakeLock();
      voiceCallRuntimeRef.current.screenWakeLockRequest = request;
      const sentinel = await request;
      if (voiceCallRuntimeRef.current.screenWakeLockRequest === request) {
        voiceCallRuntimeRef.current.screenWakeLockRequest = null;
      }
      if (!sentinel) return;
      const callStatus = toStringOrNull(voiceCallRuntimeRef.current.state?.status)?.toLowerCase();
      if (
        disposed ||
        document.visibilityState === 'hidden' ||
        !VOICE_CALL_MEDIA_ACTIVE_STATUSES.has(callStatus)
      ) {
        releaseSentinel(sentinel);
        return;
      }
      voiceCallRuntimeRef.current.screenWakeLock = sentinel;
    };
    const syncWakeLock = () => {
      if (document.visibilityState === 'visible') {
        void acquireWakeLock();
      } else {
        releaseWakeLock();
      }
    };

    if (voiceCallMediaIsActive) {
      syncWakeLock();
      document.addEventListener('visibilitychange', syncWakeLock);
    } else {
      releaseWakeLock();
    }

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', syncWakeLock);
      releaseWakeLock();
    };
  }, [voiceCallMediaIsActive]);

  const activeTimelineItemCount = Array.isArray(state.timeline.items)
    ? state.timeline.items.length
    : 0;

  useEffect(() => {
    if (typeof window === 'undefined' || !isVoiceCallUnloadRisk(voiceCall)) {
      return undefined;
    }

    const warnBeforeUnload = (event) => {
      if (!isVoiceCallUnloadRisk(voiceCallRuntimeRef.current.state)) return undefined;
      event.preventDefault();
      event.returnValue = VOICE_CALL_UNLOAD_WARNING;
      return VOICE_CALL_UNLOAD_WARNING;
    };

    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }, [voiceCall]);

  useEffect(() => {
    if (typeof window === 'undefined' || !voiceCallMediaIsActive) return undefined;

    let teardownSent = false;
    const teardownActiveCall = () => {
      if (teardownSent) return;
      const activeCall = voiceCallRuntimeRef.current.state || {};
      if (
        activeCall.provider !== LIVEKIT_CALL_PROVIDER ||
        !activeCall.callId ||
        !activeCall.conversationId
      ) {
        return;
      }

      const wasConnected = Boolean(activeCall.connectedAt || activeCall.sfuJoined);
      if (!wasConnected && activeCall.direction !== 'outgoing') return;

      teardownSent = true;
      const clientId = resolveMessengerV2CurrentClientId(
        stateRef.current || {},
        activeCall.conversationId,
      );
      void transitionMessengerV2SfuCall({
        conversationId: activeCall.conversationId,
        callId: activeCall.callId,
        action: wasConnected || activeCall.rejoining
          ? (activeCall.isGroupCall ? 'disconnect' : 'leave') : 'cancel',
        clientId,
        payload: activeCall.connectionId ? { connection_id: activeCall.connectionId } : {},
        keepalive: true,
      }).catch(() => null);
    };

    window.addEventListener('pagehide', teardownActiveCall);
    return () => {
      window.removeEventListener('pagehide', teardownActiveCall);
    };
  }, [voiceCallMediaIsActive]);

  const hasVoiceCallSignalKey = useCallback((key) => {
    const normalizedKey = toStringOrNull(key);
    if (!normalizedKey) return false;
    return voiceCallRuntimeRef.current.processedSignalKeys.has(normalizedKey);
  }, []);

  const rememberVoiceCallSignalKey = useCallback((key) => {
    const normalizedKey = toStringOrNull(key);
    if (!normalizedKey) return true;
    const seen = voiceCallRuntimeRef.current.processedSignalKeys;
    if (seen.has(normalizedKey)) return false;

    seen.add(normalizedKey);
    const order = voiceCallRuntimeRef.current.processedSignalOrder;
    order.push(normalizedKey);
    while (order.length > VOICE_CALL_PROCESSED_SIGNAL_CACHE_LIMIT) {
      const expiredKey = order.shift();
      if (expiredKey) seen.delete(expiredKey);
    }
    return true;
  }, []);

  const pruneSilentVoiceCallSignalFrontiers = useCallback(() => {
    const now = Date.now();
    const cache = voiceCallRuntimeRef.current.silentSignalFrontiers;
    for (const [key, recordedAt] of cache.entries()) {
      if (now - Number(recordedAt || 0) > VOICE_CALL_SILENT_SIGNAL_FRONTIER_TTL_MS) {
        cache.delete(key);
      }
    }
    while (cache.size > VOICE_CALL_SILENT_SIGNAL_FRONTIER_CACHE_LIMIT) {
      const oldestKey = cache.keys().next().value;
      if (!oldestKey) break;
      cache.delete(oldestKey);
    }
  }, []);

  const rememberSilentVoiceCallSignalFrontier = useCallback(
    ({ conversationId = null, payload = null, signal = null } = {}) => {
      const signalType = resolveVoiceCallSignalType(signal);
      if (!shouldSuppressVoiceCallSignalNotification(signalType)) return;

      const key = createVoiceCallSignalFrontierKey({
        conversationId:
          conversationId ??
          signal?.conversationId ??
          signal?.conversation_id ??
          payload?.conversation_id ??
          payload?.conversationId ??
          null,
        serverMsgId: payload?.server_msg_id ?? payload?.serverMsgId ?? null,
        serverSeq: payload?.server_seq ?? payload?.serverSeq ?? null,
      });
      if (!key) return;

      voiceCallRuntimeRef.current.silentSignalFrontiers.set(key, Date.now());
      pruneSilentVoiceCallSignalFrontiers();
    },
    [pruneSilentVoiceCallSignalFrontiers],
  );

  const isSilentVoiceCallSignalFrontier = useCallback(
    ({ conversationId = null, serverMsgId = null, serverSeq = null } = {}) => {
      pruneSilentVoiceCallSignalFrontiers();
      const key = createVoiceCallSignalFrontierKey({ conversationId, serverMsgId, serverSeq });
      return Boolean(key && voiceCallRuntimeRef.current.silentSignalFrontiers.has(key));
    },
    [pruneSilentVoiceCallSignalFrontiers],
  );

  const clearVoiceCallRingTimer = useCallback(() => {
    if (voiceCallRuntimeRef.current.ringTimer) {
      window.clearTimeout(voiceCallRuntimeRef.current.ringTimer);
      voiceCallRuntimeRef.current.ringTimer = null;
    }
  }, []);

  const clearVoiceCallSetupTimer = useCallback(() => {
    if (voiceCallRuntimeRef.current.setupTimer) {
      window.clearTimeout(voiceCallRuntimeRef.current.setupTimer);
      voiceCallRuntimeRef.current.setupTimer = null;
    }
  }, []);

  const stopIncomingVoiceCallAlerts = useCallback(() => {
    try {
      voiceCallRuntimeRef.current.ringtone?.stop?.();
    } catch {}
    voiceCallRuntimeRef.current.ringtone = null;
  }, []);

  const stopVoiceCallProgressTone = useCallback(() => {
    try {
      voiceCallRuntimeRef.current.progressTone?.stop?.();
    } catch {}
    voiceCallRuntimeRef.current.progressTone = null;
  }, []);

  const startVoiceCallProgressTone = useCallback(
    (tone = 'calling') => {
      stopVoiceCallProgressTone();
      voiceCallRuntimeRef.current.progressTone =
        tone === 'ringing'
          ? startMessengerV2OutgoingRingingTone()
          : startMessengerV2OutgoingCallTone();
    },
    [stopVoiceCallProgressTone],
  );

  const bufferRemoteVoiceCallIceCandidate = useCallback((callId, candidate) => {
    const normalizedCallId = toStringOrNull(callId);
    if (!normalizedCallId || !candidate || typeof candidate !== 'object') return;

    const buffer = voiceCallRuntimeRef.current.remoteIceBuffer;
    const pending = buffer.get(normalizedCallId) || [];
    pending.push(candidate);
    if (pending.length > VOICE_CALL_MAX_BUFFERED_REMOTE_ICE) {
      pending.splice(0, pending.length - VOICE_CALL_MAX_BUFFERED_REMOTE_ICE);
    }
    buffer.set(normalizedCallId, pending);
  }, []);

  const flushRemoteVoiceCallIceCandidates = useCallback((callId, session = null) => {
    const normalizedCallId = toStringOrNull(callId);
    const targetSession = session || voiceCallRuntimeRef.current.session;
    if (!normalizedCallId || !targetSession?.addIceCandidate) return;

    const buffer = voiceCallRuntimeRef.current.remoteIceBuffer;
    const pending = buffer.get(normalizedCallId) || [];
    buffer.delete(normalizedCallId);
    pending.forEach((candidate) => {
      void targetSession.addIceCandidate(candidate);
    });
  }, []);

  const startIncomingVoiceCallAlerts = useCallback(
    ({ conversationId = null, callId = null } = {}) => {
      const normalizedConversationId = toStringOrNull(conversationId);
      const normalizedCallId = toStringOrNull(callId);
      if (!normalizedConversationId || !normalizedCallId) return;

      stopIncomingVoiceCallAlerts();
      voiceCallRuntimeRef.current.ringtone = startMessengerV2IncomingCallRingtone();
    },
    [stopIncomingVoiceCallAlerts],
  );

  const refreshMessengerV2CallClientIdentity = useCallback(
    async ({ force = false, conversationId = null } = {}) => {
      if (!currentUserId) return null;

      const currentClientId = resolveMessengerV2CurrentClientId(
        stateRef.current || {},
        conversationId ?? stateRef.current?.activeConversationId ?? null,
      );
      if (!force && currentClientId) {
        return currentClientId;
      }

      const identity = await getMlsDeviceIdentity({ force });
      const clientId = toStringOrNull(
        identity?.client_id ??
          identity?.clientId ??
          identity?.identity_client_id ??
          identity?.identityClientId ??
          null,
      );
      if (!clientId) return null;

      dispatch({
        type: 'bootstrap:canonicalize',
        identityId: clientId,
        clientId,
        ciphersuite: stateRef.current.bootstrap?.ciphersuite ?? null,
      });
      return clientId;
    },
    [currentUserId],
  );

  const sendVoiceCallSignal = useCallback(
    async (signalType, payload = {}, callOverride = null, signalingContext = null) => {
      const preparedContext =
        signalingContext && typeof signalingContext === 'object' ? signalingContext : {};
      if (preparedContext.authReady !== true) {
        await ensureFreshAuthToken({ minTtlSeconds: 90 }).catch(() => null);
      }
      const normalizedSignalType = normalizeVoiceCallSignalType(signalType);
      const signalContentType = resolveVoiceCallSignalTransportContentType(normalizedSignalType);
      const call = callOverride || voiceCallRuntimeRef.current.state || {};
      const conversationId = toStringOrNull(
        call.conversationId ?? stateRef.current.activeConversationId,
      );
      const callId = toStringOrNull(call.callId);
      let stateSnapshot = stateRef.current || {};
      let conversation =
        preparedContext.conversation ||
        (Array.isArray(stateSnapshot.conversations)
          ? stateSnapshot.conversations.find((item) => String(item?.id ?? '') === conversationId)
          : null) ||
        null;
      let groupId =
        toStringOrNull(preparedContext.groupId) ||
        toStringOrNull(conversation?.mls_group_id ?? conversation?.meta?.mls_group_id ?? null);
      let currentClientId =
        toStringOrNull(preparedContext.clientId) ||
        resolveMessengerV2CurrentClientId(stateSnapshot, conversationId);

      if (conversationId && (!groupId || !conversation)) {
        const refreshedThread = await runActiveThreadSyncRef
          .current?.({ conversationId })
          .catch(() => null);
        stateSnapshot = stateRef.current || {};
        conversation =
          (Array.isArray(stateSnapshot.conversations)
            ? stateSnapshot.conversations.find((item) => String(item?.id ?? '') === conversationId)
            : null) ??
          refreshedThread?.conversation ??
          conversation;
        groupId = toStringOrNull(
          refreshedThread?.conversation?.mls_group_id ??
            refreshedThread?.conversation?.meta?.mls_group_id ??
            refreshedThread?.groupId ??
            conversation?.mls_group_id ??
            conversation?.meta?.mls_group_id ??
            groupId,
        );
        currentClientId =
          toStringOrNull(
            refreshedThread?.manager?.identityClientId ??
              refreshedThread?.manager?.identity_client_id ??
              refreshedThread?.clientId ??
              refreshedThread?.identityId ??
              resolveMessengerV2CurrentClientId(stateSnapshot, conversationId) ??
              currentClientId,
          ) ?? currentClientId;
      }

      if (!currentClientId) {
        const refreshedCurrentClientId = await refreshMessengerV2CallClientIdentity({
          force: false,
          conversationId,
        }).catch((identityError) => {
          traceMessengerV2Call('signal_sender_identity_refresh_failed', {
            callId,
            conversationId,
            signalType: normalizedSignalType,
            error: String(identityError?.message || identityError),
          });
          return null;
        });
        if (refreshedCurrentClientId) {
          currentClientId = refreshedCurrentClientId;
          stateSnapshot = stateRef.current || stateSnapshot;
        }
      }

      const normalizedCurrentUserId = toStringOrNull(currentUserId);
      if (!conversationId || !callId || !groupId || !normalizedCurrentUserId) {
        traceMessengerV2Call('signal_send_not_ready', {
          callId,
          conversationId,
          hasCurrentUserId: Boolean(normalizedCurrentUserId),
          groupId,
          signalType,
          bootstrapPhase: stateSnapshot.bootstrap?.phase ?? null,
        });
        throw new Error('Voice call encrypted signaling is not ready');
      }
      traceMessengerV2Call('signal_send_start', {
        callId,
        conversationId,
        groupId,
        signalType,
        clientId: currentClientId,
        contentType: signalContentType,
      });

      try {
        const preparedSend =
          preparedContext.preparedSend ||
          (await ensureMessengerV2SendReady({
            conversationId,
            conversation,
            groupId,
            currentUserId: normalizedCurrentUserId,
            currentUserName,
            clientId: currentClientId,
          }));
        const senderClientId =
          toStringOrNull(preparedSend?.senderClientId ?? null) ??
          toStringOrNull(preparedSend?.identityId ?? null) ??
          currentClientId;
        if (!senderClientId) {
          throw new Error('Voice call encrypted signaling sender is not ready');
        }
        if (
          senderClientId !== toStringOrNull(stateRef.current.bootstrap?.clientId ?? null) ||
          preparedSend?.identityId
        ) {
          dispatch({
            type: 'bootstrap:canonicalize',
            identityId: toStringOrNull(preparedSend?.identityId ?? null) ?? senderClientId,
            clientId: senderClientId,
            ciphersuite:
              conversation?.mls_ciphersuite ??
              conversation?.meta?.mls_ciphersuite ??
              stateRef.current.bootstrap?.ciphersuite ??
              null,
          });
        }
        const isGroupConversation = isConversationGroupRecord(conversation);
        const shouldTargetSignal =
          !isGroupConversation || GROUP_CALL_TARGETED_SIGNAL_TYPES.has(normalizedSignalType);
        const explicitTargetUserId = toStringOrNull(
          shouldTargetSignal
            ? (call.peerUserId ?? payload?.targetUserId ?? payload?.target_user_id ?? null)
            : null,
        );
        const remoteParticipant = isGroupConversation
          ? null
          : resolveRemoteConversationParticipant(conversation, currentUserId);
        const peerUserId =
          explicitTargetUserId ??
          toStringOrNull(remoteParticipant?.id ?? remoteParticipant?.user_id ?? null);
        const rawSignalPayload = payload && typeof payload === 'object' ? { ...payload } : {};
        if (!shouldTargetSignal) {
          delete rawSignalPayload.targetUserId;
          delete rawSignalPayload.target_user_id;
          delete rawSignalPayload.targetClientId;
          delete rawSignalPayload.target_client_id;
        }
        let resolvedInviteTargetClientId = null;
        let resolvedTargetUserId = peerUserId;
        const shouldResolveInitialSignalTarget =
          normalizedSignalType === 'invite' ||
          normalizedSignalType === 'offer' ||
          (normalizedSignalType === 'sfu-invite' && !isGroupConversation);
        if (shouldResolveInitialSignalTarget) {
          const callTarget =
            preparedContext.callTarget ||
            (await fetchMessengerV2CallTargetClient({
              groupId,
              targetUserId: peerUserId,
              conversationId,
            }));
          resolvedTargetUserId =
            resolvedTargetUserId ??
            toStringOrNull(callTarget?.user_id ?? callTarget?.userId ?? null);
          if (callTarget?.available === false) {
            traceMessengerV2Call('signal_target_unavailable', {
              callId,
              conversationId,
              groupId,
              signalType: normalizedSignalType,
              peerUserId,
              source: callTarget?.source ?? null,
            });
            throw new Error('Recipient is not available for calls');
          }
        }
        const targetClientId = shouldTargetSignal
          ? (resolvedInviteTargetClientId ??
            resolveVoiceCallPayloadTargetClientId(rawSignalPayload) ??
            toStringOrNull(call.peerClientId ?? null))
          : null;
        if (targetClientId && voiceCallRuntimeRef.current.state?.callId === callId) {
          setVoiceCall((current) =>
            current?.callId === callId && current.peerClientId !== targetClientId
              ? { ...current, peerClientId: targetClientId }
              : current,
          );
        }
        const signalPayload = {
          ...rawSignalPayload,
          ...(resolvedTargetUserId ? { targetUserId: resolvedTargetUserId } : {}),
          ...(targetClientId ? { targetClientId } : {}),
          ...(senderClientId ? { sourceClientId: senderClientId } : {}),
        };
        const envelope = createMessengerV2CallSignalEnvelope({
          callSignal: {
            callId,
            conversationId,
            lifecycleVersion:
              Number.isFinite(Number(call.lifecycleVersion)) && Number(call.lifecycleVersion) >= 0
                ? Math.trunc(Number(call.lifecycleVersion))
                : null,
            signalType,
            fromUserId: normalizedCurrentUserId,
            fromClientId: senderClientId,
            payload: signalPayload,
            at: new Date().toISOString(),
          },
        });
        const result = await sendMessengerV2Message({
          conversationId,
          conversation,
          groupId,
          currentUserId: normalizedCurrentUserId,
          currentUserName,
          clientId: senderClientId,
          plaintext: '',
          clientMsgId: randomUuid(),
          preparedSend,
          envelope,
          contentType: signalContentType,
          persistHistory: shouldPersistVoiceCallSignalHistory(normalizedSignalType),
          stageTimeoutMs: VOICE_CALL_SIGNAL_SEND_TIMEOUT_MS,
          encryptTimeoutMs: VOICE_CALL_SIGNAL_SEND_TIMEOUT_MS,
          sendTimeoutMs: VOICE_CALL_SIGNAL_SEND_TIMEOUT_MS,
        });
        traceMessengerV2Call('signal_send_done', {
          callId,
          conversationId,
          signalType,
          contentType: signalContentType,
          targetClientId,
          serverMsgId: result?.serverMsgId ?? result?.id ?? null,
        });
        return result;
      } catch (error) {
        traceMessengerV2Call('signal_send_failed', {
          callId,
          conversationId,
          signalType,
          error: String(error?.message || error),
        });
        throw error;
      }
    },
    [currentUserId, currentUserName, refreshMessengerV2CallClientIdentity, setVoiceCall],
  );

  const sendVoiceCallSignalQuietly = useCallback(
    (signalType, payload = {}, callOverride = null) => {
      void sendVoiceCallSignal(signalType, payload, callOverride).catch(() => null);
    },
    [sendVoiceCallSignal],
  );

  const closeVoiceCallSession = useCallback(() => {
    if (voiceCallRuntimeRef.current.cleanupPromise) {
      return voiceCallRuntimeRef.current.cleanupPromise;
    }
    const session = voiceCallRuntimeRef.current.session;
    if (!session) return Promise.resolve();
    let closeResult;
    try {
      if (typeof session?.close === 'function') {
        closeResult = session.close();
      } else {
        closeResult = session?.disconnect?.();
      }
    } catch (error) {
      closeResult = Promise.reject(error);
    }
    const cleanupPromise = Promise.resolve(closeResult)
      .catch((error) => {
        traceMessengerV2Call('session_cleanup_failed', {
          callId: voiceCallRuntimeRef.current.state?.callId ?? null,
          error: String(error?.message || error),
        });
      })
      .finally(() => {
        if (voiceCallRuntimeRef.current.session === session) {
          voiceCallRuntimeRef.current.session = null;
        }
        if (voiceCallRuntimeRef.current.cleanupPromise === cleanupPromise) {
          voiceCallRuntimeRef.current.cleanupPromise = null;
        }
      });
    voiceCallRuntimeRef.current.cleanupPromise = cleanupPromise;
    return cleanupPromise;
  }, []);

  const writeVoiceCallHistory = useCallback(
    async ({ call = null, status = 'ended', durationMs = null } = {}) => {
      const source = call || voiceCallRuntimeRef.current.state || {};
      const conversationId = toStringOrNull(source.conversationId);
      if (!conversationId) return null;
      const startedAt = toStringOrNull(source.connectedAt ?? source.startedAt ?? null);
      const endedAt = toStringOrNull(source.endedAt ?? null) || new Date().toISOString();
      const mode = source.mode === 'video' ? 'video' : 'voice';
      const participantCount = Number(
        source.participantCount ??
          source.participant_count ??
          source.joinedParticipantCount ??
          source.joined_participant_count ??
          null,
      );
      const normalizedParticipantCount = Number.isFinite(participantCount)
        ? Math.max(0, Math.trunc(participantCount))
        : null;
      const normalizedDurationMs =
        durationMs != null
          ? Math.max(0, Math.trunc(Number(durationMs) || 0))
          : startedAt
            ? Math.max(0, new Date(endedAt).getTime() - new Date(startedAt).getTime())
            : null;
      const participants = Array.isArray(source.participantHistory)
        ? source.participantHistory
        : Array.isArray(source.participants)
          ? source.participants
          : [];
      const envelope = createMessengerV2CallEventEnvelope({
        callEvent: {
          callId: source.callId,
          status,
          mode,
          direction: source.direction,
          durationMs: normalizedDurationMs,
          startedAt: source.startedAt,
          endedAt,
          startedByUserId: source.startedByUserId,
          startedByName: source.startedByName,
          startedByAvatarUrl: source.startedByAvatarUrl,
          startedByInitials: source.startedByInitials,
          participantCount: normalizedParticipantCount,
          groupCall: source.isGroupCall === true || normalizedParticipantCount > 2,
          participants,
        },
      });
      return (
        sendThreadMessageRef.current?.('', {
          envelope,
          background: true,
          conversationId,
          clientMsgId: createVoiceCallHistoryClientMsgId(source.callId),
        }) ?? null
      );
    },
    [],
  );

  const finishVoiceCall = useCallback(
    ({
      status = 'ended',
      sendSignal = false,
      writeHistory = false,
      endForEveryone = null,
      connectionLost = false,
      notifySfuServer = true,
      playEndTone = true,
      error = null,
    } = {}) => {
      const currentCall = voiceCallRuntimeRef.current.state || {};
      clearVoiceCallRingTimer();
      clearVoiceCallSetupTimer();
      stopIncomingVoiceCallAlerts();
      stopVoiceCallProgressTone();
      if (playEndTone && currentCall.status && currentCall.status !== 'idle') {
        playMessengerV2CallEndedTone();
      }
      const connectedAt = toStringOrNull(currentCall.connectedAt ?? null);
      const durationMs = connectedAt
        ? Math.max(0, Date.now() - new Date(connectedAt).getTime())
        : 0;
      const isAuthoritativeTerminal = [
        'ended',
        'cancelled',
        'failed',
        'declined',
        'missed',
      ].includes(String(status || '').toLowerCase());
      const joinedSfuCall = Boolean(currentCall.connectedAt || currentCall.sfuJoined);
      const isLocalParticipantLeave =
        currentCall.provider === LIVEKIT_CALL_PROVIDER && endForEveryone === false;
      if (
        currentCall.callId &&
        currentCall.conversationId &&
        isAuthoritativeTerminal &&
        !isLocalParticipantLeave
      ) {
        callLifecycleCoordinatorRef.current?.rememberTerminal({
          callId: currentCall.callId,
          conversationId: currentCall.conversationId,
          lifecycleVersion: currentCall.lifecycleVersion ?? null,
        });
      }
      if (sendSignal && currentCall.callId) {
        const signalType =
          status === 'cancelled' ? 'cancel' : status === 'missed' ? 'timeout' : 'end';
        sendVoiceCallSignalQuietly(signalType, { reason: status }, currentCall);
      }
      if (
        currentCall.provider === LIVEKIT_CALL_PROVIDER &&
        currentCall.callId &&
        currentCall.conversationId &&
        notifySfuServer
      ) {
        const wasConnected = joinedSfuCall;
        const isIncoming = currentCall.direction === 'incoming';
        const shouldEndForEveryone =
          currentCall.direction === 'outgoing' &&
          (typeof endForEveryone === 'boolean'
            ? endForEveryone
            : sendSignal || ['cancelled', 'failed'].includes(status));
        const transitionAction =
          connectionLost && currentCall.isGroupCall
            ? 'disconnect'
            : isIncoming && !wasConnected && ['declined', 'missed', 'failed'].includes(status)
            ? 'decline'
            : !wasConnected && currentCall.direction === 'outgoing' && !currentCall.rejoining
              ? 'cancel'
              : shouldEndForEveryone
                ? 'end'
                : 'leave';
        void Promise.resolve(
          transitionMessengerV2SfuCall({
            conversationId: currentCall.conversationId,
            callId: currentCall.callId,
            action: transitionAction,
            ...(currentCall.connectionId ? {
              clientId: resolveMessengerV2CurrentClientId(stateRef.current || {}, currentCall.conversationId),
              payload: { connection_id: currentCall.connectionId },
            } : {}),
          }),
        )
          .then((payload) => {
            void recoverActiveCallsRef.current?.({ force: true, queueIfRunning: true });
            const sfuResult = normalizeSfuCallPayload(payload);
            const serverEndedForEveryone = sfuResult.endedForEveryone;
            if (serverEndedForEveryone && !sendSignal && currentCall.callId) {
              sendVoiceCallSignalQuietly('end', { reason: status }, currentCall);
            }
            if (sfuResult.writeHistory && currentCall.status !== 'idle') {
              const historyParticipantCount =
                sfuResult.joinedParticipantCount ??
                (sfuResult.participants.length
                  ? sfuResult.participants.length
                  : Array.isArray(currentCall.participants)
                    ? currentCall.participants.length
                    : null);
              const historyStatus = ['cancelled', 'declined', 'failed', 'missed'].includes(status)
                ? status
                : serverEndedForEveryone
                  ? 'ended'
                  : status;
              const historyParticipants = sfuResult.participants.length
                ? sfuResult.participants
                : currentCall.participantHistory || currentCall.participants;
              const historyInitiator = sfuResult.initiator || null;
              void writeVoiceCallHistory({
                call: {
                  ...currentCall,
                  participantHistory: historyParticipants,
                  participantCount: historyParticipantCount,
                  isGroupCall: historyParticipantCount > 2 || currentCall.isGroupCall === true,
                  startedByUserId:
                    historyInitiator?.id ||
                    historyInitiator?.userId ||
                    currentCall.startedByUserId ||
                    currentCall.peerUserId,
                  startedByName:
                    historyInitiator?.name || currentCall.startedByName || currentCall.peerName,
                  startedByAvatarUrl:
                    historyInitiator?.avatarUrl || currentCall.startedByAvatarUrl || null,
                  startedByInitials:
                    historyInitiator?.initials || currentCall.startedByInitials || null,
                },
                status: historyStatus,
                durationMs,
              }).catch(() => null);
            }
          })
          .catch(() => null);
      }
      closeVoiceCallSession();
      if (currentCall.callId) {
        voiceCallRuntimeRef.current.remoteIceBuffer.delete(currentCall.callId);
      }
      if (
        writeHistory &&
        currentCall.status !== 'idle' &&
        currentCall.provider !== LIVEKIT_CALL_PROVIDER
      ) {
        void writeVoiceCallHistory({ call: currentCall, status, durationMs }).catch(() => null);
      }
      setVoiceCall((current) => ({
        ...current,
        status: 'idle',
        presentation: 'hidden',
        returnPath: null,
        provider: null,
        livekitUrl: null,
        isGroupCall: false,
        startedByUserId: null,
        startedByName: null,
        startedByAvatarUrl: null,
        startedByInitials: null,
        muted: false,
        soundMuted: false,
        soundMutePending: false,
        cameraEnabled: true,
        videoBackgroundEffect: 'none',
        videoBackgroundEffectPending: false,
        videoBackgroundEffectError: null,
        remoteMuted: false,
        remoteCameraEnabled: true,
        localStream: null,
        remoteStream: null,
        participants: [],
        sfuJoined: false,
        pendingOffer: null,
        mediaE2ee: null,
        secureSignalReady: false,
        answerRequested: false,
        expiresAt: null,
        startupAttemptId: null,
        connectionId: null,
        rejoining: false,
        endedAt: new Date().toISOString(),
        error: error ? String(error?.message || error) : null,
      }));
    },
    [
      clearVoiceCallSetupTimer,
      clearVoiceCallRingTimer,
      closeVoiceCallSession,
      sendVoiceCallSignalQuietly,
      setVoiceCall,
      stopIncomingVoiceCallAlerts,
      stopVoiceCallProgressTone,
      writeVoiceCallHistory,
    ],
  );

  const handleAuthoritativeVoiceCallState = useCallback(
    (payload = {}) => {
      callLifecycleCoordinatorRef.current?.observeAuthoritativeEvent(payload);
      const action = toStringOrNull(payload?.action ?? payload?.event ?? null)?.toLowerCase();
      const callId = toStringOrNull(payload?.call_id ?? payload?.callId ?? null);
      const activeCall = voiceCallRuntimeRef.current.state || {};
      if (
        !action ||
        !callId ||
        callId !== toStringOrNull(activeCall.callId) ||
        activeCall.status === 'idle'
      ) {
        return false;
      }

      if (['ended', 'cancelled', 'failed'].includes(action)) {
        const callStatus = toStringOrNull(
          payload?.call_status ?? payload?.callStatus ?? action,
        )?.toLowerCase();
        finishVoiceCall({
          status:
            callStatus === 'cancelled' ? 'missed' : callStatus === 'failed' ? 'failed' : 'ended',
          notifySfuServer: false,
          sendSignal: false,
          writeHistory: false,
        });
        return true;
      }

      const userId = toStringOrNull(payload?.user_id ?? payload?.userId ?? null);
      const winnerClientId = toStringOrNull(
        payload?.winner_client_id ?? payload?.winnerClientId ?? null,
      );
      if (action !== 'answered' || userId !== toStringOrNull(currentUserId) || !winnerClientId) {
        return false;
      }

      const dismissAnsweredElsewhere = (currentClientId) => {
        const normalizedClientId = toStringOrNull(currentClientId);
        if (!normalizedClientId || normalizedClientId === winnerClientId) return;
        const latestCall = voiceCallRuntimeRef.current.state || {};
        if (toStringOrNull(latestCall.callId) !== callId || latestCall.status === 'idle') {
          return;
        }
        traceMessengerV2Call('answered_elsewhere', {
          callId,
          conversationId:
            toStringOrNull(payload?.conversation_id ?? payload?.conversationId ?? null) ||
            latestCall.conversationId ||
            null,
        });
        finishVoiceCall({
          status: 'answered_elsewhere',
          notifySfuServer: false,
          sendSignal: false,
          writeHistory: false,
          playEndTone: false,
        });
      };

      const currentClientId = resolveMessengerV2CurrentClientId(
        stateRef.current || {},
        activeCall.conversationId,
      );
      if (currentClientId) {
        dismissAnsweredElsewhere(currentClientId);
      } else {
        void refreshMessengerV2CallClientIdentity({
          force: false,
          conversationId: activeCall.conversationId,
        })
          .then(dismissAnsweredElsewhere)
          .catch(() => null);
      }
      return true;
    },
    [currentUserId, finishVoiceCall, refreshMessengerV2CallClientIdentity],
  );

  const reconcileActiveVoiceCall = useCallback(async () => {
    const activeCall = voiceCallRuntimeRef.current.state || {};
    if (
      activeCall.provider !== LIVEKIT_CALL_PROVIDER ||
      !activeCall.callId ||
      !activeCall.conversationId ||
      activeCall.status === 'idle'
    ) {
      return null;
    }

    const payload = await loadMessengerV2SfuCall({
      conversationId: activeCall.conversationId,
      callId: activeCall.callId,
    }).catch(() => null);
    const serverCall = normalizeSfuCallPayload(payload);
    if (!serverCall.callId || serverCall.callId !== toStringOrNull(activeCall.callId)) {
      return null;
    }

    if (!['ringing', 'active'].includes(serverCall.status)) {
      handleAuthoritativeVoiceCallState({
        action: serverCall.status || 'ended',
        callId: serverCall.callId,
        conversationId: activeCall.conversationId,
        callStatus: serverCall.status || 'ended',
      });
      return serverCall;
    }

    if (serverCall.status === 'active' && ['incoming', 'securing'].includes(activeCall.status)) {
      const localParticipant = serverCall.participants.find(
        (participant) => toStringOrNull(participant.userId) === toStringOrNull(currentUserId),
      );
      if (localParticipant?.activeClientId) {
        handleAuthoritativeVoiceCallState({
          action: 'answered',
          callId: serverCall.callId,
          conversationId: activeCall.conversationId,
          userId: currentUserId,
          winnerClientId: localParticipant.activeClientId,
        });
      }
    }
    return serverCall;
  }, [currentUserId, handleAuthoritativeVoiceCallState]);

  const presentAuthoritativeIncomingVoiceCall = useCallback(
    (payload = null) => {
      const incomingCall = normalizeSfuCallPayload(
        payload?.conversation_call || payload?.conversationCall || payload?.call
          ? payload
          : { conversation_call: payload },
      );
      if (
        !incomingCall.callId ||
        !incomingCall.conversationId ||
        incomingCall.status !== 'ringing' ||
        toStringOrNull(incomingCall.startedByUserId) === toStringOrNull(currentUserId) ||
        callLifecycleCoordinatorRef.current?.isTerminal(incomingCall.callId)
      ) {
        return false;
      }

      const expiresAtMs = Date.parse(incomingCall.expiresAt || '');
      const remainingRingMs = Number.isFinite(expiresAtMs)
        ? expiresAtMs - Date.now()
        : VOICE_CALL_RING_TIMEOUT_MS;
      if (remainingRingMs <= 0) return false;

      const currentCall = voiceCallRuntimeRef.current.state || {};
      const isSameCall = toStringOrNull(currentCall.callId) === incomingCall.callId;
      if (currentCall.status && currentCall.status !== 'idle' && !isSameCall) return false;

      const snapshot = stateRef.current || {};
      const conversation = findCanonicalMessengerV2Conversation(
        snapshot,
        incomingCall.conversationId,
      );
      const isGroupCall = conversation?.kind === 'group';
      const peerName = isGroupCall
        ? toStringOrNull(conversation?.title ?? conversation?.name) ||
          incomingCall.startedByName ||
          'Group call'
        : incomingCall.startedByName || 'Contact';

      if (!isSameCall) {
        startIncomingVoiceCallAlerts({
          conversationId: incomingCall.conversationId,
          callId: incomingCall.callId,
        });
      }
      setVoiceCall((current) => {
        const currentIsSameCall = toStringOrNull(current?.callId) === incomingCall.callId;
        return {
          ...(currentIsSameCall ? current : createInitialMessengerV2CallState()),
          status: currentIsSameCall ? current.status : 'incoming',
          callId: incomingCall.callId,
          lifecycleVersion: incomingCall.lifecycleVersion,
          conversationId: incomingCall.conversationId,
          provider: LIVEKIT_CALL_PROVIDER,
          mode: incomingCall.mode,
          presentation: currentIsSameCall ? current.presentation : 'incoming',
          direction: 'incoming',
          isGroupCall,
          peerUserId: incomingCall.startedByUserId,
          peerName,
          startedByUserId: incomingCall.startedByUserId,
          startedByName: incomingCall.startedByName || peerName,
          startedByAvatarUrl: incomingCall.startedByAvatarUrl,
          startedByInitials: incomingCall.startedByInitials,
          participants: incomingCall.participants,
          participantHistory: incomingCall.participants,
          startedAt: incomingCall.ringingAt || incomingCall.startedAt || new Date().toISOString(),
          expiresAt: incomingCall.expiresAt,
          inviteServerMsgId: incomingCall.inviteServerMsgId,
          secureSignalReady: currentIsSameCall ? current.secureSignalReady === true : false,
          pendingOffer: currentIsSameCall ? current.pendingOffer || null : null,
          mediaE2ee: currentIsSameCall ? current.mediaE2ee || null : null,
          answerRequested: currentIsSameCall ? current.answerRequested === true : false,
          endedAt: null,
          error: null,
        };
      });

      clearVoiceCallRingTimer();
      voiceCallRuntimeRef.current.ringTimer = window.setTimeout(() => {
        const call = voiceCallRuntimeRef.current.state || {};
        if (
          toStringOrNull(call.callId) === incomingCall.callId &&
          ['incoming', 'securing'].includes(String(call.status || '').toLowerCase())
        ) {
          finishVoiceCall({
            status: 'missed',
            notifySfuServer: call.isGroupCall !== true,
          });
        }
      }, Math.min(remainingRingMs, VOICE_CALL_RING_TIMEOUT_MS));
      return true;
    },
    [
      clearVoiceCallRingTimer,
      currentUserId,
      finishVoiceCall,
      setVoiceCall,
      startIncomingVoiceCallAlerts,
    ],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const reconcileWhenActive = () => {
      if (document.visibilityState === 'visible') {
        void reconcileActiveVoiceCall();
      }
    };
    const handleServiceWorkerCallState = (event) => {
      handleAuthoritativeVoiceCallState(event?.detail || {});
    };

    window.addEventListener('focus', reconcileWhenActive);
    window.addEventListener('pageshow', reconcileWhenActive);
    window.addEventListener('online', reconcileWhenActive);
    window.addEventListener(MESSENGER_V2_CALL_STATE_EVENT, handleServiceWorkerCallState);
    document.addEventListener('visibilitychange', reconcileWhenActive);
    document.addEventListener('resume', reconcileWhenActive);

    return () => {
      window.removeEventListener('focus', reconcileWhenActive);
      window.removeEventListener('pageshow', reconcileWhenActive);
      window.removeEventListener('online', reconcileWhenActive);
      window.removeEventListener(MESSENGER_V2_CALL_STATE_EVENT, handleServiceWorkerCallState);
      document.removeEventListener('visibilitychange', reconcileWhenActive);
      document.removeEventListener('resume', reconcileWhenActive);
    };
  }, [handleAuthoritativeVoiceCallState, reconcileActiveVoiceCall]);

  const armVoiceCallSetupTimer = useCallback(
    ({ callId = null, reason = 'connection_timeout' } = {}) => {
      const normalizedCallId = toStringOrNull(callId);
      if (!normalizedCallId) return;
      clearVoiceCallSetupTimer();
      voiceCallRuntimeRef.current.setupTimer = window.setTimeout(() => {
        const call = voiceCallRuntimeRef.current.state || {};
        if (
          call.callId === normalizedCallId &&
          ['connecting', 'reconnecting'].includes(String(call.status || '').toLowerCase())
        ) {
          traceMessengerV2Call('setup_timeout', {
            callId: normalizedCallId,
            status: call.status,
            reason,
          });
          finishVoiceCall({
            status: 'failed',
            sendSignal: call.isGroupCall !== true,
            endForEveryone: call.isGroupCall ? false : null,
            writeHistory: true,
            error: new Error('Voice call connection timed out'),
          });
        }
      }, VOICE_CALL_SETUP_TIMEOUT_MS);
    },
    [clearVoiceCallSetupTimer, finishVoiceCall],
  );

  const handleIncomingVoiceCallSignal = useCallback(
    ({
      conversationId = null,
      callId = null,
      serverMsgId = null,
      fromUserId = null,
      fromClientId = null,
      signalType = null,
      signalPayload = {},
      lifecycleVersion = null,
      at = null,
    } = {}) => {
      const normalizedConversationId = toStringOrNull(conversationId);
      const normalizedCallId = toStringOrNull(callId);
      const normalizedServerMsgId = toStringOrNull(serverMsgId);
      const normalizedFromUserId = toStringOrNull(fromUserId);
      const normalizedFromClientId =
        toStringOrNull(fromClientId) ?? resolveVoiceCallPayloadSourceClientId(signalPayload);
      const normalizedSignalType = toStringOrNull(signalType)?.toLowerCase();
      const normalizedCurrentUserId = toStringOrNull(currentUserId);
      const signalAtMs = Date.parse(toStringOrNull(at) || '');
      if (
        !normalizedConversationId ||
        !normalizedCallId ||
        !normalizedFromUserId ||
        normalizedFromUserId === normalizedCurrentUserId ||
        !normalizedSignalType
      ) {
        return false;
      }
      const targetUserId = resolveVoiceCallPayloadTargetUserId(signalPayload);
      if (targetUserId && normalizedCurrentUserId && targetUserId !== normalizedCurrentUserId) {
        traceMessengerV2Call('signal_target_user_skipped', {
          callId: normalizedCallId,
          conversationId: normalizedConversationId,
          signalType: normalizedSignalType,
          fromUserId: normalizedFromUserId,
          targetUserId,
          currentUserId: normalizedCurrentUserId,
        });
        return true;
      }
      const targetClientId = resolveVoiceCallPayloadTargetClientId(signalPayload);
      const shouldEnforceTargetClient = Boolean(targetClientId);
      const currentClientId = resolveMessengerV2CurrentClientId(
        stateRef.current || {},
        normalizedConversationId,
      );
      if (shouldEnforceTargetClient && !currentClientId) {
        traceMessengerV2Call('signal_target_missing_local_client_allowed', {
          callId: normalizedCallId,
          conversationId: normalizedConversationId,
          signalType: normalizedSignalType,
          fromUserId: normalizedFromUserId,
          fromClientId: normalizedFromClientId,
          targetClientId,
        });
      }
      if (shouldEnforceTargetClient && currentClientId && targetClientId !== currentClientId) {
        traceMessengerV2Call('signal_target_skipped', {
          callId: normalizedCallId,
          conversationId: normalizedConversationId,
          signalType: normalizedSignalType,
          fromUserId: normalizedFromUserId,
          fromClientId: normalizedFromClientId,
          targetClientId,
          currentClientId,
        });
        return true;
      }

      const snapshot = stateRef.current || {};
      const conversations = Array.isArray(snapshot.conversations) ? snapshot.conversations : [];
      const conversation =
        conversations.find((item) => String(item?.id ?? '') === normalizedConversationId) || null;
      const signalConversationKind = toStringOrNull(
        signalPayload?.conversationKind ?? signalPayload?.conversation_kind ?? null,
      )?.toLowerCase();
      const signalIndicatesGroupCall =
        signalPayload?.groupCall === true ||
        signalPayload?.group_call === true ||
        signalPayload?.isGroupCall === true ||
        signalPayload?.is_group_call === true ||
        signalConversationKind === 'group' ||
        signalConversationKind === 'room';
      const isGroupCallConversation =
        isConversationGroupRecord(conversation) || signalIndicatesGroupCall;
      const remoteParticipant =
        findConversationParticipant(conversation, normalizedFromUserId) ||
        resolveRemoteConversationParticipant(conversation, normalizedCurrentUserId);
      const peerName = isGroupCallConversation
        ? toStringOrNull(
            conversation?.title ??
              conversation?.name ??
              signalPayload?.conversationTitle ??
              signalPayload?.conversation_title ??
              signalPayload?.groupName ??
              signalPayload?.group_name ??
              null,
          ) || 'Group call'
        : resolveParticipantDisplayName(remoteParticipant, 'Contact');
      const currentCall = voiceCallRuntimeRef.current.state || {};
      if (Number.isFinite(signalAtMs) && Date.now() - signalAtMs > VOICE_CALL_SIGNAL_STALE_TTL_MS) {
        traceMessengerV2Call('signal_stale_skipped', {
          callId: normalizedCallId,
          conversationId: normalizedConversationId,
          signalType: normalizedSignalType,
          fromUserId: normalizedFromUserId,
        });
        return true;
      }
      traceMessengerV2Call('signal_received', {
        callId: normalizedCallId,
        conversationId: normalizedConversationId,
        signalType: normalizedSignalType,
        fromUserId: normalizedFromUserId,
        fromClientId: normalizedFromClientId,
        targetClientId,
        targetUserId,
        currentStatus: currentCall.status || 'idle',
      });

      if (normalizedSignalType === 'sfu-invite') {
        if (Number.isFinite(signalAtMs) && Date.now() - signalAtMs > VOICE_CALL_RING_TIMEOUT_MS) {
          return true;
        }
        const callMode =
          toStringOrNull(
            signalPayload?.callMode ?? signalPayload?.call_mode ?? signalPayload?.mode ?? null,
          ) === 'video'
            ? 'video'
            : 'voice';
        const mediaE2ee = signalPayload?.mediaE2ee || signalPayload?.media_e2ee || null;
        if (!resolveLiveKitMediaSecretB64(mediaE2ee)) {
          sendVoiceCallSignalQuietly(
            'reject',
            { reason: 'missing_livekit_media_e2ee', provider: LIVEKIT_CALL_PROVIDER },
            {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              peerUserId: normalizedFromUserId,
              provider: LIVEKIT_CALL_PROVIDER,
            },
          );
          return true;
        }
        const isSameCall = currentCall.callId && currentCall.callId === normalizedCallId;
        const shouldJoinAfterSecureSignal =
          isSameCall &&
          (currentCall.answerRequested === true || currentCall.status === 'securing');
        if (currentCall.status && currentCall.status !== 'idle' && !isSameCall) {
          sendVoiceCallSignalQuietly(
            'busy',
            { reason: 'busy', provider: LIVEKIT_CALL_PROVIDER },
            {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              peerUserId: normalizedFromUserId,
              provider: LIVEKIT_CALL_PROVIDER,
            },
          );
          return true;
        }
        if (isSameCall) {
          setVoiceCall((current) =>
            current?.callId === normalizedCallId
              ? {
                  ...current,
                  mediaE2ee: mediaE2ee || current.mediaE2ee || null,
                  pendingOffer: { provider: LIVEKIT_CALL_PROVIDER },
                  secureSignalReady: true,
                  peerClientId: normalizedFromClientId || current.peerClientId || null,
                  error: null,
                }
              : current,
          );
        }
        if (!isSameCall) {
          startIncomingVoiceCallAlerts({
            conversationId: normalizedConversationId,
            callId: normalizedCallId,
            peerName,
            mode: callMode,
          });
          setVoiceCall({
            status: 'incoming',
            callId: normalizedCallId,
            lifecycleVersion,
            conversationId: normalizedConversationId,
            provider: LIVEKIT_CALL_PROVIDER,
            livekitUrl: null,
            mode: callMode,
            presentation: 'incoming',
            returnPath: null,
            isGroupCall: isGroupCallConversation,
            direction: 'incoming',
            peerUserId: normalizedFromUserId,
            peerClientId: normalizedFromClientId || null,
            peerName,
            startedByUserId:
              toStringOrNull(
                signalPayload?.startedByUserId ??
                  signalPayload?.started_by_user_id ??
                  signalPayload?.initiatorUserId ??
                  signalPayload?.initiator_user_id ??
                  null,
              ) || normalizedFromUserId,
            startedByName:
              toStringOrNull(
                signalPayload?.startedByName ??
                  signalPayload?.started_by_name ??
                  signalPayload?.initiatorName ??
                  signalPayload?.initiator_name ??
                  null,
              ) || peerName,
            startedByAvatarUrl:
              toStringOrNull(
                signalPayload?.startedByAvatarUrl ??
                  signalPayload?.started_by_avatar_url ??
                  signalPayload?.initiatorAvatarUrl ??
                  signalPayload?.initiator_avatar_url ??
                  null,
              ) || null,
            startedByInitials:
              toStringOrNull(
                signalPayload?.startedByInitials ??
                  signalPayload?.started_by_initials ??
                  signalPayload?.initiatorInitials ??
                  signalPayload?.initiator_initials ??
                  null,
              ) || null,
            muted: false,
            cameraEnabled: callMode === 'video',
            videoBackgroundEffect: 'none',
            videoBackgroundEffectPending: false,
            videoBackgroundEffectError: null,
            localStream: null,
            remoteStream: null,
            participants: [],
            startedAt: toStringOrNull(at) || new Date().toISOString(),
            connectedAt: null,
            endedAt: null,
            error: null,
            pendingOffer: { provider: LIVEKIT_CALL_PROVIDER },
            mediaE2ee,
            secureSignalReady: true,
            answerRequested: false,
            remoteMuted: false,
            remoteCameraEnabled: callMode === 'video',
          });
          clearVoiceCallRingTimer();
          voiceCallRuntimeRef.current.ringTimer = window.setTimeout(() => {
            const call = voiceCallRuntimeRef.current.state || {};
            if (call.callId === normalizedCallId && call.status === 'incoming') {
              finishVoiceCall({
                status: 'missed',
                notifySfuServer: call.isGroupCall !== true,
              });
            }
          }, VOICE_CALL_RING_TIMEOUT_MS);
        }
        sendVoiceCallSignalQuietly(
          'ringing',
          { receivedAt: new Date().toISOString(), provider: LIVEKIT_CALL_PROVIDER },
          {
            callId: normalizedCallId,
            conversationId: normalizedConversationId,
            peerUserId: normalizedFromUserId,
            provider: LIVEKIT_CALL_PROVIDER,
          },
        );
        const acknowledgementClientIdPromise = currentClientId
          ? Promise.resolve(currentClientId)
          : refreshMessengerV2CallClientIdentity({
              force: false,
              conversationId: normalizedConversationId,
            }).catch(() => null);
        void acknowledgementClientIdPromise.then((acknowledgementClientId) => {
          if (!acknowledgementClientId) return null;
          return Promise.resolve(
            transitionMessengerV2SfuCall({
              conversationId: normalizedConversationId,
              callId: normalizedCallId,
              action: 'acknowledge',
              clientId: acknowledgementClientId,
              payload: normalizedServerMsgId ? { invite_server_msg_id: normalizedServerMsgId } : {},
            }),
          ).catch((error) => {
            traceMessengerV2Call('invite_ack_failed', {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              error: String(error?.message || error),
            });
            return null;
          });
        });
        if (shouldJoinAfterSecureSignal) {
          const preparedCall = {
            ...currentCall,
            status: 'securing',
            mediaE2ee,
            pendingOffer: { provider: LIVEKIT_CALL_PROVIDER },
            secureSignalReady: true,
            peerClientId: normalizedFromClientId || currentCall.peerClientId || null,
          };
          void joinSfuCallRef.current?.(preparedCall).catch((error) => {
            traceMessengerV2Call('secure_answer_join_failed', {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              error: String(error?.message || error),
            });
          });
        }
        return true;
      }

      if (normalizedSignalType === 'invite' || normalizedSignalType === 'offer') {
        if (Number.isFinite(signalAtMs) && Date.now() - signalAtMs > VOICE_CALL_RING_TIMEOUT_MS) {
          return true;
        }
        const callMode =
          toStringOrNull(
            signalPayload?.callMode ??
              signalPayload?.call_mode ??
              signalPayload?.mode ??
              signalPayload?.mediaMode ??
              signalPayload?.media_mode ??
              null,
          ) === 'video'
            ? 'video'
            : 'voice';
        const mediaE2ee = signalPayload?.mediaE2ee || signalPayload?.media_e2ee || null;
        if (!mediaE2ee?.secretB64 && !mediaE2ee?.secret_b64) {
          sendVoiceCallSignalQuietly(
            'reject',
            { reason: 'missing_pq_media_e2ee' },
            {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              peerClientId: normalizedFromClientId,
            },
          );
          return true;
        }
        const isSameCall = currentCall.callId && currentCall.callId === normalizedCallId;
        if (currentCall.status && currentCall.status !== 'idle' && !isSameCall) {
          sendVoiceCallSignalQuietly(
            'busy',
            { reason: 'busy' },
            {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              peerClientId: normalizedFromClientId,
            },
          );
          return true;
        }
        if (isSameCall) {
          const pendingMediaState =
            voiceCallRuntimeRef.current.pendingMediaState.get(normalizedCallId) || null;
          if (pendingMediaState)
            voiceCallRuntimeRef.current.pendingMediaState.delete(normalizedCallId);
          setVoiceCall((current) => ({
            ...current,
            pendingOffer: signalPayload.offer || current.pendingOffer || null,
            peerClientId:
              normalizedFromClientId ||
              pendingMediaState?.peerClientId ||
              current.peerClientId ||
              null,
            mode: callMode,
            mediaE2ee: mediaE2ee || current.mediaE2ee || null,
            remoteMuted:
              pendingMediaState?.remoteMuted == null
                ? current.remoteMuted
                : pendingMediaState.remoteMuted,
            remoteCameraEnabled:
              pendingMediaState?.remoteCameraEnabled == null
                ? current.remoteCameraEnabled
                : pendingMediaState.remoteCameraEnabled,
            error: null,
          }));
          sendVoiceCallSignalQuietly(
            'ringing',
            { receivedAt: new Date().toISOString() },
            {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              peerUserId: normalizedFromUserId,
              peerClientId: normalizedFromClientId,
            },
          );
        } else {
          const pendingMediaState =
            voiceCallRuntimeRef.current.pendingMediaState.get(normalizedCallId) || null;
          if (pendingMediaState)
            voiceCallRuntimeRef.current.pendingMediaState.delete(normalizedCallId);
          startIncomingVoiceCallAlerts({
            conversationId: normalizedConversationId,
            callId: normalizedCallId,
            peerName,
            mode: callMode,
          });
          setVoiceCall({
            status: 'incoming',
            callId: normalizedCallId,
            lifecycleVersion,
            conversationId: normalizedConversationId,
            mode: callMode,
            presentation: 'incoming',
            returnPath: null,
            direction: 'incoming',
            peerUserId: normalizedFromUserId,
            peerClientId: normalizedFromClientId || pendingMediaState?.peerClientId || null,
            peerName,
            muted: false,
            cameraEnabled: callMode === 'video',
            videoBackgroundEffect: 'none',
            videoBackgroundEffectPending: false,
            videoBackgroundEffectError: null,
            localStream: null,
            remoteStream: null,
            startedAt: toStringOrNull(at) || new Date().toISOString(),
            connectedAt: null,
            endedAt: null,
            error: null,
            pendingOffer: signalPayload.offer || null,
            mediaE2ee,
            remoteMuted:
              pendingMediaState?.remoteMuted == null ? false : pendingMediaState.remoteMuted,
            remoteCameraEnabled:
              pendingMediaState?.remoteCameraEnabled == null
                ? callMode === 'video'
                : pendingMediaState.remoteCameraEnabled,
          });
          clearVoiceCallRingTimer();
          voiceCallRuntimeRef.current.ringTimer = window.setTimeout(() => {
            const call = voiceCallRuntimeRef.current.state || {};
            if (call.callId === normalizedCallId && call.status === 'incoming') {
              finishVoiceCall({ status: 'missed' });
            }
          }, VOICE_CALL_RING_TIMEOUT_MS);
          sendVoiceCallSignalQuietly(
            'ringing',
            { receivedAt: new Date().toISOString() },
            {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              peerUserId: normalizedFromUserId,
              peerClientId: normalizedFromClientId,
            },
          );
        }
        return true;
      }

      if (normalizedSignalType === 'ringing') {
        if (!currentCall.callId || currentCall.callId !== normalizedCallId) {
          return false;
        }
        if (currentCall.status === 'outgoing') {
          startVoiceCallProgressTone('ringing');
        }
        setVoiceCall((current) =>
          current.callId === normalizedCallId && current.status === 'outgoing'
            ? {
                ...current,
                status: 'ringing',
                peerClientId: normalizedFromClientId || current.peerClientId || null,
                error: null,
              }
            : current,
        );
        return true;
      }

      if (!currentCall.callId || currentCall.callId !== normalizedCallId) {
        if (normalizedSignalType === 'media-state') {
          const mediaState = resolveVoiceCallMediaStatePayload(signalPayload);
          if (mediaState.hasMediaState) {
            voiceCallRuntimeRef.current.pendingMediaState.set(normalizedCallId, {
              remoteMuted: mediaState.remoteMuted,
              remoteCameraEnabled: mediaState.remoteCameraEnabled,
              peerClientId: normalizedFromClientId || null,
              recordedAt: Date.now(),
            });
            traceMessengerV2Call('media_state_deferred', {
              callId: normalizedCallId,
              conversationId: normalizedConversationId,
              remoteMuted: mediaState.remoteMuted,
              remoteCameraEnabled: mediaState.remoteCameraEnabled,
              currentCallId: currentCall.callId || null,
            });
            return true;
          }
        }
        return false;
      }

      if (normalizedSignalType === 'media-state') {
        const { remoteMuted, remoteCameraEnabled } =
          resolveVoiceCallMediaStatePayload(signalPayload);
        setVoiceCall((current) =>
          current.callId === normalizedCallId
            ? {
                ...current,
                remoteMuted: remoteMuted == null ? current.remoteMuted : remoteMuted,
                remoteCameraEnabled:
                  remoteCameraEnabled == null ? current.remoteCameraEnabled : remoteCameraEnabled,
                peerClientId: current.peerClientId || normalizedFromClientId || null,
              }
            : current,
        );
        traceMessengerV2Call('media_state_applied', {
          callId: normalizedCallId,
          conversationId: normalizedConversationId,
          remoteMuted,
          remoteCameraEnabled,
        });
        return true;
      }

      if (normalizedSignalType === 'accept') {
        if (
          signalPayload?.mediaE2ee?.accepted !== true &&
          signalPayload?.media_e2ee?.accepted !== true
        ) {
          finishVoiceCall({
            status: 'failed',
            writeHistory: true,
            error: new Error('Voice call PQ media encryption was not accepted'),
          });
          return true;
        }
        clearVoiceCallRingTimer();
        stopVoiceCallProgressTone();
        setVoiceCall((current) => ({
          ...current,
          status: 'connecting',
          peerClientId: normalizedFromClientId || current.peerClientId || null,
          error: null,
        }));
        armVoiceCallSetupTimer({ callId: normalizedCallId, reason: 'answer_connect_timeout' });
        void Promise.resolve(
          voiceCallRuntimeRef.current.session?.applyAnswer?.(signalPayload.answer),
        )
          .then(() => {
            traceMessengerV2Call('answer_applied', { callId: normalizedCallId });
          })
          .catch((error) => {
            traceMessengerV2Call('answer_apply_failed', {
              callId: normalizedCallId,
              error: String(error?.message || error),
            });
            finishVoiceCall({ status: 'failed', sendSignal: true, writeHistory: true, error });
          });
        return true;
      }

      if (normalizedSignalType === 'ice') {
        if (normalizedFromClientId) {
          setVoiceCall((current) =>
            current.callId === normalizedCallId && !current.peerClientId
              ? { ...current, peerClientId: normalizedFromClientId }
              : current,
          );
        }
        const session = voiceCallRuntimeRef.current.session;
        if (session?.callId === normalizedCallId && session?.addIceCandidate) {
          void session.addIceCandidate(signalPayload.candidate);
        } else {
          bufferRemoteVoiceCallIceCandidate(normalizedCallId, signalPayload.candidate);
        }
        return true;
      }

      if (normalizedSignalType === 'reject') {
        if (
          currentCall.provider === LIVEKIT_CALL_PROVIDER &&
          (currentCall.isGroupCall === true || isConversationGroupRecord(conversation))
        ) {
          return true;
        }
        const declinedClientIds = Array.from(
          new Set(
            [
              ...(Array.isArray(currentCall.declinedClientIds)
                ? currentCall.declinedClientIds
                : []),
              normalizedFromClientId,
            ].filter(Boolean),
          ),
        );
        const targetDeviceCount = Math.max(1, Number(currentCall.targetDeviceCount) || 1);
        if (currentCall.direction === 'outgoing' && declinedClientIds.length < targetDeviceCount) {
          setVoiceCall((current) =>
            current.callId === normalizedCallId ? { ...current, declinedClientIds } : current,
          );
          return true;
        }
        const failed = toStringOrNull(signalPayload?.reason ?? null) === 'failed';
        finishVoiceCall({
          status: failed ? 'failed' : 'declined',
          writeHistory: currentCall.direction === 'outgoing',
        });
        return true;
      }

      if (normalizedSignalType === 'busy') {
        if (
          currentCall.provider === LIVEKIT_CALL_PROVIDER &&
          (currentCall.isGroupCall === true || isConversationGroupRecord(conversation))
        ) {
          return true;
        }
        finishVoiceCall({ status: 'declined', writeHistory: currentCall.direction === 'outgoing' });
        return true;
      }

      if (normalizedSignalType === 'cancel' || normalizedSignalType === 'timeout') {
        finishVoiceCall({ status: 'missed' });
        return true;
      }

      if (normalizedSignalType === 'end') {
        const failed = toStringOrNull(signalPayload?.reason ?? null) === 'failed';
        finishVoiceCall({ status: failed ? 'failed' : 'ended' });
        return true;
      }

      return false;
    },
    [
      bufferRemoteVoiceCallIceCandidate,
      armVoiceCallSetupTimer,
      clearVoiceCallRingTimer,
      currentUserId,
      finishVoiceCall,
      refreshMessengerV2CallClientIdentity,
      sendVoiceCallSignalQuietly,
      setVoiceCall,
      startIncomingVoiceCallAlerts,
      startVoiceCallProgressTone,
      stopVoiceCallProgressTone,
    ],
  );

  const resolveEncryptedVoiceCallSignalContext = useCallback(
    ({ payload = null, groupId = null, manager = null } = {}) => {
      const snapshot = stateRef.current || {};
      const activeConversationId = toStringOrNull(snapshot.activeConversationId ?? null);
      const payloadConversationId = toStringOrNull(
        payload?.conversation_id ?? payload?.conversationId ?? null,
      );
      const conversationId = payloadConversationId || activeConversationId;
      const conversation =
        findCanonicalMessengerV2Conversation(snapshot, conversationId) ||
        payload?.conversation ||
        null;
      const resolvedGroupId = toStringOrNull(
        groupId ??
          payload?.group_id ??
          payload?.groupId ??
          payload?.mls_group_id ??
          payload?.mlsGroupId ??
          conversation?.mls_group_id ??
          conversation?.meta?.mls_group_id ??
          null,
      );
      const resolvedConversation =
        conversation ||
        (conversationId && resolvedGroupId
          ? {
              id: conversationId,
              mls_group_id: resolvedGroupId,
              mls_ciphersuite: payload?.ciphersuite ?? payload?.mls_ciphersuite ?? null,
            }
          : null);
      let resolvedManager = manager || null;
      const normalizedCurrentUserId = toStringOrNull(currentUserId);
      if (!resolvedManager && resolvedConversation && normalizedCurrentUserId) {
        resolvedManager = resolveMessengerV2Manager({
          userId: normalizedCurrentUserId,
          identityName: currentUserName || `user-${normalizedCurrentUserId}`,
          preferredCiphersuite:
            resolvedConversation?.mls_ciphersuite ??
            resolvedConversation?.meta?.mls_ciphersuite ??
            resolvedConversation?.ciphersuite ??
            resolvedConversation?.meta?.ciphersuite ??
            DEFAULT_MLS_CIPHERSUITE,
          wantPQ: true,
          serverManaged: true,
        });
      }

      return {
        conversationId,
        conversation: resolvedConversation,
        groupId: resolvedGroupId,
        manager: resolvedManager,
      };
    },
    [currentUserId, currentUserName],
  );

  const handleEncryptedVoiceCallSignalRecord = useCallback(
    async ({ payload = null, groupId = null, manager = null, envelope = null, recoverCall = null } = {}) => {
      const senderUserId = toStringOrNull(payload?.sender_user_id ?? payload?.senderUserId ?? null);
      const normalizedCurrentUserId = toStringOrNull(currentUserId);
      const contentType = toStringOrNull(
        payload?.content_type ?? payload?.contentType ?? null,
      )?.toLowerCase();
      const isHiddenRecord = isMessengerV2HiddenRecord(payload);
      if (!payload || (!recoverCall && senderUserId === normalizedCurrentUserId)) {
        return false;
      }
      const providedEnvelope =
        envelope?.kind === 'call_signal' && envelope?.callSignal ? envelope : null;
      const canAttemptEncryptedCallSignal =
        providedEnvelope ||
        isHiddenRecord ||
        !contentType ||
        contentType === VOICE_CALL_SIGNAL_CONTENT_TYPE ||
        contentType === 'application/octet-stream';
      if (!canAttemptEncryptedCallSignal) {
        return false;
      }
      const ciphertext = providedEnvelope
        ? null
        : pickBase64(
            payload?.ciphertext_b64,
            payload?.ciphertextB64,
            payload?.envelope_b64,
            payload?.envelopeB64,
            null,
          );
      const historyCiphertext = pickBase64(
        payload?.history_ciphertext_b64,
        payload?.historyCiphertextB64,
        null,
      );
      const historyIv = pickBase64(payload?.history_iv_b64, payload?.historyIvB64, null);
      const canAttemptHistoryCallSignal = Boolean(historyCiphertext && historyIv);
      if (!providedEnvelope && !ciphertext && !canAttemptHistoryCallSignal) {
        return false;
      }

      const {
        conversationId,
        conversation,
        groupId: normalizedGroupId,
        manager: resolvedContextManager,
      } = resolveEncryptedVoiceCallSignalContext({ payload, groupId, manager });
      if (!conversationId || !conversation) {
        return false;
      }
      if (!providedEnvelope && !normalizedGroupId) {
        traceMessengerV2Call('encrypted_signal_missing_group_deferred', {
          conversationId,
          senderUserId,
          contentType,
        });
        return false;
      }

      try {
        const resolvedManager = resolvedContextManager;
        if (
          !providedEnvelope &&
          !canAttemptHistoryCallSignal &&
          (!resolvedManager || typeof resolvedManager.decrypt !== 'function')
        ) {
          traceMessengerV2Call('encrypted_signal_missing_manager_deferred', {
            conversationId,
            groupId: normalizedGroupId,
            senderUserId,
            contentType,
          });
          return false;
        }
        if (!recoverCall && !providedEnvelope && typeof resolvedManager?.ingestPendingEntries === 'function') {
          await resolvedManager
            .ingestPendingEntries({
              conversationId,
              groupId: normalizedGroupId,
              items: [payload],
            })
            .catch(() => null);
        }
        let signalEnvelope = providedEnvelope;
        if (!signalEnvelope) {
          let plaintext = null;
          if (!recoverCall && ciphertext && typeof resolvedManager?.decrypt === 'function') {
            const decrypted = await resolvedManager
              .decrypt({
                conversationId,
                groupId: normalizedGroupId,
                ciphertext,
                epochHint: payload?.epoch ?? null,
              })
              .catch(() => null);
            plaintext = toStringOrNull(decrypted?.text ?? null);
          }
          if (!plaintext && canAttemptHistoryCallSignal) {
            let historyKeyB64 = null;
            if (typeof resolvedManager?.exportHistoryKey === 'function') {
              historyKeyB64 = await resolvedManager
                .exportHistoryKey({
                  conversationId,
                  groupId: normalizedGroupId,
                  epoch: payload?.epoch ?? null,
                })
                .catch(() => null);
              if (historyKeyB64) {
                await Promise.resolve(
                  persistHistoryKey({
                    conversationId,
                    keyB64: historyKeyB64,
                  }),
                ).catch(() => null);
              }
            }
            plaintext = await decryptHistory({
              conversationId,
              ciphertextB64: historyCiphertext,
              ivB64: historyIv,
              keyB64: historyKeyB64 || null,
            }).catch(() => null);
            if (plaintext) {
              traceMessengerV2Call('encrypted_signal_history_decrypted', {
                conversationId,
                groupId: normalizedGroupId,
                senderUserId,
                contentType,
              });
            }
          }
          if (!plaintext) return false;
          signalEnvelope = parseMessengerV2Envelope(plaintext);
        }
        if (signalEnvelope.kind !== 'call_signal' || !signalEnvelope.callSignal) {
          return false;
        }
        const signal = signalEnvelope.callSignal;
        if (recoverCall) {
          // Recovery is bound to the authenticated server call and its exact stored MLS invite.
          // In particular, the initiator may decrypt their own historical invite without ringing themselves.
          if (
            String(signal.callId) !== String(recoverCall.id) ||
            String(signal.conversationId) !== String(recoverCall.conversation_id) ||
            String(senderUserId) !== String(recoverCall.initiator_user_id) ||
            String(signal.fromUserId) !== String(senderUserId) ||
            String(payload.server_msg_id) !== String(recoverCall.invite_server_msg_id) ||
            signal.signalType !== 'sfu-invite' ||
            !resolveLiveKitMediaSecretB64(signal.payload?.mediaE2ee ?? signal.payload?.media_e2ee)
          ) return null;
          return signal;
        }
        const signalSenderUserId = toStringOrNull(signal.fromUserId ?? null) || senderUserId;
        if (!signalSenderUserId || signalSenderUserId === normalizedCurrentUserId) {
          return false;
        }
        const signalKey = resolveVoiceCallSignalRecordKey({
          payload,
          signal,
          groupId: normalizedGroupId,
        });
        if (hasVoiceCallSignalKey(signalKey)) {
          traceMessengerV2Call('signal_duplicate_skipped', {
            key: signalKey,
            callId: signal.callId,
            conversationId: signal.conversationId,
            signalType: signal.signalType,
          });
          return true;
        }
        const signalDecision = await callLifecycleCoordinatorRef.current?.classifySignal({
          signal,
        });
        if (signalDecision?.decision === 'ignore') {
          traceMessengerV2Call('signal_authoritatively_skipped', {
            key: signalKey,
            callId: signal.callId,
            conversationId: signal.conversationId,
            signalType: signal.signalType,
          });
          rememberVoiceCallSignalKey(signalKey);
          rememberSilentVoiceCallSignalFrontier({
            conversationId,
            payload,
            signal,
          });
          return true;
        }
        if (signalDecision?.decision === 'defer') {
          traceMessengerV2Call('signal_authoritative_check_deferred', {
            key: signalKey,
            callId: signal.callId,
            conversationId: signal.conversationId,
            signalType: signal.signalType,
          });
          return false;
        }
        const handled = handleIncomingVoiceCallSignal({
          conversationId: signal.conversationId,
          callId: signal.callId,
          serverMsgId: payload?.server_msg_id ?? payload?.serverMsgId ?? null,
          fromUserId: signalSenderUserId,
          fromClientId: signal.fromClientId,
          signalType: signal.signalType,
          signalPayload: signal.payload || {},
          lifecycleVersion:
            signalDecision?.call?.lifecycleVersion ?? signal.lifecycleVersion ?? null,
          at: signal.at,
        });
        if (!handled) {
          if (callLifecycleCoordinatorRef.current?.isTerminal(signal.callId)) {
            rememberVoiceCallSignalKey(signalKey);
            rememberSilentVoiceCallSignalFrontier({
              conversationId,
              payload,
              signal,
            });
            return true;
          }
          traceMessengerV2Call('signal_unhandled', {
            key: signalKey,
            callId: signal.callId,
            conversationId: signal.conversationId,
            signalType: signal.signalType,
          });
          return false;
        }
        rememberVoiceCallSignalKey(signalKey);
        rememberSilentVoiceCallSignalFrontier({
          conversationId,
          payload,
          signal,
        });
        return true;
      } catch (error) {
        traceMessengerV2Call('encrypted_signal_record_failed', {
          conversationId,
          groupId: normalizedGroupId,
          senderUserId,
          contentType,
          error: String(error?.message || error),
        });
        return false;
      }
    },
    [
      currentUserId,
      hasVoiceCallSignalKey,
      handleIncomingVoiceCallSignal,
      rememberSilentVoiceCallSignalFrontier,
      rememberVoiceCallSignalKey,
      resolveEncryptedVoiceCallSignalContext,
    ],
  );

  const clearQueuedEncryptedVoiceCallSignal = useCallback((queueKey) => {
    const normalizedQueueKey = toStringOrNull(queueKey);
    if (!normalizedQueueKey) return;
    const timerId =
      voiceCallRuntimeRef.current.pendingEncryptedSignalTimers.get(normalizedQueueKey);
    if (timerId) {
      window.clearTimeout(timerId);
      voiceCallRuntimeRef.current.pendingEncryptedSignalTimers.delete(normalizedQueueKey);
    }
    voiceCallRuntimeRef.current.pendingEncryptedSignals.delete(normalizedQueueKey);
  }, []);

  const resolveEncryptedVoiceCallSignalQueueKey = useCallback(
    ({ payload = null, groupId = null } = {}) => {
      const conversationId = toStringOrNull(
        payload?.conversation_id ?? payload?.conversationId ?? null,
      );
      const normalizedGroupId = toStringOrNull(
        groupId ?? payload?.group_id ?? payload?.groupId ?? null,
      );
      const serverMsgId = toStringOrNull(payload?.server_msg_id ?? payload?.serverMsgId ?? null);
      const serverSeq = toStringOrNull(payload?.server_seq ?? payload?.serverSeq ?? null);
      const clientMsgId = toStringOrNull(payload?.client_msg_id ?? payload?.clientMsgId ?? null);
      const recordId = toStringOrNull(
        payload?.id ?? payload?.message_id ?? payload?.messageId ?? null,
      );
      const ciphertext = pickBase64(
        payload?.ciphertext_b64,
        payload?.ciphertextB64,
        payload?.envelope_b64,
        payload?.envelopeB64,
        null,
      );
      return [
        conversationId && `conversation:${conversationId}`,
        serverMsgId && `server:${serverMsgId}`,
        serverSeq && `seq:${serverSeq}`,
        clientMsgId && `client:${clientMsgId}`,
        recordId && `id:${recordId}`,
        !conversationId && normalizedGroupId ? `group:${normalizedGroupId}` : null,
        !serverMsgId && !serverSeq && !clientMsgId && !recordId && ciphertext
          ? `cipher:${String(ciphertext).slice(0, 80)}`
          : null,
      ]
        .filter(Boolean)
        .join(':');
    },
    [],
  );

  const queueEncryptedVoiceCallSignalRecord = useCallback(
    ({ payload = null, groupId = null, manager = null, envelope = null, attempt = 0 } = {}) => {
      const {
        conversationId: resolvedConversationId,
        groupId: resolvedGroupId,
        manager: resolvedManager,
      } = resolveEncryptedVoiceCallSignalContext({ payload, groupId, manager });
      const senderUserId = toStringOrNull(payload?.sender_user_id ?? payload?.senderUserId ?? null);
      const normalizedCurrentUserId = toStringOrNull(currentUserId);
      if (!payload || senderUserId === normalizedCurrentUserId) {
        return false;
      }
      if (!resolvedConversationId && !resolvedGroupId && !envelope) {
        return false;
      }

      const normalizedPayload = {
        ...payload,
        conversation_id: resolvedConversationId ?? payload?.conversation_id ?? null,
        group_id: resolvedGroupId ?? payload?.group_id ?? null,
      };
      const queueKey = resolveEncryptedVoiceCallSignalQueueKey({
        payload: normalizedPayload,
        groupId: resolvedGroupId,
      });
      if (!queueKey) return false;

      const currentEntry = voiceCallRuntimeRef.current.pendingEncryptedSignals.get(queueKey) || {};
      const requestedAttempt = Number.isFinite(Number(attempt)) ? Number(attempt) : 0;
      const currentAttempt = Number.isFinite(Number(currentEntry.attempt))
        ? Number(currentEntry.attempt)
        : requestedAttempt;
      if (currentAttempt >= ENCRYPTED_CALL_SIGNAL_RETRY_DELAYS_MS.length) {
        clearQueuedEncryptedVoiceCallSignal(queueKey);
        traceMessengerV2Call('encrypted_signal_retry_exhausted', {
          queueKey,
          conversationId: resolvedConversationId,
          groupId: resolvedGroupId,
          senderUserId,
        });
        return false;
      }

      const previousTimerId =
        voiceCallRuntimeRef.current.pendingEncryptedSignalTimers.get(queueKey);
      if (previousTimerId) {
        window.clearTimeout(previousTimerId);
        voiceCallRuntimeRef.current.pendingEncryptedSignalTimers.delete(queueKey);
      }

      voiceCallRuntimeRef.current.pendingEncryptedSignals.set(queueKey, {
        payload: normalizedPayload,
        groupId: resolvedGroupId,
        manager: resolvedManager,
        envelope,
        attempt: currentAttempt,
      });

      const delayMs =
        ENCRYPTED_CALL_SIGNAL_RETRY_DELAYS_MS[
          Math.min(currentAttempt, ENCRYPTED_CALL_SIGNAL_RETRY_DELAYS_MS.length - 1)
        ];
      const timerId = window.setTimeout(() => {
        voiceCallRuntimeRef.current.pendingEncryptedSignalTimers.delete(queueKey);
        void (async () => {
          const queued = voiceCallRuntimeRef.current.pendingEncryptedSignals.get(queueKey);
          if (!queued) return;

          const queuedContext = resolveEncryptedVoiceCallSignalContext(queued);
          const queuedConversationId = queuedContext.conversationId;
          let queuedForRetry = queued;
          if (queuedConversationId) {
            threadAutoRunKeyRef.current = null;
            traceMessengerV2Call('encrypted_signal_retry_sync_start', {
              queueKey,
              attempt: queued.attempt || 0,
              conversationId: queuedConversationId,
              groupId: queuedContext.groupId,
              senderUserId,
            });
            try {
              await Promise.resolve(
                runActiveThreadSyncRef.current?.({ conversationId: queuedConversationId }),
              );
              if (queuedContext.groupId) {
                traceMessengerV2Call('encrypted_signal_retry_timeline_sync_start', {
                  queueKey,
                  attempt: queued.attempt || 0,
                  conversationId: queuedConversationId,
                  groupId: queuedContext.groupId,
                  senderUserId,
                });
                await Promise.resolve(
                  runTimelineSyncRef.current?.({
                    conversationId: queuedConversationId,
                    groupId: queuedContext.groupId,
                    refreshToken: ['call-signal', queueKey, queued.attempt || 0].join(':'),
                  }),
                );
              }
            } catch (error) {
              traceMessengerV2Call('encrypted_signal_retry_sync_failed', {
                queueKey,
                attempt: queued.attempt || 0,
                conversationId: queuedConversationId,
                groupId: queuedContext.groupId,
                senderUserId,
                error: String(error?.message || error),
              });
            }

            const latestContext = resolveEncryptedVoiceCallSignalContext(queued);
            queuedForRetry = {
              ...queued,
              payload: {
                ...queued.payload,
                conversation_id:
                  latestContext.conversationId ?? queued.payload?.conversation_id ?? null,
                group_id: latestContext.groupId ?? queued.payload?.group_id ?? null,
              },
              groupId: latestContext.groupId ?? queued.groupId ?? null,
              manager: latestContext.manager ?? queued.manager ?? null,
            };
          }

          const handled = await handleEncryptedVoiceCallSignalRecord(queuedForRetry);
          if (handled) {
            clearQueuedEncryptedVoiceCallSignal(queueKey);
            return;
          }

          const nextQueued = {
            ...queuedForRetry,
            attempt: (queued.attempt || 0) + 1,
          };
          voiceCallRuntimeRef.current.pendingEncryptedSignals.set(queueKey, nextQueued);
          queueEncryptedVoiceCallSignalRecord(nextQueued);
        })().catch((error) => {
          const queued = voiceCallRuntimeRef.current.pendingEncryptedSignals.get(queueKey);
          if (!queued) return;
          traceMessengerV2Call('encrypted_signal_retry_failed', {
            queueKey,
            attempt: queued.attempt || 0,
            groupId: queued.groupId,
            senderUserId,
            error: String(error?.message || error),
          });
          const nextQueued = {
            ...queued,
            attempt: (queued.attempt || 0) + 1,
          };
          voiceCallRuntimeRef.current.pendingEncryptedSignals.set(queueKey, nextQueued);
          queueEncryptedVoiceCallSignalRecord(nextQueued);
        });
      }, delayMs);
      voiceCallRuntimeRef.current.pendingEncryptedSignalTimers.set(queueKey, timerId);
      return true;
    },
    [
      clearQueuedEncryptedVoiceCallSignal,
      currentUserId,
      handleEncryptedVoiceCallSignalRecord,
      resolveEncryptedVoiceCallSignalQueueKey,
      resolveEncryptedVoiceCallSignalContext,
    ],
  );

  const maybeNotifyConversationUpdate = useCallback(
    async ({
      conversationId = null,
      conversation = null,
      previousConversation = null,
      latestActivity = null,
    } = {}) => {
      const normalizedConversationId = toStringOrNull(conversationId);
      if (!normalizedConversationId) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'missing_conversation_id',
        });
        return null;
      }
      if (stateRef.current?.conversationCatalog?.phase !== 'ready') {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'catalog_not_ready',
          conversationId: normalizedConversationId,
        });
        return null;
      }
      if (!latestActivity || !['message', 'reaction'].includes(latestActivity.kind)) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'missing_decrypted_activity',
          conversationId: normalizedConversationId,
          activityKind: latestActivity?.kind ?? null,
        });
        return null;
      }
      if (isMessengerV2ConversationNotificationMuted(conversation)) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'room_notifications_muted',
          conversationId: normalizedConversationId,
        });
        return null;
      }

      const permission = getMessengerV2NotificationPermission();
      const visibilityState = resolveMessengerV2NotificationVisibility();
      const nextUnreadCount = Number(conversation?.unread_count ?? 0);
      const previousUnreadCount = Number(previousConversation?.unread_count ?? 0);
      const nextLastServerMsgId = toStringOrNull(conversation?.last_server_msg_id ?? null);
      const nextLastServerSeq = toServerSeqOrNull(conversation?.last_server_seq ?? null);
      const nextLastSenderUserId = toStringOrNull(conversation?.last_sender_user_id ?? null);
      const previousLastServerMsgId = toStringOrNull(
        previousConversation?.last_server_msg_id ?? null,
      );
      const previousLastServerSeq = toServerSeqOrNull(
        previousConversation?.last_server_seq ?? null,
      );
      const previousLastSenderUserId = toStringOrNull(
        previousConversation?.last_sender_user_id ?? null,
      );
      const normalizedCurrentUserId = toStringOrNull(currentUserId);
      const activityAuthorId = toStringOrNull(latestActivity.authorId ?? null);
      const conversationVisibleAnywhere = isMessengerV2ConversationVisibleInAnyWindow({
        userId: normalizedCurrentUserId || currentUserId,
        conversationId: normalizedConversationId,
        excludeWindowId: windowActivityWindowIdRef.current,
      });
      if (
        activityAuthorId &&
        normalizedCurrentUserId &&
        activityAuthorId === normalizedCurrentUserId
      ) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'self_message',
          conversationId: normalizedConversationId,
          nextLastSenderUserId: activityAuthorId,
          currentUserId: normalizedCurrentUserId,
        });
        return null;
      }
      const frontierAdvanced =
        (nextLastServerSeq != null && nextLastServerSeq !== previousLastServerSeq) ||
        (!!nextLastServerMsgId && nextLastServerMsgId !== previousLastServerMsgId);
      if (!frontierAdvanced) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'frontier_not_advanced',
          conversationId: normalizedConversationId,
          nextUnreadCount,
          previousUnreadCount,
          nextLastServerMsgId,
          previousLastServerMsgId,
          nextLastServerSeq,
          previousLastServerSeq,
          nextLastSenderUserId,
          previousLastSenderUserId,
        });
        return null;
      }
      if (
        isSilentVoiceCallSignalFrontier({
          conversationId: normalizedConversationId,
          serverMsgId: nextLastServerMsgId,
          serverSeq: nextLastServerSeq,
        })
      ) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'silent_call_signal',
          conversationId: normalizedConversationId,
          nextLastServerMsgId,
          previousLastServerMsgId,
          nextLastServerSeq,
          previousLastServerSeq,
          nextLastSenderUserId,
          previousLastSenderUserId,
        });
        return null;
      }
      if (conversationVisibleAnywhere) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'conversation_visible_elsewhere',
          conversationId: normalizedConversationId,
          conversationVisibleAnywhere,
          permission,
          visibilityState,
          nextUnreadCount,
          previousUnreadCount,
          nextLastServerMsgId,
          previousLastServerMsgId,
          nextLastServerSeq,
          previousLastServerSeq,
          nextLastSenderUserId,
          previousLastSenderUserId,
        });
        return null;
      }

      const reactionNotification = resolveMessengerV2ReactionNotification({
        activity: latestActivity,
        conversation,
        currentUserId,
        formatMessage,
      });
      if (latestActivity?.kind === 'reaction' && !reactionNotification) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'reaction_not_for_current_user',
          conversationId: normalizedConversationId,
          reactionAuthorId: latestActivity.authorId ?? null,
          reactionAction: latestActivity.action ?? null,
        });
        return null;
      }
      const notificationBody =
        reactionNotification?.body ||
        resolveMessengerV2NotificationBody({
          preview: latestActivity.preview,
          fallbackLabel: 'New message',
        });
      const notificationActor = resolveConversationNotificationActor(
        conversation,
        currentUserId,
        latestActivity?.authorId ?? nextLastSenderUserId,
      );

      const frontierKey = createMessengerV2ConversationNotificationKey({
        conversationId: normalizedConversationId,
        serverMsgId: nextLastServerMsgId,
        serverSeq: nextLastServerSeq,
        unreadCount: nextUnreadCount,
        previewText: notificationBody,
      });

      const lastNotifiedFrontierKey =
        desktopNotificationFrontierRef.current.get(normalizedConversationId) ?? null;
      if (
        !shouldShowMessengerV2ConversationNotification({
          permission,
          visibilityState,
          conversationId: normalizedConversationId,
          frontierKey,
          lastNotifiedFrontierKey,
          conversationVisibleAnywhere,
        })
      ) {
        traceMessengerV2NotificationEvent({
          stage: 'controller:skip',
          reason: 'gate_blocked',
          conversationId: normalizedConversationId,
          permission,
          visibilityState,
          frontierKey,
          lastNotifiedFrontierKey,
          conversationVisibleAnywhere,
          nextUnreadCount,
          previousUnreadCount,
          nextLastServerMsgId,
          previousLastServerMsgId,
          nextLastServerSeq,
          previousLastServerSeq,
        });
        return null;
      }

      desktopNotificationFrontierRef.current.set(normalizedConversationId, frontierKey);
      traceMessengerV2NotificationEvent({
        stage: 'controller:notify',
        conversationId: normalizedConversationId,
        permission,
        visibilityState,
        frontierKey,
        nextUnreadCount,
        previousUnreadCount,
        conversationVisibleAnywhere,
        nextLastServerMsgId,
        previousLastServerMsgId,
        nextLastServerSeq,
        previousLastServerSeq,
        nextLastSenderUserId,
        previousLastSenderUserId,
      });
      return showMessengerV2ConversationNotification({
        title: reactionNotification?.title || notificationActor.name,
        body: notificationBody,
        icon:
          protectedUrl(reactionNotification?.avatarUrl || notificationActor.avatarUrl) || undefined,
        senderName: reactionNotification?.title || notificationActor.name,
        conversationId: normalizedConversationId,
        conversationUrl: resolveMessengerV2ConversationNotificationUrl({
          conversation,
          conversationId: normalizedConversationId,
        }),
        frontierKey,
        messageId: nextLastServerMsgId ?? toStringOrNull(nextLastServerSeq ?? null),
      });
    },
    [currentUserId, formatMessage, isSilentVoiceCallSignalFrontier],
  );

  useEffect(() => {
    maybeNotifyConversationUpdateRef.current = maybeNotifyConversationUpdate;
  }, [maybeNotifyConversationUpdate]);

  useEffect(
    () => () => {
      stopIncomingVoiceCallAlerts();
    },
    [stopIncomingVoiceCallAlerts],
  );

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const runActiveThreadSync = async ({
    conversationId,
    manager = null,
    refreshToken = null,
    invalidate = false,
  } = {}) => {
    const normalizedConversationId = conversationId != null ? String(conversationId).trim() : null;
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      stateRef.current.bootstrap.phase !== 'ready' ||
      !normalizedConversationId
    ) {
      return null;
    }

    const activeClientId =
      stateRef.current.bootstrap?.clientId ?? stateRef.current.bootstrap?.identityId ?? null;
    const activeThreadRunKey = createActiveThreadRunKey({
      conversationId: normalizedConversationId,
      userId: currentUserId,
      clientId: activeClientId,
    });
    const requestKey = [
      activeThreadRunKey,
      toStringOrNull(refreshToken) || 'authoritative',
      invalidate === true ? 'invalidate' : 'refresh',
    ].join(':');

    return activeThreadSyncRef.current.run({
      conversationId: normalizedConversationId,
      requestKey,
      task: () => {
        if (String(stateRef.current.activeConversationId ?? '') === normalizedConversationId) {
          threadAutoRunKeyRef.current = activeThreadRunKey;
        }

        dispatch({
          type: 'thread:start',
          conversationId: normalizedConversationId,
          invalidate: invalidate === true,
        });

        return Promise.resolve()
          .then(() =>
            synchronizeMessengerV2ActiveThread({
              manager,
              conversationId: normalizedConversationId,
              currentUserId,
              currentUserName,
              clientId: stateRef.current.bootstrap?.clientId ?? null,
              allowRecovery: true,
              allowPendingWelcomeJoin: true,
              onSeeded: (seeded) => {
                const seededConversation = mergeCanonicalMessengerV2Conversation({
                  snapshot: stateRef.current,
                  conversationId: normalizedConversationId,
                  conversation: seeded?.conversation ?? null,
                });
                dispatch({
                  type: 'thread:seeded',
                  conversationId: normalizedConversationId,
                  conversation: seededConversation,
                  snapshot: seeded?.snapshot ?? null,
                  readyInfo: null,
                });
              },
            }),
          )
          .then((result) => {
            if (!result) {
              return null;
            }
            const mergedConversation = mergeCanonicalMessengerV2Conversation({
              snapshot: stateRef.current,
              conversationId: normalizedConversationId,
              conversation: result?.conversation ?? null,
            });
            const resolvedGroupId =
              resolveMessengerV2ConversationGroupId(mergedConversation) ??
              toStringOrNull(result?.readyInfo?.groupId ?? null) ??
              toStringOrNull(result?.groupId ?? null) ??
              null;
            const compatibleReadyInfo = resolveCompatibleMessengerV2ReadyInfo(
              result?.readyInfo ?? null,
              resolvedGroupId,
            );
            const canonicalIdentityId =
              toStringOrNull(
                result?.manager?.identityId ??
                  result?.manager?.identity_id ??
                  result?.identityId ??
                  null,
              ) || null;
            const canonicalClientId =
              toStringOrNull(
                result?.manager?.identityClientId ??
                  result?.manager?.identity_client_id ??
                  result?.clientId ??
                  canonicalIdentityId,
              ) || null;
            if (
              canonicalIdentityId ||
              canonicalClientId ||
              result?.conversation?.mls_ciphersuite != null
            ) {
              dispatch({
                type: 'bootstrap:canonicalize',
                identityId: canonicalIdentityId,
                clientId: canonicalClientId,
                ciphersuite:
                  mergedConversation?.mls_ciphersuite ??
                  mergedConversation?.meta?.mls_ciphersuite ??
                  stateRef.current.bootstrap?.ciphersuite ??
                  null,
              });
            }
            dispatch({
              type: 'thread:seeded',
              conversationId: normalizedConversationId,
              conversation: mergedConversation,
              snapshot: result?.snapshot ?? null,
              readyInfo: compatibleReadyInfo,
            });
            dispatch({
              type: 'thread:success',
              conversationId: normalizedConversationId,
              conversation: mergedConversation,
              snapshot: result?.snapshot ?? null,
              readyInfo: compatibleReadyInfo,
              ready: result?.ready === true && !!compatibleReadyInfo,
            });
            return {
              ...result,
              conversation: mergedConversation,
              readyInfo: compatibleReadyInfo,
            };
          })
          .catch((error) => {
            const snapshot = stateRef.current;
            const currentThreadRecord =
              snapshot?.threadsByConversation?.[normalizedConversationId] ??
              (toStringOrNull(snapshot?.activeConversationId ?? null) === normalizedConversationId
                ? snapshot?.activeThread
                : null);
            const preserveValidatedRuntime = Boolean(
              invalidate !== true &&
                currentThreadRecord?.phase === 'ready' &&
                currentThreadRecord?.stale !== true &&
                currentThreadRecord?.readyInfo?.ready === true &&
                currentThreadRecord?.readyInfo?.sendCapable === true,
            );
            const knownConversation = findCanonicalMessengerV2Conversation(
              snapshot,
              normalizedConversationId,
            );
            if (isMessengerV2ForbiddenError(error)) {
              dispatch({
                type: 'conversation:hide',
                conversationId: normalizedConversationId,
              });
            } else if (
              currentUserId &&
              isMessengerV2AccessDeniedError(error) &&
              resolveConversationKind(knownConversation) === 'group' &&
              !conversationIncludesParticipant(knownConversation, currentUserId)
            ) {
              dispatch({
                type: 'group:member_removed',
                conversationId: normalizedConversationId,
                removedUserId: currentUserId,
                currentUserId,
              });
            }
            dispatch({
              type: preserveValidatedRuntime ? 'thread:refreshError' : 'thread:error',
              conversationId: normalizedConversationId,
              error,
            });
            throw error;
          });
      },
    });
  };

  runActiveThreadSyncRef.current = runActiveThreadSync;

  const runActiveThreadReadySync = async ({ conversation = null, groupId = null } = {}) => {
    const normalizedConversationId =
      stateRef.current.activeConversationId != null
        ? String(stateRef.current.activeConversationId).trim()
        : null;
    const resolvedConversation =
      conversation ??
      findCanonicalMessengerV2Conversation(stateRef.current, normalizedConversationId);
    const resolvedGroupId =
      groupId ??
      resolvedConversation?.mls_group_id ??
      resolvedConversation?.meta?.mls_group_id ??
      null;

    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      stateRef.current.bootstrap.phase !== 'ready' ||
      !normalizedConversationId ||
      !resolvedConversation ||
      !resolvedGroupId
    ) {
      return null;
    }

    const result = await convergeMessengerV2ActiveThreadReady({
      conversationId: normalizedConversationId,
      conversation: resolvedConversation,
      groupId: resolvedGroupId,
      currentUserId,
      currentUserName,
    });

    if (!result) {
      return null;
    }

    const mergedConversation = mergeCanonicalMessengerV2Conversation({
      snapshot: stateRef.current,
      conversationId: normalizedConversationId,
      conversation: result?.conversation ?? resolvedConversation,
    });
    const compatibleReadyInfo = resolveCompatibleMessengerV2ReadyInfo(
      result?.readyInfo ?? null,
      resolvedGroupId,
    );

    dispatch({
      type: 'thread:success',
      conversationId: normalizedConversationId,
      conversation: mergedConversation,
      snapshot: stateRef.current.activeThread.snapshot ?? null,
      readyInfo: compatibleReadyInfo,
      ready: result?.ready === true && !!compatibleReadyInfo,
    });

    return {
      ...result,
      conversation: mergedConversation,
      readyInfo: compatibleReadyInfo,
    };
  };

  runActiveThreadReadySyncRef.current = runActiveThreadReadySync;

  const scheduleTimelineRuntimeRecovery = useCallback(
    ({ result = null, conversation = null, groupId = null } = {}) => {
      const directive = result?.recoveryDirective ?? null;
      const normalizedGroupId =
        toStringOrNull(directive?.groupId ?? directive?.group_id ?? groupId) || null;
      const normalizedConversationId =
        toStringOrNull(
          directive?.conversationId ?? directive?.conversation_id ?? conversation?.id ?? null,
        ) || null;
      if (
        result?.recoveryRequired !== true ||
        !directive ||
        !normalizedGroupId ||
        !normalizedConversationId ||
        !currentUserId
      ) {
        return;
      }

      const manager = resolveMessengerV2Manager({
        userId: currentUserId,
        identityName: currentUserName || `user-${currentUserId}`,
        preferredCiphersuite:
          directive?.authoritativeCiphersuite ??
          conversation?.mls_ciphersuite ??
          conversation?.meta?.mls_ciphersuite ??
          DEFAULT_MLS_CIPHERSUITE,
        wantPQ: true,
        serverManaged: true,
      });

      void runtimeRecoveryCoordinatorRef.current
        .request({
          directive,
          recover: () =>
            manager.recoverAuthoritativeRuntime({
              groupId: normalizedGroupId,
              conversationId: normalizedConversationId,
              authoritativeEpoch: directive?.authoritativeEpoch ?? null,
              authoritativeCiphersuite: directive?.authoritativeCiphersuite ?? null,
              reason: directive?.reason ?? 'timeline_runtime_recovery_required',
              forceLocalRebuild: directive?.forceLocalRebuild === true,
            }),
        })
        .then((outcome) => {
          if (outcome?.performed !== true || String(outcome?.status || '') !== 'recovered') {
            return null;
          }
          return runTimelineSyncRef.current?.({
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            refreshToken: `runtime-recovery:${outcome.key}`,
            resetHistory: true,
          });
        })
        .catch(() => null);
    },
    [currentUserId, currentUserName],
  );

  const runTimelineSync = async ({
    conversationId = null,
    groupId = null,
    refreshToken = null,
    resetHistory = false,
  } = {}) => {
    const normalizedConversationId =
      toStringOrNull(conversationId) ??
      (stateRef.current.activeConversationId != null
        ? String(stateRef.current.activeConversationId).trim()
        : null);
    const snapshot = stateRef.current || {};
    const resolvedConversation = findCanonicalMessengerV2Conversation(
      snapshot,
      normalizedConversationId,
    );
    const resolvedGroupId =
      toStringOrNull(groupId) ??
      toStringOrNull(
        resolvedConversation?.mls_group_id ?? resolvedConversation?.meta?.mls_group_id ?? null,
      );

    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      !normalizedConversationId ||
      !resolvedGroupId
    ) {
      return null;
    }

    return timelineSyncRef.current.run({
      conversationId: normalizedConversationId,
      requestKey: [
        resolvedGroupId,
        resetHistory === true ? 'reset-history' : 'incremental',
        toStringOrNull(refreshToken) || 'base',
      ].join(':'),
      task: async () => {
        const latestSnapshot = stateRef.current || {};
        const latestConversation =
          findCanonicalMessengerV2Conversation(latestSnapshot, normalizedConversationId) ??
          resolvedConversation;
        const latestGroupId =
          toStringOrNull(groupId) ??
          toStringOrNull(
            latestConversation?.mls_group_id ?? latestConversation?.meta?.mls_group_id ?? null,
          ) ??
          resolvedGroupId;
        const currentTimelineRecord =
          toStringOrNull(latestSnapshot.activeConversationId) === normalizedConversationId
            ? latestSnapshot.timeline
            : (latestSnapshot.timelinesByConversation?.[normalizedConversationId] ?? null);
        const targetThreadRecord =
          toStringOrNull(latestSnapshot.activeConversationId) === normalizedConversationId
            ? latestSnapshot.activeThread
            : (latestSnapshot.threadsByConversation?.[normalizedConversationId] ?? null);
        const cachedTimelineItems =
          currentTimelineRecord?.groupId === latestGroupId &&
          Array.isArray(currentTimelineRecord?.items)
            ? currentTimelineRecord.items
            : [];
        const currentTimelineItems = resetHistory === true ? [] : cachedTimelineItems;
        const hasCachedTimelineForTarget =
          currentTimelineRecord?.groupId === latestGroupId && cachedTimelineItems.length > 0;

        if (
          !hasCachedTimelineForTarget &&
          (currentTimelineRecord?.phase !== 'ready' ||
            currentTimelineRecord?.groupId !== latestGroupId)
        ) {
          dispatch({ type: 'timeline:start', conversationId: normalizedConversationId });
        }

        const currentTimelineFrontierSeq =
          resetHistory !== true && currentTimelineRecord?.groupId === latestGroupId
            ? toServerSeqOrNull(currentTimelineRecord?.frontierServerSeq ?? null)
            : null;

        try {
          const result = await loadMessengerV2ThreadTimeline({
            conversationId: normalizedConversationId,
            groupId: latestGroupId,
            currentUserId,
            currentUserName,
            conversation: latestConversation,
            sinceServerSeq: currentTimelineFrontierSeq,
            baseItems: currentTimelineItems,
            baseFrontierServerMsgId:
              resetHistory !== true && currentTimelineRecord?.groupId === latestGroupId
                ? (currentTimelineRecord?.frontierServerMsgId ?? null)
                : null,
            baseFrontierServerSeq: currentTimelineFrontierSeq,
            initialHistoryLimit: currentTimelineItems.length === 0 ? 50 : null,
            readyInfo: targetThreadRecord?.readyInfo ?? null,
            readOnly: isMessengerV2ReadOnlyGroup(targetThreadRecord?.readyInfo ?? null),
            onCallSignalRecord: ({
              record,
              groupId: recordGroupId,
              manager: recordManager,
              envelope,
            }) =>
              handleEncryptedVoiceCallSignalRecord({
                payload: record,
                groupId: recordGroupId,
                manager: recordManager,
                envelope,
              }),
          });
          dispatch({
            type: 'timeline:success',
            conversationId: normalizedConversationId,
            items: result?.items ?? [],
            latestActivity: result?.latestActivity ?? null,
            removedTargets: result?.removedTargets ?? [],
            groupId: result?.groupId ?? latestGroupId,
            frontierServerMsgId: result?.frontierServerMsgId ?? null,
            frontierServerSeq: result?.frontierServerSeq ?? null,
            applicationFrontierServerMsgId: result?.applicationFrontierServerMsgId ?? null,
            applicationFrontierServerSeq: result?.applicationFrontierServerSeq ?? null,
            remoteDeliveredServerMsgId: result?.remoteDeliveredServerMsgId ?? null,
            remoteDeliveredServerSeq: result?.remoteDeliveredServerSeq ?? null,
            remoteDecryptedServerMsgId: result?.remoteDecryptedServerMsgId ?? null,
            remoteDecryptedServerSeq: result?.remoteDecryptedServerSeq ?? null,
            unresolvedServerSeqs: result?.unresolvedServerSeqs ?? [],
            replaceTimeline: resetHistory === true,
            historyBeforeSeq: result?.historyBeforeSeq ?? null,
            historyHasMore: result?.historyHasMore,
            stale: result?.hasUnresolvedRecords === true,
          });
          scheduleTimelineRuntimeRecovery({
            result,
            conversation: latestConversation,
            groupId: latestGroupId,
          });
          return result;
        } catch (error) {
          dispatch({ type: 'timeline:error', conversationId: normalizedConversationId, error });
          throw error;
        }
      },
    });
  };

  runTimelineSyncRef.current = runTimelineSync;

  const publishReceiptFrontier = useCallback(
    async ({
      conversationId = null,
      conversation = null,
      timelineItems = [],
      remoteDeliveredServerMsgId = null,
      remoteDeliveredServerSeq = null,
      remoteDecryptedServerMsgId = null,
      remoteDecryptedServerSeq = null,
      markRead = false,
    } = {}) => {
      const normalizedConversationId = toStringOrNull(conversationId);
      if (!normalizedConversationId || !currentUserId) {
        return null;
      }

      const items = Array.isArray(timelineItems) ? timelineItems : [];
      const deliveredFrontier = selectMessengerV2AdvancedFrontier(
        resolveMessengerV2TimelineFrontier(items, { remoteOnly: true }),
        {
          serverMsgId: remoteDeliveredServerMsgId,
          serverSeq: remoteDeliveredServerSeq,
        },
      );
      const decryptedFrontier = selectMessengerV2AdvancedFrontier(
        resolveMessengerV2TimelineFrontier(items, {
          remoteOnly: true,
          renderableOnly: true,
        }),
        {
          serverMsgId: remoteDecryptedServerMsgId,
          serverSeq: remoteDecryptedServerSeq,
        },
      );
      const latestRemoteItem = items.find((item) => {
        const itemServerSeq = toServerSeqOrNull(item?.serverSeq ?? item?.server_seq ?? null);
        const itemServerMsgId = toStringOrNull(item?.serverMsgId ?? item?.server_msg_id ?? null);
        return (
          item?.mine !== true &&
          ((deliveredFrontier.serverSeq != null && itemServerSeq === deliveredFrontier.serverSeq) ||
            (deliveredFrontier.serverSeq == null &&
              deliveredFrontier.serverMsgId &&
              itemServerMsgId === deliveredFrontier.serverMsgId))
        );
      });
      if (!deliveredFrontier.serverMsgId && deliveredFrontier.serverSeq == null) {
        return null;
      }

      const readServerMsgId = markRead ? decryptedFrontier.serverMsgId : null;
      const readServerSeq = markRead ? decryptedFrontier.serverSeq : null;
      const decryptedServerMsgId = decryptedFrontier.serverMsgId;
      const decryptedServerSeq = decryptedFrontier.serverSeq;
      const deliveredServerMsgId = deliveredFrontier.serverMsgId;
      const deliveredServerSeq = deliveredFrontier.serverSeq;
      const snapshot = stateRef.current || {};
      const activeConversationSummary =
        findCanonicalMessengerV2Conversation(snapshot, normalizedConversationId) ?? null;
      const threadConversation = activeConversationSummary ?? conversation ?? null;
      const localParticipant =
        findConversationParticipant(threadConversation, currentUserId) ??
        findConversationParticipant(activeConversationSummary, currentUserId);
      const deliveredAlreadyKnown = messengerV2FrontierCovers(
        {
          serverMsgId:
            toStringOrNull(localParticipant?.last_delivered_server_msg_id ?? null) ??
            toStringOrNull(localParticipant?.last_delivered_message_id ?? null),
          serverSeq: localParticipant?.last_delivered_server_seq,
        },
        { serverMsgId: deliveredServerMsgId, serverSeq: deliveredServerSeq },
      );
      const readAlreadyKnown =
        !markRead ||
        messengerV2FrontierCovers(
          {
            serverMsgId:
              toStringOrNull(localParticipant?.last_read_server_msg_id ?? null) ??
              toStringOrNull(localParticipant?.last_read_message_id ?? null),
            serverSeq: localParticipant?.last_read_server_seq,
          },
          { serverMsgId: readServerMsgId, serverSeq: readServerSeq },
        );
      const decryptedAlreadyKnown = messengerV2FrontierCovers(
        {
          serverMsgId:
            toStringOrNull(localParticipant?.last_decrypted_server_msg_id ?? null) ??
            toStringOrNull(localParticipant?.last_read_server_msg_id ?? null),
          serverSeq:
            localParticipant?.last_decrypted_server_seq ?? localParticipant?.last_read_server_seq,
        },
        { serverMsgId: decryptedServerMsgId, serverSeq: decryptedServerSeq },
      );

      const nextReceipt = {
        conversationId: normalizedConversationId,
        lastDeliveredServerMsgId: deliveredServerMsgId,
        lastDecryptedServerMsgId: decryptedServerMsgId,
        lastReadServerMsgId: readServerMsgId,
        lastDeliveredServerSeq: deliveredServerSeq,
        lastDecryptedServerSeq: decryptedServerSeq,
        lastReadServerSeq: readServerSeq,
      };
      const commitReceipt = (acknowledgedReceipt = nextReceipt) => {
        dispatch({
          type: 'conversation:receipt',
          conversationId: normalizedConversationId,
          userId: currentUserId,
          lastDeliveredAt: latestRemoteItem?.createdAt ?? null,
          lastReadAt: markRead ? (latestRemoteItem?.createdAt ?? null) : null,
          lastDeliveredMessageId: deliveredServerMsgId,
          lastReadMessageId: readServerMsgId,
          ...acknowledgedReceipt,
          unreadCount:
            acknowledgedReceipt?.unreadCount ?? acknowledgedReceipt?.unread_count ?? null,
          clearUnread:
            markRead &&
            (acknowledgedReceipt?.unreadCount == null || acknowledgedReceipt.unreadCount === 0),
        });
      };

      if (!deliveredServerMsgId && deliveredServerSeq == null) {
        return null;
      }

      if (deliveredAlreadyKnown && decryptedAlreadyKnown && readAlreadyKnown) {
        return null;
      }

      const acknowledgedReceipt = await receiptCoordinatorRef.current.publish(nextReceipt);
      commitReceipt(acknowledgedReceipt || nextReceipt);
      return acknowledgedReceipt || nextReceipt;
    },
    [currentUserId],
  );

  useEffect(() => {
    publishReceiptFrontierRef.current = publishReceiptFrontier;
  }, [publishReceiptFrontier]);

  const runPassiveConversationSync = useCallback(
    async ({
      conversationId = null,
      refreshToken = null,
      allowRecovery = false,
      allowPendingWelcomeJoin = false,
    } = {}) => {
      const normalizedConversationId = toStringOrNull(conversationId);
      const snapshot = stateRef.current || {};
      const activeConversationId = toStringOrNull(snapshot.activeConversationId ?? null);
      if (
        !normalizedConversationId ||
        !hydrationReady ||
        !isAuthorized ||
        !currentUserId ||
        snapshot.bootstrap?.phase !== 'ready' ||
        normalizedConversationId === activeConversationId
      ) {
        return null;
      }

      return passiveConversationSyncRef.current.run({
        conversationId: normalizedConversationId,
        requestKey: [
          toStringOrNull(refreshToken) || 'base',
          allowRecovery === true ? 'recovery' : 'hydrate',
          allowPendingWelcomeJoin === true ? 'welcome' : 'no-welcome',
        ].join(':'),
        task: () =>
          Promise.resolve()
            .then(async () => {
              const latestSnapshot = stateRef.current || {};
              const bootstrapClientId = latestSnapshot.bootstrap?.clientId || null;
              const result = await synchronizeMessengerV2ActiveThread({
                conversationId: normalizedConversationId,
                currentUserId,
                currentUserName,
                clientId: bootstrapIdentityKey || bootstrapClientId || null,
                allowRecovery,
                allowPendingWelcomeJoin,
              });

              const mergedConversation = mergeCanonicalMessengerV2Conversation({
                snapshot: latestSnapshot,
                conversationId: normalizedConversationId,
                conversation: result?.conversation ?? null,
              });
              const resolvedGroupId =
                resolveMessengerV2ConversationGroupId(mergedConversation) ??
                toStringOrNull(result?.readyInfo?.groupId ?? null) ??
                toStringOrNull(result?.groupId ?? null) ??
                null;
              const compatibleReadyInfo = resolveCompatibleMessengerV2ReadyInfo(
                result?.readyInfo ?? null,
                resolvedGroupId,
              );

              dispatch({
                type: 'thread:success',
                conversationId: normalizedConversationId,
                conversation: mergedConversation,
                snapshot: result?.snapshot ?? null,
                readyInfo: compatibleReadyInfo,
                ready: result?.ready === true && !!compatibleReadyInfo,
              });

              if (!resolvedGroupId || result?.ready !== true || !compatibleReadyInfo) {
                return result;
              }

              const timelineRecord =
                latestSnapshot.timelinesByConversation?.[normalizedConversationId] ?? null;
              const timelineFrontierSeq =
                timelineRecord?.groupId === resolvedGroupId
                  ? toServerSeqOrNull(timelineRecord?.frontierServerSeq ?? null)
                  : null;
              const timelineBaseItems =
                timelineRecord?.groupId === resolvedGroupId && Array.isArray(timelineRecord?.items)
                  ? timelineRecord.items
                  : [];

              if (
                timelineCoversConversationFrontier({
                  conversation: mergedConversation,
                  timelineRecord,
                  groupId: resolvedGroupId,
                })
              ) {
                return {
                  ...result,
                  timeline: timelineRecord,
                  refreshToken,
                };
              }

              const timelineResult = await loadMessengerV2ThreadTimeline({
                conversationId: normalizedConversationId,
                groupId: resolvedGroupId,
                currentUserId,
                conversation: mergedConversation,
                manager: result?.manager ?? null,
                sinceServerSeq: timelineFrontierSeq,
                baseItems: timelineBaseItems,
                baseFrontierServerMsgId:
                  timelineRecord?.groupId === resolvedGroupId
                    ? (timelineRecord?.frontierServerMsgId ?? null)
                    : null,
                baseFrontierServerSeq: timelineFrontierSeq,
                initialHistoryLimit: timelineBaseItems.length === 0 ? 50 : null,
                readyInfo: compatibleReadyInfo ?? mergedConversation?.meta?.mls_group ?? null,
                readOnly: isMessengerV2ReadOnlyGroup(
                  compatibleReadyInfo ?? mergedConversation?.meta?.mls_group ?? null,
                ),
                onCallSignalRecord: ({
                  record,
                  groupId: recordGroupId,
                  manager: recordManager,
                  envelope,
                }) =>
                  handleEncryptedVoiceCallSignalRecord({
                    payload: record,
                    groupId: recordGroupId,
                    manager: recordManager,
                    envelope,
                  }),
              });

              dispatch({
                type: 'timeline:success',
                conversationId: normalizedConversationId,
                items: timelineResult?.items ?? [],
                latestActivity: timelineResult?.latestActivity ?? null,
                removedTargets: timelineResult?.removedTargets ?? [],
                groupId: timelineResult?.groupId ?? resolvedGroupId,
                frontierServerMsgId: timelineResult?.frontierServerMsgId ?? null,
                frontierServerSeq: timelineResult?.frontierServerSeq ?? null,
                applicationFrontierServerMsgId:
                  timelineResult?.applicationFrontierServerMsgId ?? null,
                applicationFrontierServerSeq: timelineResult?.applicationFrontierServerSeq ?? null,
                remoteDeliveredServerMsgId: timelineResult?.remoteDeliveredServerMsgId ?? null,
                remoteDeliveredServerSeq: timelineResult?.remoteDeliveredServerSeq ?? null,
                remoteDecryptedServerMsgId: timelineResult?.remoteDecryptedServerMsgId ?? null,
                remoteDecryptedServerSeq: timelineResult?.remoteDecryptedServerSeq ?? null,
                unresolvedServerSeqs: timelineResult?.unresolvedServerSeqs ?? [],
                historyBeforeSeq: timelineResult?.historyBeforeSeq ?? null,
                historyHasMore: timelineResult?.historyHasMore,
                stale: timelineResult?.hasUnresolvedRecords === true,
              });
              scheduleTimelineRuntimeRecovery({
                result: timelineResult,
                conversation: mergedConversation,
                groupId: resolvedGroupId,
              });

              await publishReceiptFrontier({
                conversationId: normalizedConversationId,
                conversation: mergedConversation,
                timelineItems: timelineResult?.items ?? [],
                remoteDeliveredServerMsgId: timelineResult?.remoteDeliveredServerMsgId ?? null,
                remoteDeliveredServerSeq: timelineResult?.remoteDeliveredServerSeq ?? null,
                remoteDecryptedServerMsgId: timelineResult?.remoteDecryptedServerMsgId ?? null,
                remoteDecryptedServerSeq: timelineResult?.remoteDecryptedServerSeq ?? null,
                markRead: false,
              }).catch(() => null);

              return {
                ...result,
                timeline: timelineResult ?? null,
                refreshToken,
              };
            })
            .catch((error) => {
              dispatch({
                type: 'thread:error',
                conversationId: normalizedConversationId,
                error,
              });
              throw error;
            }),
      });
    },
    [
      bootstrapIdentityKey,
      currentUserId,
      currentUserName,
      handleEncryptedVoiceCallSignalRecord,
      hydrationReady,
      isAuthorized,
      publishReceiptFrontier,
      scheduleTimelineRuntimeRecovery,
    ],
  );

  useEffect(() => {
    runPassiveConversationSyncRef.current = runPassiveConversationSync;
  }, [runPassiveConversationSync]);

  useEffect(() => {
    if (!isAuthorized || !currentUserId) {
      localComposerActivityPublisherRef.current?.reset();
      clearReceiptPublishes();
      desktopNotificationFrontierRef.current.clear();
      runtimeRecoveryCoordinatorRef.current.clear();
      activeThreadSyncRef.current.clear();
      completedMlsRefreshesRef.current.clear();
      restoredUserIdRef.current = null;
      runtimePersistenceClientIdRef.current = null;
      bootstrapAutoRunKeyRef.current = null;
      contactsAutoRunKeyRef.current = null;
      conversationsAutoRunKeyRef.current = null;
      threadAutoRunKeyRef.current = null;
      timelineAutoRunKeyRef.current = null;
      timelineSyncRef.current.clear();
      passiveConversationSyncRef.current.clear();
      passiveConversationAutoRunKeyRef.current = null;
      conversationCatalogRecoveryRef.current.generation += 1;
      conversationCatalogRecoveryRef.current.attempted.clear();
      conversationCatalogRecoveryRef.current.tail = Promise.resolve();
      dispatch({
        type: 'runtime:reset',
        conversationId: initialConversationId ?? null,
      });
      setHydrationReady(false);
      return;
    }

    if (restoredUserIdRef.current && restoredUserIdRef.current !== currentUserId) {
      localComposerActivityPublisherRef.current?.reset();
      clearReceiptPublishes();
      desktopNotificationFrontierRef.current.clear();
      bootstrapAutoRunKeyRef.current = null;
      contactsAutoRunKeyRef.current = null;
      conversationsAutoRunKeyRef.current = null;
      threadAutoRunKeyRef.current = null;
      timelineAutoRunKeyRef.current = null;
      activeThreadSyncRef.current.clear();
      timelineSyncRef.current.clear();
      passiveConversationSyncRef.current.clear();
      passiveConversationAutoRunKeyRef.current = null;
      conversationCatalogRecoveryRef.current.generation += 1;
      conversationCatalogRecoveryRef.current.attempted.clear();
      conversationCatalogRecoveryRef.current.tail = Promise.resolve();
      runtimeRecoveryCoordinatorRef.current.clear();
      completedMlsRefreshesRef.current.clear();
      restoredUserIdRef.current = null;
      runtimePersistenceClientIdRef.current = null;
      dispatch({
        type: 'runtime:reset',
        conversationId: initialConversationId ?? null,
      });
      setHydrationReady(false);
      return;
    }

    if (restoredUserIdRef.current === currentUserId && hydrationReady) {
      return;
    }

    let cancelled = false;

    const manager = resolveMessengerV2Manager({
      userId: currentUserId,
      identityName: currentUserName || `user-${currentUserId}`,
      preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
      wantPQ: true,
      serverManaged: true,
    });

    Promise.resolve(
      typeof manager?.ensureIdentity === 'function'
        ? manager.ensureIdentity({
            ciphersuite: DEFAULT_MLS_CIPHERSUITE,
            name: currentUserName || undefined,
          })
        : getMlsDeviceIdentity(),
    )
      .then((identity) => {
        const clientId = toStringOrNull(
          identity?.clientId ??
            identity?.client_id ??
            manager?.identityClientId ??
            manager?.identity_client_id ??
            null,
        );
        runtimePersistenceClientIdRef.current = clientId;
        if (clientId) {
          dispatch({
            type: 'bootstrap:canonicalize',
            identityId: identity?.identityId ?? identity?.identity_id ?? clientId,
            clientId,
            ciphersuite: identity?.ciphersuite ?? identity?.cipherSuite ?? DEFAULT_MLS_CIPHERSUITE,
          });
        }
        return restoreMessengerV2Runtime({ userId: currentUserId, clientId });
      })
      .catch(() => {
        runtimePersistenceClientIdRef.current = null;
        return restoreMessengerV2Runtime({ userId: currentUserId, clientId: null });
      })
      .then((restored) => {
        if (cancelled) return;

        if (restored) {
          dispatch({
            type: 'runtime:restore',
            payload: restored,
            routeConversationId: initialConversationId ?? null,
          });
        } else if (initialConversationId) {
          dispatch({
            type: 'conversation:select',
            conversationId: initialConversationId,
          });
        }

        restoredUserIdRef.current = currentUserId;
        setHydrationReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        if (initialConversationId) {
          dispatch({
            type: 'conversation:select',
            conversationId: initialConversationId,
          });
        }
        restoredUserIdRef.current = currentUserId;
        setHydrationReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [
    clearReceiptPublishes,
    currentUserId,
    currentUserName,
    hydrationReady,
    initialConversationId,
    isAuthorized,
    setHydrationReady,
  ]);

  useEffect(() => clearReceiptPublishes, [clearReceiptPublishes]);

  useEffect(() => {
    const activeConversationId = toStringOrNull(stateRef.current.activeConversationId);
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      !normalizedReadReceiptConversationId ||
      normalizedReadReceiptConversationId === activeConversationId ||
      stateRef.current.hiddenConversationIds?.includes(normalizedReadReceiptConversationId)
    ) {
      return;
    }

    threadAutoRunKeyRef.current = null;
    timelineAutoRunKeyRef.current = null;
    timelineSyncRef.current.clear();
    timelineHistorySyncRef.current = { key: null, promise: null };
    dispatch({
      type: 'conversation:select',
      conversationId: normalizedReadReceiptConversationId,
    });
  }, [currentUserId, hydrationReady, isAuthorized, normalizedReadReceiptConversationId]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId) {
      return;
    }

    let cancelled = false;
    const bootstrapState = stateRef.current.bootstrap || {};
    const bootstrapRunKey = `${currentUserId}:${currentUserName || ''}`;
    const hasRestoredBootstrap =
      bootstrapState.phase === 'ready' && (bootstrapState.identityId || bootstrapState.clientId);
    if (hasRestoredBootstrap && bootstrapAutoRunKeyRef.current === bootstrapRunKey) {
      return;
    }
    if (
      bootstrapAutoRunKeyRef.current === bootstrapRunKey &&
      (bootstrapState.phase === 'running' || bootstrapState.phase === 'error')
    ) {
      return;
    }
    bootstrapAutoRunKeyRef.current = bootstrapRunKey;
    if (!hasRestoredBootstrap) {
      dispatch({ type: 'bootstrap:start' });
    }

    Promise.resolve()
      .then(() =>
        ensureMessengerV2Bootstrap({
          currentUserId,
          currentUserName,
          preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
        }),
      )
      .then((result) => {
        if (cancelled) return;
        dispatch({
          type: 'bootstrap:success',
          identityId: result?.identityId ?? null,
          clientId: result?.clientId ?? null,
          ciphersuite: result?.ciphersuite ?? null,
          keyPackageStatus: result?.keyPackageStatus ?? null,
          keyPackageRef: result?.keyPackageRef ?? null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        dispatch({ type: 'bootstrap:error', error });
      });

    return () => {
      cancelled = true;
    };
  }, [currentUserId, currentUserName, hydrationReady, isAuthorized]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId || state.bootstrap.phase !== 'ready') {
      return undefined;
    }

    let cancelled = false;
    let refreshInFlight = null;

    const refreshCanonicalBootstrap = () => {
      if (cancelled || refreshInFlight) return refreshInFlight;

      refreshInFlight = Promise.resolve(
        ensureMessengerV2Bootstrap({
          currentUserId,
          currentUserName,
          preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
        }),
      )
        .catch((error) => {
          if (typeof console?.warn === 'function') {
            console.warn('[MLS V2] key-package inventory refresh failed', error);
          }
          return null;
        })
        .finally(() => {
          refreshInFlight = null;
        });

      return refreshInFlight;
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshCanonicalBootstrap();
      }
    };
    const intervalHandle = window.setInterval(
      refreshCanonicalBootstrap,
      MLS_KEY_PACKAGE_INVENTORY_REFRESH_INTERVAL_MS,
    );

    window.addEventListener('focus', refreshCanonicalBootstrap);
    window.addEventListener('online', refreshCanonicalBootstrap);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalHandle);
      window.removeEventListener('focus', refreshCanonicalBootstrap);
      window.removeEventListener('online', refreshCanonicalBootstrap);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [currentUserId, currentUserName, hydrationReady, isAuthorized, state.bootstrap.phase]);

  useEffect(() => {
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      !cable ||
      typeof cable?.subscriptions?.create !== 'function'
    ) {
      return undefined;
    }

    const clearRemoteComposerActivityTimer = (conversationId) => {
      const timerId = remoteComposerActivityTimersRef.current.get(conversationId);
      if (timerId) {
        window.clearTimeout(timerId);
        remoteComposerActivityTimersRef.current.delete(conversationId);
      }
    };

    const clearRemotePresenceTimer = (conversationId) => {
      const timerId = remotePresenceTimersRef.current.get(conversationId);
      if (timerId) {
        window.clearTimeout(timerId);
        remotePresenceTimersRef.current.delete(conversationId);
      }
    };

    const normalizedCurrentUserId = toStringOrNull(currentUserId);
    const subscriptionUserId = normalizedCurrentUserId || currentUserId;
    conversationEventsChannel(cable, subscriptionUserId, {
      onConnected: () => {
        passiveConversationAutoRunKeyRef.current = null;
        void recoverActiveCallsRef.current?.();
        void publishRoutedReadReceiptRef.current?.().catch(() => null);
        window.dispatchEvent(new CustomEvent('nettgrav:groups-directory-refresh'));
      },
      onDisconnected: () => {
        passiveConversationAutoRunKeyRef.current = null;
      },
      onReceive: (payload) => {
        const eventType = String(payload?.type ?? payload?.event ?? '').toLowerCase();
        if (eventType === 'inbox_activity') {
          return;
        }
        if (eventType === 'conversation_call_state') {
          const handled = handleAuthoritativeVoiceCallState(payload);
          const action = toStringOrNull(payload?.action ?? payload?.event ?? null)?.toLowerCase();
          if (action === 'roster_changed' || ['ended', 'cancelled', 'failed'].includes(action) || (!handled && action === 'incoming')) {
            void recoverActiveCallsRef.current?.({ force: true, queueIfRunning: true });
          }
          return;
        }
        if (HIDDEN_MLS_MESSAGE_EVENT_TYPES.has(eventType)) {
          const rawRecord =
            payload?.message ??
            payload?.mls_message ??
            payload?.mlsMessage ??
            payload?.record ??
            payload;
          const record = rawRecord && typeof rawRecord === 'object' ? rawRecord : {};
          const conversationId = toStringOrNull(
            payload?.conversation_id ??
              payload?.conversationId ??
              record?.conversation_id ??
              record?.conversationId ??
              null,
          );
          const snapshot = stateRef.current || {};
          const canonicalConversation = findCanonicalMessengerV2Conversation(
            snapshot,
            conversationId,
          );
          const groupId = toStringOrNull(
            payload?.group_id ??
              payload?.groupId ??
              record?.group_id ??
              record?.groupId ??
              canonicalConversation?.mls_group_id ??
              canonicalConversation?.meta?.mls_group_id ??
              null,
          );
          const contentType =
            toStringOrNull(
              payload?.content_type ??
                payload?.contentType ??
                record?.content_type ??
                record?.contentType ??
                null,
            ) || (eventType === 'hidden_mls_message' ? VOICE_CALL_SIGNAL_CONTENT_TYPE : null);
          const normalizedRecord = {
            ...record,
            conversation_id: conversationId ?? record?.conversation_id ?? null,
            group_id: groupId ?? record?.group_id ?? null,
            sender_user_id:
              record?.sender_user_id ??
              record?.senderUserId ??
              payload?.sender_user_id ??
              payload?.senderUserId ??
              null,
            sender_client_id:
              record?.sender_client_id ??
              record?.senderClientId ??
              payload?.sender_client_id ??
              payload?.senderClientId ??
              null,
            ciphertext_b64:
              record?.ciphertext_b64 ??
              record?.ciphertextB64 ??
              payload?.ciphertext_b64 ??
              payload?.ciphertextB64 ??
              null,
            envelope_b64:
              record?.envelope_b64 ??
              record?.envelopeB64 ??
              payload?.envelope_b64 ??
              payload?.envelopeB64 ??
              null,
            history_ciphertext_b64:
              record?.history_ciphertext_b64 ??
              record?.historyCiphertextB64 ??
              payload?.history_ciphertext_b64 ??
              payload?.historyCiphertextB64 ??
              null,
            history_iv_b64:
              record?.history_iv_b64 ??
              record?.historyIvB64 ??
              payload?.history_iv_b64 ??
              payload?.historyIvB64 ??
              null,
            epoch: record?.epoch ?? payload?.epoch ?? null,
            content_type: contentType ?? record?.content_type ?? null,
            hidden: true,
          };

          void Promise.resolve(
            handleEncryptedVoiceCallSignalRecord({
              payload: normalizedRecord,
              groupId,
            }),
          )
            .then((handled) => {
              if (!handled) {
                queueEncryptedVoiceCallSignalRecord({
                  payload: normalizedRecord,
                  groupId,
                });
              }
            })
            .catch(() => {
              queueEncryptedVoiceCallSignalRecord({
                payload: normalizedRecord,
                groupId,
              });
            });
          return;
        }

        if (eventType === 'metadata') {
          const conversation = payload?.conversation;
          const conversationId = toStringOrNull(
            conversation?.id ?? payload?.conversation_id ?? payload?.conversationId ?? null,
          );
          if (!conversationId || !conversation || typeof conversation !== 'object') {
            return;
          }

          const snapshot = stateRef.current || {};
          const conversations = Array.isArray(snapshot.conversations) ? snapshot.conversations : [];
          const activeConversationId = toStringOrNull(snapshot.activeConversationId ?? null);
          const isActiveConversation = activeConversationId === conversationId;
          const normalizedConversation =
            normalizeMessengerV2Conversation(conversation) || conversation;
          const previousConversation =
            conversations.find((item) => String(item?.id ?? '') === conversationId) ?? null;
          const mergedConversation = normalizedConversation;

          dispatch({
            type: 'conversation:metadata',
            conversation: mergedConversation,
          });

          if (isActiveConversation && snapshot.activeThread?.readyInfo?.ready === true) {
            const previousLastServerMsgId = toStringOrNull(
              previousConversation?.last_server_msg_id ?? null,
            );
            const previousLastServerSeq = toServerSeqOrNull(
              previousConversation?.last_server_seq ?? null,
            );
            const nextLastServerMsgId = toStringOrNull(
              mergedConversation?.last_server_msg_id ?? null,
            );
            const nextLastServerSeq = toServerSeqOrNull(
              mergedConversation?.last_server_seq ?? null,
            );
            const nextUnreadCount = Number(mergedConversation?.unread_count ?? 0);
            const messageFrontierAdvanced =
              (nextLastServerSeq != null && nextLastServerSeq !== previousLastServerSeq) ||
              (!!nextLastServerMsgId && nextLastServerMsgId !== previousLastServerMsgId) ||
              (nextUnreadCount > 0 && (nextLastServerSeq != null || !!nextLastServerMsgId));

            if (messageFrontierAdvanced) {
              timelineAutoRunKeyRef.current = null;
            }
          } else if (
            !isActiveConversation &&
            resolveMessengerV2NotificationVisibility() !== 'visible' &&
            mergedConversation
          ) {
            const previousLastServerMsgId = toStringOrNull(
              previousConversation?.last_server_msg_id ?? null,
            );
            const previousLastServerSeq = toServerSeqOrNull(
              previousConversation?.last_server_seq ?? null,
            );
            const nextLastServerMsgId = toStringOrNull(
              mergedConversation?.last_server_msg_id ?? null,
            );
            const nextLastServerSeq = toServerSeqOrNull(
              mergedConversation?.last_server_seq ?? null,
            );
            const nextUnreadCount = Number(mergedConversation?.unread_count ?? 0);
            const messageFrontierAdvanced =
              (nextLastServerSeq != null && nextLastServerSeq !== previousLastServerSeq) ||
              (!!nextLastServerMsgId && nextLastServerMsgId !== previousLastServerMsgId) ||
              (nextUnreadCount > 0 && (nextLastServerSeq != null || !!nextLastServerMsgId));

            if (messageFrontierAdvanced) {
              timelineAutoRunKeyRef.current = null;
              void Promise.resolve(
                runPassiveConversationSyncRef.current?.({
                  conversationId,
                  refreshToken:
                    nextLastServerMsgId ??
                    toStringOrNull(mergedConversation?.last_message_at ?? null) ??
                    null,
                  allowRecovery: true,
                  allowPendingWelcomeJoin: true,
                }),
              )
                .then((result) =>
                  maybeNotifyConversationUpdateRef.current?.({
                    conversationId,
                    conversation: mergedConversation,
                    previousConversation,
                    latestActivity:
                      result?.timeline?.latestActivity ?? result?.latestActivity ?? null,
                    timelineItems: result?.timeline?.items ?? result?.items ?? [],
                  }),
                )
                .catch(() => null);
            }
          }
          return;
        }

        if (eventType === 'call_signal') {
          // Call signaling is intentionally ignored on the plaintext events channel.
          // Messages-v2 voice calls require MLS/PQ encrypted hidden application messages.
          return;
        }

        if (eventType === 'presence') {
          const conversationId = toStringOrNull(
            payload?.conversation_id ?? payload?.conversationId ?? null,
          );
          const authorUserId = toStringOrNull(payload?.user_id ?? payload?.userId ?? null);
          if (!conversationId || !authorUserId || authorUserId === normalizedCurrentUserId) {
            return;
          }

          const presence = {
            state: toStringOrNull(payload?.state ?? null) || 'unknown',
            since: toStringOrNull(payload?.since ?? null),
            lastSeenAt: toStringOrNull(payload?.last_seen_at ?? payload?.lastSeenAt ?? null),
            onlineCount:
              typeof payload?.online_count === 'number'
                ? payload.online_count
                : typeof payload?.onlineCount === 'number'
                  ? payload.onlineCount
                  : null,
            totalCount:
              typeof payload?.total_count === 'number'
                ? payload.total_count
                : typeof payload?.totalCount === 'number'
                  ? payload.totalCount
                  : null,
          };

          dispatch({
            type: 'conversation:presence',
            conversationId,
            presence,
          });

          clearRemotePresenceTimer(conversationId);
          if (presence.state === 'online') {
            const timerId = window.setTimeout(() => {
              remotePresenceTimersRef.current.delete(conversationId);
              dispatch({
                type: 'conversation:presence',
                conversationId,
                presence: {
                  ...presence,
                  state: 'offline',
                },
              });
            }, REMOTE_PRESENCE_TTL_MS);
            remotePresenceTimersRef.current.set(conversationId, timerId);
          }
          return;
        }

        if (eventType === 'receipt') {
          const conversationId = toStringOrNull(
            payload?.conversation_id ?? payload?.conversationId ?? null,
          );
          const authorUserId = toStringOrNull(payload?.user_id ?? payload?.userId ?? null);
          if (!conversationId || !authorUserId || authorUserId === normalizedCurrentUserId) {
            return;
          }

          dispatch({
            type: 'conversation:receipt',
            conversationId,
            userId: authorUserId,
            lastDeliveredAt: payload?.last_delivered_at ?? payload?.lastDeliveredAt ?? null,
            lastReadAt: payload?.last_read_at ?? payload?.lastReadAt ?? null,
            lastDeliveredMessageId:
              payload?.last_delivered_message_id ?? payload?.lastDeliveredMessageId ?? null,
            lastReadMessageId: payload?.last_read_message_id ?? payload?.lastReadMessageId ?? null,
            lastDeliveredServerMsgId:
              payload?.last_delivered_server_msg_id ?? payload?.lastDeliveredServerMsgId ?? null,
            lastDecryptedServerMsgId:
              payload?.last_decrypted_server_msg_id ?? payload?.lastDecryptedServerMsgId ?? null,
            lastReadServerMsgId:
              payload?.last_read_server_msg_id ?? payload?.lastReadServerMsgId ?? null,
            lastDeliveredServerSeq:
              payload?.last_delivered_server_seq ?? payload?.lastDeliveredServerSeq ?? null,
            lastDecryptedServerSeq:
              payload?.last_decrypted_server_seq ?? payload?.lastDecryptedServerSeq ?? null,
            lastReadServerSeq: payload?.last_read_server_seq ?? payload?.lastReadServerSeq ?? null,
          });
          return;
        }

        if (eventType === 'group_member_removed') {
          const membershipUpdatedAt =
            resolveMembershipUpdatedAt(payload?.conversation) ??
            toStringOrNull(
              payload?.membership_updated_at ?? payload?.membershipUpdatedAt ?? payload?.at,
            );
          const conversation =
            payload?.conversation && typeof payload.conversation === 'object'
              ? withMembershipTimestamp(payload.conversation, membershipUpdatedAt)
              : null;
          dispatch({
            type: 'group:member_removed',
            conversationId: payload?.conversation_id ?? payload?.conversationId ?? null,
            removedUserId: payload?.removed_user_id ?? payload?.removedUserId ?? null,
            currentUserId: normalizedCurrentUserId,
            conversation,
          });
          const conversationId = toStringOrNull(
            conversation?.id ?? payload?.conversation_id ?? payload?.conversationId ?? null,
          );
          const removedUserId = toStringOrNull(
            payload?.removed_user_id ?? payload?.removedUserId ?? null,
          );
          if (conversationId && removedUserId === normalizedCurrentUserId) {
            threadAutoRunKeyRef.current = null;
            timelineAutoRunKeyRef.current = null;
            void runActiveThreadSyncRef.current?.({ conversationId }).catch(() => null);
          }
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('nettgrav:messenger-v2:group-member-removed', {
                detail: {
                  ...payload,
                  conversation,
                },
              }),
            );
          }
          return;
        }

        if (eventType === 'group_member_added') {
          const membershipUpdatedAt =
            resolveMembershipUpdatedAt(payload?.conversation) ??
            toStringOrNull(
              payload?.membership_updated_at ?? payload?.membershipUpdatedAt ?? payload?.at,
            );
          const conversation = withMembershipTimestamp(payload?.conversation, membershipUpdatedAt);
          const conversationId = toStringOrNull(
            conversation?.id ?? payload?.conversation_id ?? payload?.conversationId ?? null,
          );
          if (conversationId && conversation && typeof conversation === 'object') {
            const normalizedConversation =
              normalizeMessengerV2Conversation(conversation) || conversation;
            const snapshot = stateRef.current || {};
            const mergedConversation = normalizedConversation;
            dispatch({
              type: 'group:member_added',
              conversationId,
              addedUserId: payload?.added_user_id ?? payload?.addedUserId ?? null,
              currentUserId: normalizedCurrentUserId,
              conversation: mergedConversation,
            });

            const addedUserId = toStringOrNull(
              payload?.added_user_id ?? payload?.addedUserId ?? null,
            );
            const isKnownThread =
              toStringOrNull(snapshot.activeConversationId ?? null) === conversationId ||
              Boolean(findCanonicalMessengerV2Conversation(snapshot, conversationId));
            if (addedUserId === normalizedCurrentUserId && isKnownThread) {
              threadAutoRunKeyRef.current = null;
              timelineAutoRunKeyRef.current = null;
              void runActiveThreadSyncRef.current?.({ conversationId }).catch(() => null);
            }
          }

          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('nettgrav:messenger-v2:group-member-added', {
                detail: {
                  ...payload,
                  conversation,
                },
              }),
            );
          }
          return;
        }

        if (eventType !== 'typing') return;

        const conversationId = toStringOrNull(
          payload?.conversation_id ?? payload?.conversationId ?? null,
        );
        const authorUserId = toStringOrNull(payload?.user_id ?? payload?.userId ?? null);
        if (!conversationId || !authorUserId || authorUserId === normalizedCurrentUserId) {
          return;
        }

        const typing = payload?.typing === true;
        const activity = typing
          ? normalizeMessengerV2ComposerActivity(payload?.activity) ||
            MESSENGER_V2_COMPOSER_ACTIVITY_TYPING
          : null;
        dispatch({
          type: 'conversation:typing',
          conversationId,
          userId: authorUserId,
          typing,
          activity,
        });

        clearRemoteComposerActivityTimer(conversationId);
        if (typing) {
          const timerId = window.setTimeout(() => {
            remoteComposerActivityTimersRef.current.delete(conversationId);
            dispatch({
              type: 'conversation:typing',
              conversationId,
              userId: authorUserId,
              typing: false,
              activity: null,
            });
          }, REMOTE_COMPOSER_ACTIVITY_TTL_MS);
          remoteComposerActivityTimersRef.current.set(conversationId, timerId);
        }
      },
    });

    const remoteComposerActivityTimers = remoteComposerActivityTimersRef.current;
    const remotePresenceTimers = remotePresenceTimersRef.current;

    return () => {
      remoteComposerActivityTimers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      remoteComposerActivityTimers.clear();
      remotePresenceTimers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      remotePresenceTimers.clear();
      releaseConversationEventsSubscription(subscriptionUserId);
    };
  }, [
    bufferRemoteVoiceCallIceCandidate,
    cable,
    currentUserId,
    handleAuthoritativeVoiceCallState,
    handleEncryptedVoiceCallSignalRecord,
    hydrationReady,
    isAuthorized,
    queueEncryptedVoiceCallSignalRecord,
  ]);

  useEffect(() => {
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    let disposed = false;
    let intervalId = null;

    const sendPing = () =>
      Promise.resolve()
        .then(() => pingMessengerV2Presence())
        .catch(() => null);

    const schedule = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      intervalId = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          void sendPing();
        }
      }, PRESENCE_PING_INTERVAL_MS);
    };

    const markVisible = () => {
      if (document.visibilityState === 'visible') {
        void sendPing();
      }
    };

    const leave = () => {
      if (disposed) return;
      void Promise.resolve(leaveMessengerV2Presence({ keepalive: true })).catch(() => null);
    };

    void sendPing();
    schedule();

    document.addEventListener('visibilitychange', markVisible);
    window.addEventListener('focus', markVisible);
    window.addEventListener('pagehide', leave);
    window.addEventListener('beforeunload', leave);

    return () => {
      disposed = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', markVisible);
      window.removeEventListener('focus', markVisible);
      window.removeEventListener('pagehide', leave);
      window.removeEventListener('beforeunload', leave);
      void Promise.resolve(leaveMessengerV2Presence({ keepalive: true })).catch(() => null);
    };
  }, [currentUserId, hydrationReady, isAuthorized]);

  useEffect(() => {
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    let disposed = false;
    let intervalId = null;

    const refreshVisibleDevice = () => {
      if (disposed) return;
      const activeCall = voiceCallRuntimeRef.current.state;
      if (
        activeCall?.provider === LIVEKIT_CALL_PROVIDER &&
        activeCall?.callId &&
        activeCall?.conversationId &&
        activeCall?.connectedAt && activeCall.status === 'active'
      ) {
        const clientId = resolveMessengerV2CurrentClientId(
          stateRef.current || {},
          activeCall.conversationId,
        );
        if (!clientId) return;
        void transitionMessengerV2SfuCall({
          conversationId: activeCall.conversationId,
          callId: activeCall.callId,
          action: 'heartbeat',
          clientId,
          payload: activeCall.connectionId ? { connection_id: activeCall.connectionId } : {},
        }).catch((error) => {
          traceMessengerV2Call('call_heartbeat_failed', {
            callId: activeCall.callId,
            conversationId: activeCall.conversationId,
            error: String(error?.message || error),
          });
        });
        return;
      }
      if (activeCall?.status && activeCall.status !== 'idle') return;
      if (document.visibilityState !== 'visible') return;
      void refreshMessengerV2CallClientIdentity({
        force: true,
        conversationId: stateRef.current.activeConversationId,
      }).catch((error) => {
        traceMessengerV2Call('call_device_heartbeat_failed', {
          error: String(error?.message || error),
        });
      });
    };

    refreshVisibleDevice();
    intervalId = window.setInterval(refreshVisibleDevice, VOICE_CALL_DEVICE_HEARTBEAT_INTERVAL_MS);

    document.addEventListener('visibilitychange', refreshVisibleDevice);
    window.addEventListener('focus', refreshVisibleDevice);

    return () => {
      disposed = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', refreshVisibleDevice);
      window.removeEventListener('focus', refreshVisibleDevice);
    };
  }, [currentUserId, hydrationReady, isAuthorized, refreshMessengerV2CallClientIdentity]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId || typeof window === 'undefined') {
      return undefined;
    }

    const windowId = windowActivityWindowIdRef.current;
    const publishActivity = () => {
      void Promise.resolve(
        updateMessengerV2WindowActivity({
          userId: currentUserId,
          windowId,
          conversationId: stateRef.current.activeConversationId,
          isVisible: resolveMessengerV2WindowVisibility(),
        }),
      ).catch(() => null);
    };

    publishActivity();

    const handleActivity = () => {
      publishActivity();
    };

    window.addEventListener('focus', handleActivity);
    window.addEventListener('blur', handleActivity);
    document.addEventListener('visibilitychange', handleActivity);
    window.addEventListener('pagehide', handleActivity);
    window.addEventListener('beforeunload', handleActivity);

    return () => {
      window.removeEventListener('focus', handleActivity);
      window.removeEventListener('blur', handleActivity);
      document.removeEventListener('visibilitychange', handleActivity);
      window.removeEventListener('pagehide', handleActivity);
      window.removeEventListener('beforeunload', handleActivity);
      void Promise.resolve(
        clearMessengerV2WindowActivity({
          userId: currentUserId,
          windowId,
        }),
      ).catch(() => null);
    };
  }, [currentUserId, hydrationReady, isAuthorized, state.activeConversationId]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId || state.bootstrap.phase !== 'ready') {
      return;
    }

    let cancelled = false;
    const conversationsRunKey = `${currentUserId}:${bootstrapIdentityKey || 'bootstrap-ready'}`;
    if (conversationsAutoRunKeyRef.current === conversationsRunKey) {
      return;
    }

    conversationsAutoRunKeyRef.current = conversationsRunKey;
    const requestId = conversationCatalogRefreshRef.current.requestId + 1;
    conversationCatalogRefreshRef.current.requestId = requestId;
    dispatch({ type: 'conversations:start' });
    Promise.resolve()
      .then(() => loadMessengerV2Conversations())
      .then((items) => {
        if (cancelled || conversationCatalogRefreshRef.current.requestId !== requestId) return;
        dispatch({
          type: 'conversations:replace',
          items,
        });
      })
      .catch((error) => {
        if (cancelled || conversationCatalogRefreshRef.current.requestId !== requestId) return;
        dispatch({ type: 'conversations:error', error });
      });

    return () => {
      cancelled = true;
    };
  }, [bootstrapIdentityKey, currentUserId, hydrationReady, isAuthorized, state.bootstrap.phase]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId || state.bootstrap.phase !== 'ready') {
      return undefined;
    }

    let cancelled = false;
    const reloadConversationCatalog = () => {
      const requestId = conversationCatalogRefreshRef.current.requestId + 1;
      conversationCatalogRefreshRef.current.requestId = requestId;
      dispatch({ type: 'conversations:start' });
      Promise.resolve()
        .then(() => loadMessengerV2Conversations())
        .then((items) => {
          if (cancelled || conversationCatalogRefreshRef.current.requestId !== requestId) return;
          dispatch({
            type: 'conversations:replace',
            items,
          });
        })
        .catch((error) => {
          if (cancelled || conversationCatalogRefreshRef.current.requestId !== requestId) return;
          dispatch({ type: 'conversations:error', error });
        });
    };
    const handleRefresh = () => {
      reloadConversationCatalog();
    };
    const handleStorageRefresh = (event) => {
      if (event.key === 'nettgrav:groups-directory-refresh') {
        reloadConversationCatalog();
      }
    };

    window.addEventListener('nettgrav:groups-directory-refresh', handleRefresh);
    window.addEventListener('storage', handleStorageRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('nettgrav:groups-directory-refresh', handleRefresh);
      window.removeEventListener('storage', handleStorageRefresh);
    };
  }, [currentUserId, hydrationReady, isAuthorized, state.bootstrap.phase]);

  useEffect(() => {
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      state.bootstrap.phase !== 'ready' ||
      state.conversationCatalog?.phase !== 'ready'
    ) {
      return;
    }

    const recoveryState = conversationCatalogRecoveryRef.current;
    const generation = recoveryState.generation;
    const activeConversationId = toStringOrNull(state.activeConversationId);
    const candidates = (Array.isArray(state.conversations) ? state.conversations : [])
      .filter((conversation) => {
        const conversationId = toStringOrNull(conversation?.id ?? null);
        if (!conversationId || conversationId === activeConversationId) return false;
        const recoveryKey = [bootstrapIdentityKey || 'device', conversationId].join(':');
        if (recoveryState.attempted.has(recoveryKey)) return false;
        recoveryState.attempted.add(recoveryKey);
        return true;
      })
      .sort((left, right) => {
        const leftUnread = Number(left?.unread_count ?? 0);
        const rightUnread = Number(right?.unread_count ?? 0);
        if (rightUnread !== leftUnread) return rightUnread - leftUnread;
        return (
          new Date(right?.last_message_at ?? right?.updated_at ?? 0).getTime() -
          new Date(left?.last_message_at ?? left?.updated_at ?? 0).getTime()
        );
      });
    if (!candidates.length) return;

    const manager = resolveMessengerV2Manager({
      userId: currentUserId,
      identityName: currentUserName || `user-${currentUserId}`,
      preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
      wantPQ: true,
      serverManaged: true,
    });
    recoveryState.tail = recoveryState.tail
      .catch(() => null)
      .then(() =>
        recoverMessengerV2ConversationCatalog({
          manager,
          conversations: candidates,
          currentUserId,
          currentUserName,
          clientId: bootstrapIdentityKey,
          shouldContinue: () =>
            conversationCatalogRecoveryRef.current.generation === generation &&
            toStringOrNull(
              (stateRef.current || {}).bootstrap?.clientId ??
                (stateRef.current || {}).bootstrap?.identityId ??
                null,
            ) === toStringOrNull(bootstrapIdentityKey),
          onResult: ({ conversation, conversationId, ready, readyInfo, error }) => {
            const recoveryKey = [bootstrapIdentityKey || 'device', conversationId].join(':');
            const readinessState = toStringOrNull(
              readyInfo?.readinessState ?? readyInfo?.readiness_state ?? null,
            )?.toLowerCase();
            const terminallyUnavailable = ['blocked', 'read_only'].includes(readinessState);
            if (error || (!ready && !terminallyUnavailable)) {
              // Keep transient failures eligible for the next authoritative
              // catalog/reconnect transition without introducing a retry loop.
              conversationCatalogRecoveryRef.current.attempted.delete(recoveryKey);
            }
            if (
              conversationCatalogRecoveryRef.current.generation !== generation ||
              !readyInfo ||
              (!ready && !terminallyUnavailable) ||
              toStringOrNull((stateRef.current || {}).activeConversationId ?? null) ===
                conversationId
            ) {
              return;
            }
            dispatch({
              type: 'thread:success',
              conversationId,
              conversation,
              snapshot: null,
              readyInfo,
              ready,
            });
          },
        }),
      );
  }, [
    bootstrapIdentityKey,
    currentUserId,
    currentUserName,
    hydrationReady,
    isAuthorized,
    state.activeConversationId,
    state.bootstrap.phase,
    state.conversationCatalog?.phase,
    state.conversations,
  ]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId) {
      return undefined;
    }

    let cancelled = false;
    Promise.resolve(ensureMessengerV2NotificationServiceWorker())
      .then(() => {
        if (cancelled) return;
        return syncMessengerV2WebPushSubscription({
          clientId:
            stateRef.current?.bootstrap?.clientId ??
            stateRef.current?.bootstrap?.identityId ??
            null,
        });
      })
      .catch(() => null);

    return () => {
      cancelled = true;
    };
  }, [currentUserId, hydrationReady, isAuthorized]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId || state.bootstrap.phase !== 'ready') {
      return undefined;
    }

    const activeConversationId = toStringOrNull(state.activeConversationId);
    const candidates = (Array.isArray(state.conversations) ? state.conversations : [])
      .filter((conversation) => {
        const conversationId = toStringOrNull(conversation?.id ?? null);
        if (!conversationId || conversationId === activeConversationId) {
          return false;
        }

        const lastServerMsgId = toStringOrNull(conversation?.last_server_msg_id ?? null);
        const lastServerSeq = toServerSeqOrNull(conversation?.last_server_seq ?? null);
        if (!lastServerMsgId && lastServerSeq == null) {
          return false;
        }

        const threadRecord = state.threadsByConversation?.[conversationId] ?? null;
        const readinessState = String(
          threadRecord?.readyInfo?.readinessState ?? threadRecord?.readyInfo?.readiness_state ?? '',
        ).toLowerCase();
        if (
          threadRecord?.readyInfo?.recoveryBlocked === true ||
          threadRecord?.readyInfo?.recovery_blocked === true ||
          isMessengerV2ReadOnlyGroup(threadRecord?.readyInfo ?? null) ||
          readinessState === 'blocked'
        ) {
          return false;
        }

        const timelineRecord = state.timelinesByConversation?.[conversationId] ?? null;
        const timelineAtFrontier = timelineCoversConversationFrontier({
          conversation,
          timelineRecord,
          groupId: conversation?.mls_group_id ?? conversation?.meta?.mls_group_id ?? null,
        });
        const threadReady =
          threadRecord?.phase === 'ready' &&
          threadRecord?.stale !== true &&
          threadRecord?.readyInfo?.ready === true;
        const timelineNeedsHydration = timelineRecord?.phase !== 'ready' || !timelineAtFrontier;

        if (!threadReady || timelineRecord?.phase === 'loading') {
          return false;
        }

        return timelineNeedsHydration;
      })
      .sort((left, right) => {
        const leftUnread = Number(left?.unread_count ?? 0);
        const rightUnread = Number(right?.unread_count ?? 0);
        if (rightUnread !== leftUnread) {
          return rightUnread - leftUnread;
        }
        return (
          new Date(right?.last_message_at ?? right?.updated_at ?? 0).getTime() -
          new Date(left?.last_message_at ?? left?.updated_at ?? 0).getTime()
        );
      });

    const nextConversation = candidates[0] ?? null;
    if (!nextConversation) {
      passiveConversationAutoRunKeyRef.current = null;
      return undefined;
    }

    const nextConversationId = toStringOrNull(nextConversation?.id ?? null);
    const nextConversationRefreshToken =
      toStringOrNull(nextConversation?.last_server_msg_id ?? null) ??
      toStringOrNull(nextConversation?.last_message_at ?? null) ??
      null;
    const nextThreadRecord = state.threadsByConversation?.[nextConversationId] ?? null;
    const nextTimelineRecord = state.timelinesByConversation?.[nextConversationId] ?? null;
    const nextThreadReadinessState = String(
      nextThreadRecord?.readyInfo?.readinessState ??
        nextThreadRecord?.readyInfo?.readiness_state ??
        '',
    ).toLowerCase();
    const passiveConversationSyncKey = [
      nextConversationId,
      nextConversationRefreshToken || 'base',
      nextThreadRecord?.readyInfo?.ready === true ? 'ready' : 'pending',
      nextThreadReadinessState || 'unknown',
      nextTimelineRecord?.phase ?? 'unknown',
    ].join(':');
    if (passiveConversationAutoRunKeyRef.current === passiveConversationSyncKey) {
      return undefined;
    }
    passiveConversationAutoRunKeyRef.current = passiveConversationSyncKey;

    void runPassiveConversationSyncRef
      .current?.({
        conversationId: nextConversationId,
        refreshToken: nextConversationRefreshToken,
      })
      .catch(() => null);

    return undefined;
  }, [
    currentUserId,
    hydrationReady,
    isAuthorized,
    state.activeConversationId,
    state.bootstrap.phase,
    state.conversations,
    state.threadsByConversation,
    state.timelinesByConversation,
  ]);

  useEffect(() => {
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      !state.activeConversationId ||
      !activeThreadConversationId ||
      String(activeThreadConversationId) !== String(state.activeConversationId)
    ) {
      return;
    }

    if (!activeThreadGroupId) {
      return;
    }

    const timelineRunKey = [
      state.activeConversationId,
      activeThreadGroupId,
      currentUserId,
      activeControllerConversationFingerprint,
      activeThreadIsReady ? 'ready' : 'pending',
      activeThreadReadyInfoIsReadOnly && !activeControllerConversationIsActiveMember
        ? 'read-only'
        : 'live',
      activeConversationTimelineRefreshToken || 'base',
    ].join(':');
    if (timelineAutoRunKeyRef.current === timelineRunKey) {
      return;
    }
    const hasCachedTimeline =
      state.timeline.groupId === activeThreadGroupId && activeTimelineItemCount > 0;
    const runSync = () => {
      timelineAutoRunKeyRef.current = timelineRunKey;
      void runTimelineSyncRef.current?.({
        conversationId: state.activeConversationId,
        groupId: activeThreadGroupId,
        refreshToken: activeConversationTimelineRefreshToken,
      });
    };
    const cancelBackgroundSync = hasCachedTimeline
      ? scheduleMessengerV2BackgroundTask(runSync)
      : (runSync(), () => {});

    return () => {
      cancelBackgroundSync();
    };
  }, [
    activeThreadConversationId,
    activeThreadGroupId,
    activeControllerConversationFingerprint,
    activeConversationTimelineRefreshToken,
    activeControllerConversationIsActiveMember,
    activeThreadReadyInfoIsReadOnly,
    currentUserId,
    hydrationReady,
    isAuthorized,
    state.activeConversationId,
    activeThreadIsReady,
    activeTimelineItemCount,
    state.timeline.groupId,
  ]);

  useEffect(() => {
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      !state.activeConversationId ||
      !activeThreadGroupId ||
      !activeThreadIsReady ||
      (activeThreadReadyInfoIsReadOnly && !activeControllerConversationIsActiveMember) ||
      !cable ||
      typeof cable?.subscriptions?.create !== 'function'
    ) {
      return undefined;
    }

    const normalizedGroupId = String(activeThreadGroupId);
    mlsGroupChannel(cable, normalizedGroupId, {
      onConnected: () => {
        timelineAutoRunKeyRef.current = null;
        const snapshot = stateRef.current || {};
        const conversationId = toStringOrNull(snapshot.activeConversationId ?? null);
        const conversation = findCanonicalMessengerV2Conversation(snapshot, conversationId);
        const refreshToken =
          toStringOrNull(conversation?.last_server_msg_id ?? null) ??
          toStringOrNull(conversation?.last_server_seq ?? null) ??
          'connected';
        void Promise.resolve(
          runTimelineSyncRef.current?.({
            conversationId,
            groupId: normalizedGroupId,
            refreshToken,
          }),
        )
          .then(() => publishRoutedReadReceiptRef.current?.())
          .catch(() => null);
      },
      onReceive: async (payload) => {
        const eventType = String(
          payload?.event ?? payload?.type ?? payload?.msg_type ?? '',
        ).toLowerCase();

        if (eventType === 'mls_group_refresh') {
          const activeConversationId = toStringOrNull(
            (stateRef.current || {}).activeConversationId ?? null,
          );
          if (!activeConversationId) return;
          const eventConversationId = toStringOrNull(
            payload?.conversationId ?? payload?.conversation_id ?? null,
          );
          const eventGroupId = toStringOrNull(payload?.groupId ?? payload?.group_id ?? null);
          if (
            (eventConversationId && eventConversationId !== activeConversationId) ||
            (eventGroupId && eventGroupId !== normalizedGroupId)
          ) {
            return;
          }
          const refreshId = toStringOrNull(payload?.refreshId ?? payload?.refresh_id ?? null);
          if (refreshId && completedMlsRefreshesRef.current.get(normalizedGroupId) === refreshId) {
            return;
          }
          const refreshToken =
            refreshId ??
            [
              toStringOrNull(payload?.sessionGeneration ?? payload?.session_generation ?? null) ||
                'generation',
              toStringOrNull(payload?.epoch ?? payload?.server_epoch ?? null) || 'epoch',
            ].join(':');
          timelineAutoRunKeyRef.current = null;
          try {
            const refreshDisposition = resolveMlsRefreshDisposition({
              payload,
              threadRecord: (stateRef.current || {}).activeThread ?? null,
            });
            if (refreshDisposition.requiresThreadSync) {
              await runActiveThreadSyncRef.current?.({
                conversationId: activeConversationId,
                refreshToken,
                invalidate: refreshDisposition.invalidate,
              });
            }
            const refreshedSnapshot = stateRef.current || {};
            const refreshedConversation = findCanonicalMessengerV2Conversation(
              refreshedSnapshot,
              activeConversationId,
            );
            const timelineRefreshToken =
              toStringOrNull(refreshedConversation?.last_server_msg_id ?? null) ??
              toStringOrNull(refreshedConversation?.last_server_seq ?? null) ??
              refreshToken;
            await runTimelineSyncRef.current?.({
              conversationId: activeConversationId,
              groupId: normalizedGroupId,
              refreshToken: timelineRefreshToken,
            });
            await publishRoutedReadReceiptRef.current?.();
            if (refreshId) {
              completedMlsRefreshesRef.current.set(normalizedGroupId, refreshId);
            }
          } catch {
            // The active-thread synchronizer owns the surfaced error state.
          }
          return;
        }

        if (payload?.server_msg_id != null || payload?.server_seq != null) {
          const contentType = toStringOrNull(
            payload?.content_type ?? payload?.contentType ?? null,
          )?.toLowerCase();
          const handledCallSignal = await handleEncryptedVoiceCallSignalRecord({
            payload,
            groupId: normalizedGroupId,
          });
          if (handledCallSignal) {
            return;
          }
          if (
            contentType === VOICE_CALL_SIGNAL_CONTENT_TYPE ||
            isMessengerV2HiddenRecord(payload)
          ) {
            queueEncryptedVoiceCallSignalRecord({
              payload,
              groupId: normalizedGroupId,
            });
            return;
          }
          timelineAutoRunKeyRef.current = null;
          const snapshot = stateRef.current || {};
          const conversations = Array.isArray(snapshot.conversations) ? snapshot.conversations : [];
          const activeConversationId = toStringOrNull(snapshot.activeConversationId ?? null);
          const activeConversationSummary = conversations.find(
            (conversation) => toStringOrNull(conversation?.id ?? null) === activeConversationId,
          );
          const incomingSenderUserId = toStringOrNull(payload?.sender_user_id ?? null);
          const normalizedCurrentUserId = toStringOrNull(currentUserId);
          const isSelfMessage =
            incomingSenderUserId && normalizedCurrentUserId
              ? incomingSenderUserId === normalizedCurrentUserId
              : false;
          const isConversationVisibleAnywhere =
            !!activeConversationId &&
            isMessengerV2ConversationVisibleInAnyWindow({
              userId: normalizedCurrentUserId || currentUserId,
              conversationId: activeConversationId,
              excludeWindowId: windowActivityWindowIdRef.current,
            });
          const shouldTryMessageNotification =
            !isSelfMessage &&
            activeConversationSummary &&
            !isMessengerV2ConversationNotificationMuted(activeConversationSummary) &&
            activeConversationId &&
            resolveMessengerV2NotificationVisibility() !== 'visible' &&
            !isConversationVisibleAnywhere;
          const serverMsgId = toStringOrNull(payload?.server_msg_id ?? null);
          const serverSeq = toServerSeqOrNull(payload?.server_seq ?? null);
          const timelineSyncPromise = Promise.resolve(
            runTimelineSyncRef.current?.({
              conversationId: activeConversationId,
              groupId: normalizedGroupId,
              refreshToken: serverMsgId ?? toStringOrNull(serverSeq ?? null),
            }),
          );

          if (shouldTryMessageNotification) {
            traceMessengerV2NotificationEvent({
              stage: 'mls_group:notify_attempt',
              conversationId: activeConversationId,
              serverMsgId: toStringOrNull(payload?.server_msg_id ?? null),
              serverSeq: toServerSeqOrNull(payload?.server_seq ?? null),
              senderUserId: incomingSenderUserId,
            });
            const conversationId = activeConversationId;
            const frontierKey = createMessengerV2ConversationNotificationKey({
              conversationId,
              serverMsgId,
              serverSeq,
              unreadCount: 1,
              previewText: 'New message',
            });
            const lastNotifiedFrontierKey =
              desktopNotificationFrontierRef.current.get(conversationId) ?? null;

            if (
              conversationId &&
              frontierKey !== lastNotifiedFrontierKey &&
              !isSelfMessage &&
              shouldShowMessengerV2ConversationNotification({
                permission: getMessengerV2NotificationPermission(),
                visibilityState: resolveMessengerV2NotificationVisibility(),
                conversationId,
                frontierKey,
                lastNotifiedFrontierKey,
                conversationVisibleAnywhere: isConversationVisibleAnywhere,
              })
            ) {
              void timelineSyncPromise
                .then((timelineResult) => {
                  const latestActivity = timelineResult?.latestActivity ?? null;
                  if (!latestActivity || !['message', 'reaction'].includes(latestActivity.kind)) {
                    traceMessengerV2NotificationEvent({
                      stage: 'mls_group:notify_skip',
                      reason: 'missing_decrypted_activity',
                      conversationId,
                      activityKind: latestActivity?.kind ?? null,
                    });
                    return null;
                  }
                  if (
                    toStringOrNull(latestActivity.authorId ?? null) ===
                    toStringOrNull(currentUserId)
                  ) {
                    traceMessengerV2NotificationEvent({
                      stage: 'mls_group:notify_skip',
                      reason: 'self_decrypted_activity',
                      conversationId,
                      activityAuthorId: latestActivity.authorId ?? null,
                    });
                    return null;
                  }
                  const reactionNotification = resolveMessengerV2ReactionNotification({
                    activity: latestActivity,
                    conversation: activeConversationSummary,
                    currentUserId,
                    formatMessage,
                  });
                  if (latestActivity?.kind === 'reaction' && !reactionNotification) {
                    traceMessengerV2NotificationEvent({
                      stage: 'mls_group:notify_skip',
                      reason: 'reaction_not_for_current_user',
                      conversationId,
                      activityAuthorId: latestActivity.authorId ?? null,
                    });
                    return null;
                  }
                  const body =
                    reactionNotification?.body ||
                    resolveMessengerV2NotificationBody({
                      preview: latestActivity.preview,
                      fallbackLabel: 'New message',
                    });
                  const notificationActor = resolveConversationNotificationActor(
                    activeConversationSummary,
                    currentUserId,
                    latestActivity.authorId ?? incomingSenderUserId,
                  );
                  desktopNotificationFrontierRef.current.set(conversationId, frontierKey);
                  traceMessengerV2NotificationEvent({
                    stage: 'mls_group:notify',
                    conversationId,
                    activityKind: latestActivity.kind,
                    activityAuthorId: latestActivity.authorId ?? null,
                  });
                  return showMessengerV2ConversationNotification({
                    title: reactionNotification?.title || notificationActor.name,
                    body,
                    icon:
                      protectedUrl(
                        reactionNotification?.avatarUrl || notificationActor.avatarUrl,
                      ) || undefined,
                    senderName: reactionNotification?.title || notificationActor.name,
                    conversationId,
                    conversationUrl: resolveMessengerV2ConversationNotificationUrl({
                      conversation: activeConversationSummary,
                      conversationId,
                    }),
                    frontierKey,
                    messageId: serverMsgId ?? toStringOrNull(serverSeq ?? null),
                  });
                })
                .catch(() => null);
            }
          }
        }
      },
    });

    return () => {
      releaseGroupSubscription(normalizedGroupId);
    };
  }, [
    activeThreadGroupId,
    activeControllerConversationIsActiveMember,
    activeThreadReadyInfoIsReadOnly,
    activeThreadIsReady,
    cable,
    currentUserId,
    formatMessage,
    handleEncryptedVoiceCallSignalRecord,
    hydrationReady,
    isAuthorized,
    queueEncryptedVoiceCallSignalRecord,
    state.activeConversationId,
  ]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserSlug || state.bootstrap.phase !== 'ready') {
      return;
    }

    const contactsRunKey = `${currentUserSlug}:${bootstrapIdentityKey || 'bootstrap-ready'}`;

    if (contactsAutoRunKeyRef.current === contactsRunKey) {
      return;
    }

    contactsAutoRunKeyRef.current = contactsRunKey;

    let cancelled = false;

    dispatch({ type: 'contacts:start' });

    Promise.resolve(loadMessengerV2Contacts({ currentUserSlug }))
      .then((items) => {
        if (cancelled) return;

        dispatch({ type: 'contacts:success', items });
      })
      .catch((error) => {
        if (cancelled) return;

        dispatch({ type: 'contacts:error', error });
      });

    return () => {
      cancelled = true;
    };
  }, [currentUserSlug, hydrationReady, isAuthorized, state.bootstrap.phase, bootstrapIdentityKey]);

  useEffect(() => {
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      state.bootstrap.phase !== 'ready' ||
      !state.activeConversationId
    ) {
      return;
    }

    let cancelled = false;
    const threadRunKey = createActiveThreadRunKey({
      conversationId: state.activeConversationId,
      userId: currentUserId,
      clientId: bootstrapIdentityKey,
    });
    const activeThreadIdentityClientId =
      toStringOrNull(
        state.activeThread.readyInfo?.identityClientId ??
          state.activeThread.readyInfo?.identity_client_id ??
          null,
      ) || null;
    const hasReusableReadyThread =
      !!activeThreadConversationId &&
      String(activeThreadConversationId) === String(state.activeConversationId) &&
      state.activeThread.phase === 'ready' &&
      state.activeThread.stale !== true &&
      state.activeThread.readyInfo?.ready === true &&
      state.activeThread.readyInfo?.sendCapable === true &&
      (!bootstrapIdentityKey ||
        !activeThreadIdentityClientId ||
        activeThreadIdentityClientId === bootstrapIdentityKey);
    if (hasReusableReadyThread) {
      threadAutoRunKeyRef.current = threadRunKey;
      return;
    }
    const threadSyncAlreadyHandled =
      threadAutoRunKeyRef.current === threadRunKey &&
      ['loading', 'pending', 'ready', 'error'].includes(state.activeThread.phase);
    if (threadSyncAlreadyHandled) {
      return;
    }
    const runSync = () => {
      threadAutoRunKeyRef.current = threadRunKey;
      Promise.resolve()
        .then(() =>
          runActiveThreadSyncRef.current?.({
            conversationId: state.activeConversationId,
          }),
        )
        .then(() => {
          if (cancelled) return;
        })
        .catch((error) => {
          if (cancelled) return;
          dispatch({
            type: 'thread:error',
            conversationId: state.activeConversationId,
            error,
          });
        });
    };
    runSync();

    return () => {
      cancelled = true;
    };
  }, [
    currentUserId,
    currentUserName,
    activeControllerConversationIsActiveMember,
    activeThreadReadyInfoIsReadOnly,
    hydrationReady,
    isAuthorized,
    state.activeConversationId,
    activeThreadConversationId,
    state.activeThread.phase,
    state.activeThread.readyInfo?.identityClientId,
    state.activeThread.readyInfo?.identity_client_id,
    state.activeThread.readyInfo?.ready,
    state.activeThread.readyInfo?.sendCapable,
    state.activeThread.stale,
    state.bootstrap.clientId,
    bootstrapIdentityKey,
    state.bootstrap.phase,
  ]);

  const publishRoutedReadReceipt = useCallback(() => {
    const snapshot = stateRef.current || {};
    const conversationId = normalizedReadReceiptConversationId;
    const timelineRecord = conversationId
      ? (snapshot.timelinesByConversation?.[conversationId] ?? null)
      : null;
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      !conversationId ||
      timelineRecord?.phase !== 'ready' ||
      !resolveMessengerV2WindowVisibility()
    ) {
      return Promise.resolve(null);
    }

    return Promise.resolve(
      publishReceiptFrontierRef.current?.({
        conversationId,
        conversation: findCanonicalMessengerV2Conversation(snapshot, conversationId),
        timelineItems: timelineRecord.items,
        remoteDeliveredServerMsgId: timelineRecord.remoteDeliveredServerMsgId,
        remoteDeliveredServerSeq: timelineRecord.remoteDeliveredServerSeq,
        remoteDecryptedServerMsgId: timelineRecord.remoteDecryptedServerMsgId,
        remoteDecryptedServerSeq: timelineRecord.remoteDecryptedServerSeq,
        markRead: true,
      }),
    );
  }, [currentUserId, hydrationReady, isAuthorized, normalizedReadReceiptConversationId]);

  useEffect(() => {
    publishRoutedReadReceiptRef.current = publishRoutedReadReceipt;
  }, [publishRoutedReadReceipt]);

  useEffect(() => {
    const conversationId = normalizedReadReceiptConversationId;
    const timelineRecord = conversationId
      ? (state.timelinesByConversation?.[conversationId] ?? null)
      : null;
    if (!conversationId || timelineRecord?.phase !== 'ready') return undefined;

    void publishRoutedReadReceipt().catch(() => null);
    return undefined;
  }, [
    normalizedReadReceiptConversationId,
    publishRoutedReadReceipt,
    state.timelinesByConversation,
  ]);

  useEffect(() => {
    if (normalizedReadReceiptConversationId) return undefined;

    const conversationId = toStringOrNull(state.activeConversationId);
    const timelineRecord = conversationId
      ? (state.timelinesByConversation?.[conversationId] ?? null)
      : null;
    if (!conversationId || timelineRecord?.phase !== 'ready') return undefined;

    void Promise.resolve(
      publishReceiptFrontierRef.current?.({
        conversationId,
        conversation: findCanonicalMessengerV2Conversation(stateRef.current, conversationId),
        timelineItems: timelineRecord.items,
        remoteDeliveredServerMsgId: timelineRecord.remoteDeliveredServerMsgId,
        remoteDeliveredServerSeq: timelineRecord.remoteDeliveredServerSeq,
        remoteDecryptedServerMsgId: timelineRecord.remoteDecryptedServerMsgId,
        remoteDecryptedServerSeq: timelineRecord.remoteDecryptedServerSeq,
        markRead: false,
      }),
    ).catch(() => null);
    return undefined;
  }, [
    normalizedReadReceiptConversationId,
    state.activeConversationId,
    state.timelinesByConversation,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const publishWhenVisible = () => {
      if (!resolveMessengerV2WindowVisibility()) return;
      void publishRoutedReadReceiptRef.current?.().catch(() => null);
    };

    window.addEventListener('focus', publishWhenVisible);
    document.addEventListener('visibilitychange', publishWhenVisible);
    window.addEventListener('pageshow', publishWhenVisible);

    return () => {
      window.removeEventListener('focus', publishWhenVisible);
      document.removeEventListener('visibilitychange', publishWhenVisible);
      window.removeEventListener('pageshow', publishWhenVisible);
    };
  }, []);

  useLayoutEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId) {
      return;
    }
    Promise.resolve(
      persistMessengerV2Runtime({
        userId: currentUserId,
        clientId: state.bootstrap?.clientId ?? runtimePersistenceClientIdRef.current ?? null,
        state,
      }),
    ).catch(() => null);
  }, [currentUserId, hydrationReady, isAuthorized, state]);

  useEffect(() => {
    if (!hydrationReady || !isAuthorized || !currentUserId) {
      return undefined;
    }

    const flushRuntimeSnapshot = () => {
      Promise.resolve(
        persistMessengerV2Runtime({
          userId: currentUserId,
          clientId:
            stateRef.current?.bootstrap?.clientId ?? runtimePersistenceClientIdRef.current ?? null,
          state: stateRef.current,
        }),
      ).catch(() => null);
    };

    window.addEventListener('beforeunload', flushRuntimeSnapshot);
    return () => {
      window.removeEventListener('beforeunload', flushRuntimeSnapshot);
    };
  }, [currentUserId, hydrationReady, isAuthorized]);

  const retryBootstrap = async () => {
    if (!currentUserId) return null;
    bootstrapAutoRunKeyRef.current = null;
    dispatch({ type: 'bootstrap:start' });
    try {
      const result = await ensureMessengerV2Bootstrap({
        currentUserId,
        currentUserName,
        preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
        forcePublish: true,
      });
      dispatch({
        type: 'bootstrap:success',
        identityId: result?.identityId ?? null,
        clientId: result?.clientId ?? null,
        ciphersuite: result?.ciphersuite ?? null,
        keyPackageStatus: result?.keyPackageStatus ?? null,
        keyPackageRef: result?.keyPackageRef ?? null,
      });
      return result;
    } catch (error) {
      dispatch({ type: 'bootstrap:error', error });
      throw error;
    }
  };

  const reloadContacts = useCallback(
    async ({ background = false } = {}) => {
      if (!currentUserSlug) return [];
      contactsAutoRunKeyRef.current = null;
      if (!background) dispatch({ type: 'contacts:start' });
      try {
        const items = await Promise.resolve(loadMessengerV2Contacts({ currentUserSlug }));
        dispatch({ type: 'contacts:success', items });
        return items;
      } catch (error) {
        if (!background) dispatch({ type: 'contacts:error', error });
        throw error;
      }
    },
    [currentUserSlug],
  );

  useEffect(() => {
    if (!currentUserSlug || typeof window === 'undefined') return undefined;

    const handleCacheInvalidation = (event) => {
      const detail = event?.detail || {};
      const payload = detail.payload || detail;
      if (payload?.resource !== '/api/user_connections') return;

      const keys = new Set([...(detail.keys || []), ...(payload.keys || [])].map(String));
      const contactKey = `/api/user_connections?slug=${currentUserSlug}`;
      if (keys.size > 0 && !keys.has(contactKey) && !keys.has('/api/user_connections')) return;

      void reloadContacts({ background: true }).catch(() => {});
    };

    window.addEventListener('cache-invalidate', handleCacheInvalidation);
    return () => window.removeEventListener('cache-invalidate', handleCacheInvalidation);
  }, [currentUserSlug, reloadContacts]);

  const startConversation = async (participantId) => {
    const normalizedParticipantId = toStringOrNull(participantId);
    if (!currentUserId) {
      throw new Error('currentUserId is required');
    }
    if (!normalizedParticipantId) {
      throw new Error('participantId is required');
    }
    if (
      startConversationFlightRef.current.participantId === normalizedParticipantId &&
      startConversationFlightRef.current.promise
    ) {
      return startConversationFlightRef.current.promise;
    }
    threadAutoRunKeyRef.current = null;
    dispatch({ type: 'conversation:start' });
    const createPromise = (async () => {
      try {
        const result = await createMessengerV2Conversation({
          participantId: normalizedParticipantId,
          currentUserId,
          currentUserName,
          preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
        });

        if (result?.conversation?.id != null) {
          const createdConversationId = String(result.conversation.id);
          threadAutoRunKeyRef.current = null;
          timelineAutoRunKeyRef.current = null;
          dispatch({ type: 'conversation:created', conversation: result.conversation });
          if (typeof onOpenConversation === 'function') {
            onOpenConversation(createdConversationId, {
              kind: toStringOrNull(result.conversation?.kind ?? null) || 'direct',
            });
          }
        }

        return result;
      } catch (error) {
        dispatch({ type: 'conversation:error', error });
        throw error;
      } finally {
        if (startConversationFlightRef.current.promise === createPromise) {
          startConversationFlightRef.current = { participantId: null, promise: null };
        }
      }
    })();
    startConversationFlightRef.current = {
      participantId: normalizedParticipantId,
      promise: createPromise,
    };
    return createPromise;
  };

  const openGroupConversation = async (groupId) => {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) {
      throw new Error('groupId is required');
    }
    if (
      openGroupConversationFlightRef.current.groupId === normalizedGroupId &&
      openGroupConversationFlightRef.current.promise
    ) {
      return openGroupConversationFlightRef.current.promise;
    }

    threadAutoRunKeyRef.current = null;
    dispatch({ type: 'conversation:start' });

    const openPromise = (async () => {
      try {
        const result = await openMessengerV2GroupConversation({
          groupId: normalizedGroupId,
          currentUserId,
          currentUserName,
          preferredCiphersuite: DEFAULT_MLS_CIPHERSUITE,
        });

        if (result?.conversation?.id != null) {
          const openedConversationId = String(result.conversation.id);
          threadAutoRunKeyRef.current = null;
          timelineAutoRunKeyRef.current = null;
          dispatch({ type: 'conversation:created', conversation: result.conversation });
          if (typeof onOpenConversation === 'function') {
            onOpenConversation(openedConversationId, {
              kind: toStringOrNull(result.conversation?.kind ?? null) || 'group',
              groupId: toStringOrNull(result.conversation?.group_id ?? null) || normalizedGroupId,
            });
          }
        }

        return result;
      } catch (error) {
        dispatch({ type: 'conversation:error', error });
        throw error;
      } finally {
        if (openGroupConversationFlightRef.current.promise === openPromise) {
          openGroupConversationFlightRef.current = { groupId: null, promise: null };
        }
      }
    })();

    openGroupConversationFlightRef.current = {
      groupId: normalizedGroupId,
      promise: openPromise,
    };
    return openPromise;
  };

  const selectConversation = (conversationId, options = {}) => {
    threadAutoRunKeyRef.current = null;
    timelineAutoRunKeyRef.current = null;
    timelineSyncRef.current.clear();
    timelineHistorySyncRef.current = { key: null, promise: null };
    dispatch({ type: 'conversation:select', conversationId });
    if (
      options.notifyRoute !== false &&
      typeof onOpenConversation === 'function' &&
      conversationId != null
    ) {
      const selectedConversation =
        stateRef.current.conversations.find(
          (conversation) => String(conversation?.id ?? '') === String(conversationId),
        ) || null;
      onOpenConversation(String(conversationId), {
        kind: toStringOrNull(
          selectedConversation?.kind ?? selectedConversation?.meta?.kind ?? null,
        ),
        groupId: toStringOrNull(
          selectedConversation?.group_id ?? selectedConversation?.groupId ?? null,
        ),
      });
    }
  };

  recoverActiveCallsRef.current = async (options = {}) => {
    if (!hydrationReady || !isAuthorized || !currentUserId) return null;
    return Promise.resolve()
      .then(() => callLifecycleCoordinatorRef.current?.recover(options))
      .then(async (payload) => {
        if (!payload) return null;
        const activeCalls = Array.isArray(payload?.conversation_calls)
          ? payload.conversation_calls
          : [];
        dispatch({ type: 'calls:recovered', calls: activeCalls.filter(call =>
          call.conversation_kind === 'group' && ['ringing', 'active'].includes(call.status)
        ) });
        const pendingCallSummary = Array.isArray(payload?.pending_call_summaries)
          ? payload.pending_call_summaries[0]
          : null;
        const currentCall = voiceCallRuntimeRef.current.state || {};
        if (currentCall.status && currentCall.status !== 'idle') {
          return reconcileActiveVoiceCall();
        }
        const incomingCall = activeCalls.find(
          (call) =>
            toStringOrNull(call?.initiator_user_id) !== toStringOrNull(currentUserId) &&
            toStringOrNull(call?.status)?.toLowerCase() === 'ringing' &&
            !callLifecycleCoordinatorRef.current?.isTerminal(call?.id),
        );
        if (!incomingCall && pendingCallSummary) {
          const summary = normalizeSfuCallPayload({ conversation_call: pendingCallSummary });
          if (summary.conversationId && summary.callId) {
            selectConversation(summary.conversationId);
            const threadResult = await runActiveThreadSync({
              conversationId: summary.conversationId,
            });
            const groupId = toStringOrNull(
              threadResult?.readyInfo?.groupId ??
                threadResult?.groupId ??
                threadResult?.conversation?.mls_group_id ??
                null,
            );
            await runTimelineSyncRef.current?.({
              conversationId: summary.conversationId,
              groupId,
            });
            await writeVoiceCallHistory({
              call: {
                ...summary,
                direction:
                  toStringOrNull(summary.startedByUserId) === toStringOrNull(currentUserId)
                    ? 'outgoing'
                    : 'incoming',
                participantCount: summary.joinedParticipantCount,
                participantHistory: summary.participants,
                isGroupCall: summary.joinedParticipantCount > 2,
              },
              status: summary.status || 'ended',
            });
            return pendingCallSummary;
          }
        }
        const conversationId = toStringOrNull(incomingCall?.conversation_id);
        if (!conversationId) return null;
        if (!presentAuthoritativeIncomingVoiceCall(incomingCall)) return null;

        selectConversation(conversationId);
        const threadResult = await runActiveThreadSync({ conversationId });
        const groupId = toStringOrNull(
          threadResult?.readyInfo?.groupId ??
            threadResult?.groupId ??
            threadResult?.conversation?.mls_group_id ??
            null,
        );
        await runTimelineSyncRef.current?.({ conversationId, groupId });
        return incomingCall;
      })
      .catch((error) => {
        traceMessengerV2Call('active_call_recovery_failed', {
          error: String(error?.message || error),
        });
        return null;
      });
  };

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const recover = () => {
      if (document.visibilityState === 'visible') {
        void recoverActiveCallsRef.current?.();
      }
    };
    recover();
    window.addEventListener('focus', recover);
    window.addEventListener('online', recover);
    window.addEventListener('pageshow', recover);
    window.addEventListener(MESSENGER_V2_INCOMING_CALL_EVENT, recover);
    document.addEventListener('visibilitychange', recover);

    return () => {
      window.removeEventListener('focus', recover);
      window.removeEventListener('online', recover);
      window.removeEventListener('pageshow', recover);
      window.removeEventListener(MESSENGER_V2_INCOMING_CALL_EVENT, recover);
      document.removeEventListener('visibilitychange', recover);
    };
  }, [currentUserId, hydrationReady, isAuthorized]);

  const adoptConversation = (conversation, options = {}) => {
    const conversationId = toStringOrNull(conversation?.id ?? null);
    if (!conversationId) return null;

    dispatch({ type: 'conversation:created', conversation });
    if (options.notifyRoute !== false && typeof onOpenConversation === 'function') {
      onOpenConversation(conversationId, {
        kind: toStringOrNull(conversation?.kind ?? null),
        groupId: toStringOrNull(conversation?.group_id ?? conversation?.groupId ?? null),
      });
    }
    return conversationId;
  };

  const reloadActiveThread = async () => {
    if (!state.activeConversationId || !currentUserId) return null;
    const reloadingConversationId = toStringOrNull(state.activeConversationId);
    threadAutoRunKeyRef.current = null;
    timelineAutoRunKeyRef.current = null;
    try {
      const result = await runActiveThreadSync({
        conversationId: state.activeConversationId,
      });
      return result;
    } catch (error) {
      if (isMessengerV2ForbiddenError(error) && reloadingConversationId) {
        dispatch({
          type: 'conversation:hide',
          conversationId: reloadingConversationId,
        });
      }
      dispatch({ type: 'thread:error', error });
      throw error;
    }
  };

  const loadOlderTimelineMessages = async () => {
    const snapshot = stateRef.current;
    const conversationId = toStringOrNull(snapshot.activeConversationId);
    const conversation = findCanonicalMessengerV2Conversation(snapshot, conversationId);
    const groupId =
      toStringOrNull(conversation?.mls_group_id ?? conversation?.meta?.mls_group_id ?? null) ??
      toStringOrNull(activeThreadGroupId);
    const timelineRecord = snapshot.timeline ?? null;
    const beforeServerSeq =
      toServerSeqOrNull(timelineRecord?.historyBeforeSeq ?? null) ??
      resolveTimelineOldestServerSeq(timelineRecord?.items ?? []);

    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      !conversationId ||
      !groupId ||
      timelineRecord?.historyPhase === 'loading' ||
      timelineRecord?.historyHasMore === false
    ) {
      return null;
    }

    const syncKey = [conversationId, groupId, beforeServerSeq ?? 'latest'].join(':');
    if (timelineHistorySyncRef.current.key === syncKey && timelineHistorySyncRef.current.promise) {
      return timelineHistorySyncRef.current.promise;
    }

    dispatch({ type: 'timeline:historyStart', conversationId });

    const syncPromise = Promise.resolve()
      .then(() =>
        loadMessengerV2ThreadHistory({
          conversationId,
          groupId,
          currentUserId,
          currentUserName,
          conversation,
          beforeServerSeq,
          limit: 50,
        }),
      )
      .then((result) => {
        dispatch({
          type: 'timeline:historySuccess',
          conversationId,
          items: result?.items ?? [],
          removedTargets: result?.removedTargets ?? [],
          groupId: result?.groupId ?? groupId,
          nextBeforeServerSeq: result?.nextBeforeServerSeq ?? null,
          hasMore: result?.hasMore === true,
        });
        return result;
      })
      .catch((error) => {
        dispatch({ type: 'timeline:historyError', conversationId, error });
        throw error;
      })
      .finally(() => {
        if (timelineHistorySyncRef.current.promise === syncPromise) {
          timelineHistorySyncRef.current = { key: null, promise: null };
        }
      });

    timelineHistorySyncRef.current = {
      key: syncKey,
      promise: syncPromise,
    };

    return syncPromise;
  };

  const sendThreadMessage = async (plaintext, options = {}) => {
    const normalizedPlaintext = typeof plaintext === 'string' ? plaintext.trim() : '';
    const linkPreview = options?.linkPreview || null;
    const envelope = options?.envelope || null;
    const background = options?.background === true;
    const attachments = Array.isArray(options?.attachments)
      ? options.attachments.filter(Boolean)
      : [];
    const envelopeAttachments =
      Array.isArray(envelope?.attachments) && envelope.attachments.length > 0
        ? envelope.attachments.filter(Boolean)
        : [];
    const hasAttachments = attachments.length > 0 || envelopeAttachments.length > 0;
    const displayText = envelope
      ? resolveMessengerV2EnvelopeDisplayText(envelope) ||
        (envelope.kind === 'attachment'
          ? resolveMessengerV2AttachmentSummaryText(envelope.attachments)
          : '')
      : normalizedPlaintext || resolveMessengerV2AttachmentSummaryText(attachments);
    if (!normalizedPlaintext && !hasAttachments && !displayText) return null;
    const sendSnapshot = stateRef.current || state;
    const persistedOutboxEntry =
      options?.outboxEntry && typeof options.outboxEntry === 'object' ? options.outboxEntry : null;
    const scopedConversationId = toStringOrNull(
      persistedOutboxEntry?.conversationId ?? options?.conversationId ?? null,
    );
    const scopedConversation = scopedConversationId
      ? findCanonicalMessengerV2Conversation(sendSnapshot, scopedConversationId)
      : null;
    const scopedGroupId = toStringOrNull(
      resolveMessengerV2ConversationGroupId(scopedConversation) ??
        persistedOutboxEntry?.groupId ??
        options?.groupId ??
        null,
    );
    const sendContext = scopedConversationId
      ? {
          activeConversationId: scopedConversationId,
          threadConversationId: scopedConversationId,
          groupId: scopedGroupId,
          conversation: scopedConversation,
        }
      : resolveMessengerV2SendContext(sendSnapshot);
    if (
      !sendContext.activeConversationId ||
      (sendContext.threadConversationId &&
        sendContext.threadConversationId !== sendContext.activeConversationId) ||
      !currentUserId
    ) {
      throw createMessengerV2SendNotReadyError();
    }
    let clientMsgId = null;
    let outboxEntry = null;
    try {
      clientMsgId = toStringOrNull(options?.clientMsgId ?? null) || randomUuid();
      outboxEntry = {
        kind: 'message',
        clientMsgId,
        conversationId: sendContext.activeConversationId,
        groupId: sendContext.groupId,
        plaintext: normalizedPlaintext,
        envelope,
        linkPreview,
        attachments,
        createdAt:
          toStringOrNull(persistedOutboxEntry?.createdAt ?? null) || new Date().toISOString(),
        attempts: Math.max(0, Number(persistedOutboxEntry?.attempts) || 0),
        phase: 'sending',
        retryAt: null,
      };
      dispatch({ type: 'outbox:enqueue', entry: outboxEntry });
      if (!background) {
        dispatch({
          type: 'timeline:appendOptimistic',
          conversationId: sendContext.activeConversationId,
          groupId: sendContext.groupId,
          item: {
            id: `optimistic-${clientMsgId}`,
            conversationId: sendContext.activeConversationId,
            body: displayText,
            rawText: normalizedPlaintext || displayText,
            messageKind: envelope?.kind || (linkPreview ? 'link_preview' : 'text'),
            linkPreview: linkPreview || null,
            quote: envelope?.quote || null,
            callEvent: envelope?.callEvent || null,
            forwarded: envelope?.forwarded || null,
            attachments: envelopeAttachments.length > 0 ? envelopeAttachments : attachments,
            createdAt: new Date().toISOString(),
            authorId: currentUserId,
            authorName: currentUserName || 'You',
            mine: true,
            optimistic: true,
            delivered: false,
            localPendingUpload: false,
            clientMsgId,
            senderClientId: sendSnapshot.bootstrap?.clientId || null,
            state: 'sending',
          },
        });
      }

      if (!sendContext.conversation || !sendContext.groupId) {
        throw createMessengerV2SendNotReadyError(
          'Encrypted thread metadata is still synchronizing',
        );
      }

      const preparedSend = await ensureMessengerV2SendReady({
        conversationId: sendContext.activeConversationId,
        conversation: sendContext.conversation,
        groupId: sendContext.groupId,
        currentUserId,
        currentUserName,
        clientId: sendSnapshot.bootstrap?.clientId ?? null,
      });

      const item = await sendMessengerV2Message({
        conversationId: sendContext.activeConversationId,
        conversation: sendContext.conversation,
        groupId: sendContext.groupId,
        currentUserId,
        currentUserName,
        clientId: sendSnapshot.bootstrap?.clientId ?? null,
        plaintext: normalizedPlaintext,
        clientMsgId,
        preparedSend,
        envelope,
      });
      dispatch({
        type: 'timeline:ackMessage',
        conversationId: sendContext.activeConversationId,
        item,
        groupId: sendContext.groupId,
      });
      dispatch({ type: 'outbox:remove', clientMsgId });
      return item;
    } catch (error) {
      const attempts = Math.max(0, Number(outboxEntry?.attempts) || 0);
      const failure = resolveMessengerV2OutboxFailure({
        error,
        attempts,
        online: typeof navigator === 'undefined' || navigator.onLine !== false,
      });
      if (clientMsgId && failure.automatic) {
        dispatch({
          type: 'outbox:defer',
          clientMsgId,
          error,
          retryAt: failure.retryAt,
          waitForOnline: failure.waitForOnline,
        });
        dispatch({
          type: 'timeline:markQueued',
          conversationId: sendContext.activeConversationId,
          clientMsgId,
        });
        return {
          id: `optimistic-${clientMsgId}`,
          clientMsgId,
          conversationId: sendContext.activeConversationId,
          queued: true,
          state: 'queued',
        };
      }
      if (clientMsgId) {
        dispatch({
          type: 'timeline:markFailed',
          conversationId: sendContext.activeConversationId,
          clientMsgId,
          error,
          retryable: failure.retryable,
        });
        if (failure.retryable) {
          dispatch({ type: 'outbox:fail', clientMsgId, error, retryable: true });
        } else {
          dispatch({ type: 'outbox:remove', clientMsgId });
        }
      }
      throw error;
    }
  };
  sendThreadMessageRef.current = sendThreadMessage;

  const retryThreadMessage = async (itemOrClientMsgId = null) => {
    const clientMsgId = toStringOrNull(
      typeof itemOrClientMsgId === 'object'
        ? (itemOrClientMsgId?.clientMsgId ?? itemOrClientMsgId?.client_msg_id)
        : itemOrClientMsgId,
    );
    if (!clientMsgId) return null;
    const entry = stateRef.current.outboxByClientMsgId?.[clientMsgId] || null;
    if (!entry || entry.kind === 'reaction' || entry.retryable === false) return null;
    const existingFlight = outboundRetryFlightsRef.current.get(clientMsgId);
    if (existingFlight) return existingFlight;

    dispatch({
      type: 'timeline:markSending',
      conversationId: entry.conversationId,
      clientMsgId,
    });
    const retryPromise = sendThreadMessageRef
      .current?.(entry.plaintext || '', {
        clientMsgId,
        conversationId: entry.conversationId,
        groupId: entry.groupId || null,
        envelope: entry.envelope || null,
        linkPreview: entry.linkPreview || null,
        attachments: Array.isArray(entry.attachments) ? entry.attachments : [],
        background: true,
        outboxEntry: entry,
      })
      ?.finally(() => {
        if (outboundRetryFlightsRef.current.get(clientMsgId) === retryPromise) {
          outboundRetryFlightsRef.current.delete(clientMsgId);
        }
      });
    if (!retryPromise) return null;
    outboundRetryFlightsRef.current.set(clientMsgId, retryPromise);
    return retryPromise;
  };

  useEffect(() => {
    if (!hydrationReady || state.bootstrap?.phase !== 'ready') return undefined;
    const entries = Object.values(state.outboxByClientMsgId || {})
      .filter(
        (entry) =>
          entry &&
          typeof entry === 'object' &&
          entry.phase === 'deferred' &&
          entry.retryAt != null &&
          Number.isFinite(Number(entry.retryAt)),
      )
      .sort((left, right) => {
        const leftRetry = Number(left.retryAt) || 0;
        const rightRetry = Number(right.retryAt) || 0;
        if (leftRetry !== rightRetry) return leftRetry - rightRetry;
        return String(left.createdAt || '').localeCompare(String(right.createdAt || ''));
      });
    const nextEntry = entries[0];
    if (!nextEntry) return undefined;

    const waitMs = Math.max(0, (Number(nextEntry.retryAt) || 0) - Date.now());
    const timer = window.setTimeout(() => {
      void Promise.resolve(retryOutboxEntryRef.current?.(nextEntry)).catch(() => null);
    }, waitMs);

    return () => window.clearTimeout(timer);
  }, [hydrationReady, state.bootstrap?.phase, state.outboxByClientMsgId]);

  useEffect(() => {
    if (!hydrationReady || state.bootstrap?.phase !== 'ready') return undefined;
    const handleOnline = () => {
      dispatch({ type: 'outbox:resumeDeferred', retryAt: Date.now() });
    };
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      handleOnline();
    }
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [hydrationReady, state.bootstrap?.phase]);

  const appendLocalTimelineItem = ({ conversationId = null, groupId = null, item = null } = {}) => {
    const normalizedConversationId =
      toStringOrNull(conversationId) ?? toStringOrNull(stateRef.current.activeConversationId);
    if (!normalizedConversationId || !item || typeof item !== 'object') return null;
    dispatch({
      type: 'timeline:appendOptimistic',
      conversationId: normalizedConversationId,
      groupId:
        toStringOrNull(groupId) ??
        toStringOrNull(item.groupId ?? item.group_id ?? null) ??
        toStringOrNull(activeThreadGroupId),
      item,
    });
    return item;
  };

  const markLocalTimelineItemFailed = ({
    conversationId = null,
    clientMsgId = null,
    error = null,
  } = {}) => {
    const normalizedConversationId =
      toStringOrNull(conversationId) ?? toStringOrNull(stateRef.current.activeConversationId);
    const normalizedClientMsgId = toStringOrNull(clientMsgId);
    if (!normalizedConversationId || !normalizedClientMsgId) return;
    dispatch({
      type: 'timeline:markFailed',
      conversationId: normalizedConversationId,
      clientMsgId: normalizedClientMsgId,
      error,
    });
  };

  const updateAttachmentTimelineState = ({
    conversationId,
    clientMsgId,
    uploadState,
    progress = 0,
    error = null,
  }) => {
    dispatch({
      type: 'timeline:updateUpload',
      conversationId,
      clientMsgId,
      state: uploadState,
      progress,
      error,
    });
  };

  const patchQueuedAttachment = async (clientMsgId, index, patch) =>
    updateMessengerV2AttachmentUploadJob(clientMsgId, (current) => ({
      ...current,
      attachments: (Array.isArray(current.attachments) ? current.attachments : []).map(
        (attachment, attachmentIndex) => {
          if (attachmentIndex !== index) return attachment;
          const resolvedPatch = typeof patch === 'function' ? patch(attachment, current) : patch;
          return { ...attachment, ...resolvedPatch };
        },
      ),
    }));

  const assertAttachmentUploadCanContinue = async (clientMsgId, signal) => {
    const latestJob = await getMessengerV2AttachmentUploadJob(clientMsgId);
    if (
      signal?.aborted ||
      !latestJob ||
      latestJob.cancelled === true ||
      latestJob.paused === true ||
      latestJob.state === 'paused'
    ) {
      throw Object.assign(new Error('Attachment upload paused'), { name: 'AbortError' });
    }
    return latestJob;
  };

  const waitForAttachmentRetry = (delayMs, signal = null) =>
    new Promise((resolve, reject) => {
      if (!delayMs) {
        resolve();
        return;
      }
      const timerId = window.setTimeout(resolve, delayMs);
      signal?.addEventListener?.(
        'abort',
        () => {
          window.clearTimeout(timerId);
          reject(Object.assign(new Error('Attachment upload aborted'), { name: 'AbortError' }));
        },
        { once: true },
      );
    });

  const projectAttachmentUploadJob = (job) => {
    if (!job) return null;

    const clientMsgId = toStringOrNull(job.clientMsgId);
    const conversationId = toStringOrNull(job.conversationId);
    if (!clientMsgId || !conversationId) return null;

    const attachments = (Array.isArray(job.attachments) ? job.attachments : []).map(
      (attachment, index) => ({
        id: attachment.draftId || `${clientMsgId}-attachment-${index}`,
        draftId: attachment.draftId || null,
        name: attachment.name || `Attachment ${index + 1}`,
        mimeType: attachment.mimeType || '',
        kind: attachment.kind || 'file',
        size: Math.max(0, Number(attachment.size) || 0),
        caption: attachment.caption || '',
        previewUrl: attachment.previewUrl || null,
        voiceNote: attachment.voiceNote === true,
        durationMs: Math.max(0, Number(attachment.durationMs) || 0),
        waveform: Array.isArray(attachment.waveform) ? attachment.waveform.slice(0, 128) : null,
        localPendingUpload: true,
      }),
    );
    const displayText = job.plaintext || resolveMessengerV2AttachmentSummaryText(attachments);

    dispatch({
      type: 'outbox:enqueue',
      entry: {
        clientMsgId,
        conversationId,
        groupId: job.groupId || null,
        plaintext: job.plaintext || '',
        linkPreview: job.linkPreview || null,
        createdAt: job.createdAt,
        attempts: Math.max(0, Number(job.attempts) || 0),
        phase: 'uploading',
        retryAt: null,
      },
    });
    dispatch({
      type: 'timeline:appendOptimistic',
      conversationId,
      groupId: job.groupId || null,
      item: {
        id: `optimistic-${clientMsgId}`,
        conversationId,
        groupId: job.groupId || null,
        body: displayText,
        rawText: displayText,
        messageKind: 'attachment',
        linkPreview: job.linkPreview || null,
        quote: job.replyQuote || null,
        attachments,
        createdAt: job.createdAt,
        authorId: currentUserId,
        authorName: currentUserName || 'You',
        mine: true,
        optimistic: true,
        delivered: false,
        clientMsgId,
        senderClientId: stateRef.current.bootstrap?.clientId || null,
        localPendingUpload: true,
        uploadProgress: Math.max(0, Number(job.progress) || 0),
        state: 'uploading',
      },
    });

    return { attachments, displayText };
  };

  const runAttachmentUploadJob = async (clientMsgId) => {
    const normalizedClientMsgId = toStringOrNull(clientMsgId);
    if (!normalizedClientMsgId) return null;
    const activeJob = attachmentUploadRuntimeRef.current.activeJobs.get(normalizedClientMsgId);
    if (activeJob) return activeJob;

    const abortController = new AbortController();
    attachmentUploadRuntimeRef.current.abortControllers.set(normalizedClientMsgId, abortController);
    const jobPromise = (async () => {
      let job = await getMessengerV2AttachmentUploadJob(normalizedClientMsgId);
      if (!job || job.cancelled === true || job.paused === true || job.state === 'paused') {
        return null;
      }
      if (job.userId && String(job.userId) !== String(currentUserId)) return null;

      const conversationId = toStringOrNull(job.conversationId);
      const { displayText } = projectAttachmentUploadJob(job);

      const uploadedAttachments = [];
      const attachmentCount = Math.max(1, job.attachments.length);
      for (let index = 0; index < job.attachments.length; index += 1) {
        if (abortController.signal.aborted) {
          throw Object.assign(new Error('Attachment upload aborted'), { name: 'AbortError' });
        }
        job = (await getMessengerV2AttachmentUploadJob(normalizedClientMsgId)) || job;
        let queuedAttachment = job.attachments[index];
        if (queuedAttachment?.state === 'uploaded' && queuedAttachment.resultAttachment) {
          uploadedAttachments.push(queuedAttachment.resultAttachment);
          continue;
        }

        let queuedPayload = await getMessengerV2AttachmentUploadPayload(
          normalizedClientMsgId,
          index,
        );
        let prepared = queuedPayload?.prepared || null;
        if (!prepared) {
          if (!queuedPayload?.sourceFile) {
            throw new Error('The local attachment file is no longer available');
          }
          updateAttachmentTimelineState({
            conversationId,
            clientMsgId: normalizedClientMsgId,
            uploadState: 'preparing',
            progress: Math.round((index / attachmentCount) * 100),
          });
          let uploadFile;
          try {
            uploadFile = await materializeMessengerV2ImageEdit({
              file: queuedPayload.sourceFile,
              name: queuedAttachment.name,
              mimeType: queuedAttachment.mimeType,
              editDocument: queuedAttachment.editDocument || null,
              previewUrl: null,
            });
          } catch (error) {
            throw createMessengerV2AttachmentError('edit_render', error);
          }
          try {
            prepared = await prepareMessengerV2AttachmentUpload({
              file: uploadFile,
              filename: queuedAttachment.name,
              contentType: queuedAttachment.mimeType,
            });
          } catch (error) {
            throw createMessengerV2AttachmentError('encryption', error);
          }
          queuedPayload = await updateMessengerV2AttachmentUploadPayload(
            normalizedClientMsgId,
            index,
            {
              prepared,
              sourceFile: null,
            },
          );
          job = await patchQueuedAttachment(normalizedClientMsgId, index, {
            state: 'queued',
            progress: 0,
          });
          queuedAttachment = job.attachments[index];
        }

        let uploadedAttachment = null;
        let lastError = null;
        for (let attempt = 0; attempt < ATTACHMENT_UPLOAD_RETRY_DELAYS_MS.length; attempt += 1) {
          await waitForAttachmentRetry(
            ATTACHMENT_UPLOAD_RETRY_DELAYS_MS[attempt],
            abortController.signal,
          );
          try {
            job = (await getMessengerV2AttachmentUploadJob(normalizedClientMsgId)) || job;
            queuedAttachment = job.attachments[index];
            await patchQueuedAttachment(normalizedClientMsgId, index, {
              state: 'uploading',
              attempt: attempt + 1,
              lastError: null,
            });
            const visibleProgressFloor = Math.max(0, Number(job?.progress) || 0);
            const uploadResult = await uploadResumablePreparedMessengerV2Attachment({
              prepared,
              session: queuedAttachment?.multipartSession || null,
              createMultipartUpload: createMessengerV2MultipartAttachmentUpload,
              refreshMultipartUpload: refreshMessengerV2MultipartAttachmentUpload,
              completeMultipartUpload: completeMessengerV2MultipartAttachmentUpload,
              signal: abortController.signal,
              onSession: async (multipartSession) => {
                await patchQueuedAttachment(
                  normalizedClientMsgId,
                  index,
                  (_attachment, currentJob) => ({
                    multipartSession,
                    objectKey: multipartSession.objectKey,
                    state:
                      currentJob?.paused === true || currentJob?.state === 'paused'
                        ? 'paused'
                        : 'uploading',
                  }),
                );
                void requestMessengerV2AttachmentBackgroundSync();
              },
              onProgress: (attachmentProgress) => {
                if (abortController.signal.aborted) return;
                const measuredProgress = Math.round(
                  ((index + attachmentProgress / 100) / attachmentCount) * 100,
                );
                // A partially transmitted multipart chunk restarts after Resume. Keep the
                // visible percentage monotonic so Pause/Resume never appears to lose progress.
                const overallProgress = Math.max(visibleProgressFloor, measuredProgress);
                updateAttachmentTimelineState({
                  conversationId,
                  clientMsgId: normalizedClientMsgId,
                  uploadState: 'uploading',
                  progress: overallProgress,
                });
              },
            });
            uploadedAttachment = uploadResult.attachment;
            queuedAttachment = {
              ...queuedAttachment,
              multipartSession: uploadResult.session,
            };
            job = await assertAttachmentUploadCanContinue(
              normalizedClientMsgId,
              abortController.signal,
            );
            break;
          } catch (error) {
            lastError = normalizeMessengerV2AttachmentError(error, 'storage_upload');
            if (error?.name === 'AbortError') throw error;
            await patchQueuedAttachment(normalizedClientMsgId, index, {
              state: 'failed',
              lastError: String(lastError?.message || lastError),
            });
          }
        }
        if (!uploadedAttachment) throw lastError || new Error('Attachment upload failed');

        const completedAttachment = {
          ...uploadedAttachment,
          caption: queuedAttachment.caption || '',
          voiceNote: queuedAttachment.voiceNote === true,
          durationMs: Math.max(0, Number(queuedAttachment.durationMs) || 0),
          waveform: Array.isArray(queuedAttachment.waveform)
            ? queuedAttachment.waveform.slice(0, 128)
            : null,
        };
        uploadedAttachments.push(completedAttachment);
        await patchQueuedAttachment(normalizedClientMsgId, index, {
          state: 'uploaded',
          progress: 100,
          signedUrl: null,
          resultAttachment: completedAttachment,
          multipartSession: queuedAttachment.multipartSession,
        });
        await removeMessengerV2AttachmentUploadPayload(normalizedClientMsgId, index);
      }

      job = await assertAttachmentUploadCanContinue(normalizedClientMsgId, abortController.signal);
      const envelope = createMessengerV2AttachmentEnvelope({
        caption: job.plaintext || '',
        attachments: uploadedAttachments,
        preview: job.linkPreview || null,
        target: job.replyTarget || null,
        quote: job.replyQuote || null,
      });
      const sendingEntry = {
        clientMsgId: normalizedClientMsgId,
        conversationId,
        groupId: job.groupId || null,
        plaintext: job.plaintext || '',
        attachments: uploadedAttachments,
        envelope,
        linkPreview: job.linkPreview || null,
        createdAt: job.createdAt,
        attempts: Math.max(0, Number(job.attempts) || 0),
        phase: 'sending',
        retryAt: null,
      };
      dispatch({ type: 'outbox:enqueue', entry: sendingEntry });
      dispatch({
        type: 'timeline:markSending',
        conversationId,
        clientMsgId: normalizedClientMsgId,
        attachments: uploadedAttachments,
        body: resolveMessengerV2EnvelopeDisplayText(envelope) || displayText,
        rawText: job.plaintext || displayText,
      });

      const result = await sendThreadMessage(job.plaintext || '', {
        clientMsgId: normalizedClientMsgId,
        conversationId,
        groupId: job.groupId || null,
        attachments: uploadedAttachments,
        envelope,
        linkPreview: job.linkPreview || null,
        background: true,
        outboxEntry: sendingEntry,
      });
      job.attachments.forEach(revokeMessengerV2AttachmentDraftPreview);
      await removeMessengerV2AttachmentUploadJob(normalizedClientMsgId);
      return result;
    })()
      .catch(async (error) => {
        const job = await getMessengerV2AttachmentUploadJob(normalizedClientMsgId);
        if (
          error?.name === 'AbortError' ||
          job?.cancelled === true ||
          job?.paused === true ||
          job?.state === 'paused'
        ) {
          return null;
        }
        if (job) {
          await saveMessengerV2AttachmentUploadJob({
            ...job,
            state: 'failed',
            attempts: Math.max(0, Number(job.attempts) || 0) + 1,
            lastError: String(error?.message || error),
          }).catch(() => null);
          dispatch({
            type: 'outbox:enqueue',
            entry: {
              clientMsgId: normalizedClientMsgId,
              conversationId: job.conversationId,
              groupId: job.groupId || null,
              plaintext: job.plaintext || '',
              createdAt: job.createdAt,
              attempts: Math.max(0, Number(job.attempts) || 0) + 1,
              phase: 'failed',
              retryAt: null,
            },
          });
          updateAttachmentTimelineState({
            conversationId: job.conversationId,
            clientMsgId: normalizedClientMsgId,
            uploadState: 'failed',
            progress: Math.max(0, Number(job.progress) || 0),
            error,
          });
          void requestMessengerV2AttachmentBackgroundSync({ processNow: true });
        }
        throw error;
      })
      .finally(() => {
        attachmentUploadRuntimeRef.current.activeJobs.delete(normalizedClientMsgId);
        attachmentUploadRuntimeRef.current.abortControllers.delete(normalizedClientMsgId);
      });

    attachmentUploadRuntimeRef.current.activeJobs.set(normalizedClientMsgId, jobPromise);
    return jobPromise;
  };

  const retryAttachmentMessage = async (itemOrClientMsgId = null) => {
    const clientMsgId = toStringOrNull(
      typeof itemOrClientMsgId === 'object'
        ? (itemOrClientMsgId?.clientMsgId ?? itemOrClientMsgId?.client_msg_id)
        : itemOrClientMsgId,
    );
    if (!clientMsgId) return null;
    const job = await updateMessengerV2AttachmentUploadJob(clientMsgId, (current) => ({
      ...current,
      cancelled: false,
      paused: false,
      state: 'queued',
      lastError: null,
      attachments: (Array.isArray(current.attachments) ? current.attachments : []).map(
        (attachment) => ({
          ...attachment,
          state: attachment?.state === 'uploaded' ? 'uploaded' : 'queued',
        }),
      ),
    }));
    if (!job) return null;
    updateAttachmentTimelineState({
      conversationId: job.conversationId,
      clientMsgId,
      uploadState: 'uploading',
      progress: Math.max(0, Number(job.progress) || 0),
    });
    return runAttachmentUploadJob(clientMsgId);
  };

  const pauseAttachmentMessage = async (itemOrClientMsgId = null) => {
    const clientMsgId = toStringOrNull(
      typeof itemOrClientMsgId === 'object'
        ? (itemOrClientMsgId?.clientMsgId ??
            itemOrClientMsgId?.client_msg_id ??
            itemOrClientMsgId?.clientMessageId ??
            itemOrClientMsgId?.client_message_id)
        : itemOrClientMsgId,
    );
    if (!clientMsgId) return false;
    // Stop foreground progress callbacks before committing the paused state.
    attachmentUploadRuntimeRef.current.abortControllers.get(clientMsgId)?.abort?.();
    const visibleProgress =
      typeof itemOrClientMsgId === 'object'
        ? Number(itemOrClientMsgId?.uploadProgress ?? itemOrClientMsgId?.progress)
        : 0;
    const job = await updateMessengerV2AttachmentUploadJob(clientMsgId, (current) => {
      const pausedProgress = Math.max(
        0,
        Math.min(100, Math.round(Math.max(Number(current.progress) || 0, visibleProgress || 0))),
      );
      return {
        ...current,
        paused: true,
        progress: pausedProgress,
        state: 'paused',
        attachments: (Array.isArray(current.attachments) ? current.attachments : []).map(
          (attachment) => ({
            ...attachment,
            state: attachment?.state === 'uploaded' ? 'uploaded' : 'paused',
          }),
        ),
      };
    });
    if (!job) return false;
    const pausedProgress = Math.max(0, Math.min(100, Math.round(Number(job.progress) || 0)));
    dispatch({
      type: 'outbox:enqueue',
      entry: {
        clientMsgId,
        conversationId: job.conversationId,
        groupId: job.groupId || null,
        plaintext: job.plaintext || '',
        createdAt: job.createdAt,
        attempts: Math.max(0, Number(job.attempts) || 0),
        phase: 'paused',
        retryAt: null,
      },
    });
    updateAttachmentTimelineState({
      conversationId: job.conversationId || stateRef.current.activeConversationId,
      clientMsgId,
      uploadState: 'paused',
      progress: pausedProgress,
    });
    return true;
  };

  const resumeAttachmentMessage = async (itemOrClientMsgId = null) => {
    const clientMsgId = toStringOrNull(
      typeof itemOrClientMsgId === 'object'
        ? (itemOrClientMsgId?.clientMsgId ?? itemOrClientMsgId?.client_msg_id)
        : itemOrClientMsgId,
    );
    if (!clientMsgId) return null;
    const stoppingJob = attachmentUploadRuntimeRef.current.activeJobs.get(clientMsgId);
    if (stoppingJob) await stoppingJob.catch(() => null);
    return retryAttachmentMessage(clientMsgId);
  };

  const sendAttachmentMessage = async (plaintext, options = {}) => {
    const drafts = Array.isArray(options?.drafts)
      ? options.drafts.filter((draft) => draft?.file)
      : [];
    if (!drafts.length) return null;

    const sendSnapshot = stateRef.current || state;
    const scopedConversationId = toStringOrNull(
      options?.conversationId ?? sendSnapshot.activeConversationId,
    );
    const scopedConversation = scopedConversationId
      ? findCanonicalMessengerV2Conversation(sendSnapshot, scopedConversationId)
      : null;
    const sendContext = scopedConversationId
      ? {
          activeConversationId: scopedConversationId,
          groupId: toStringOrNull(resolveMessengerV2ConversationGroupId(scopedConversation)),
        }
      : resolveMessengerV2SendContext(sendSnapshot);
    if (!sendContext.activeConversationId || !currentUserId) {
      throw createMessengerV2SendNotReadyError();
    }

    const clientMsgId = toStringOrNull(options?.clientMsgId ?? null) || randomUuid();
    const createdAt = new Date().toISOString();
    const normalizedPlaintext = typeof plaintext === 'string' ? plaintext.trim() : '';
    options?.onPreparationProgress?.({ completed: 0, total: drafts.length });
    const queuedJob = await saveMessengerV2AttachmentUploadJob({
      clientMsgId,
      userId: currentUserId,
      conversationId: sendContext.activeConversationId,
      groupId: sendContext.groupId || null,
      plaintext: normalizedPlaintext,
      linkPreview: options?.linkPreview || null,
      replyTarget: options?.replyTarget || null,
      replyQuote: options?.replyQuote || null,
      createdAt,
      attempts: 0,
      progress: 0,
      paused: false,
      state: 'queued',
      attachments: drafts.map((draft, index) => ({
        draftId: draft?.draftId || `${clientMsgId}-attachment-${index}`,
        name: toStringOrNull(draft?.name ?? draft?.file?.name ?? null) || `Attachment ${index + 1}`,
        mimeType: toStringOrNull(draft?.mimeType ?? draft?.file?.type ?? null) || '',
        kind: toStringOrNull(draft?.kind ?? null) || 'file',
        size: Math.max(0, Number(draft?.size ?? draft?.file?.size) || 0),
        caption: toStringOrNull(draft?.caption ?? null) || '',
        previewUrl: toStringOrNull(draft?.previewUrl ?? null),
        voiceNote: draft?.voiceNote === true,
        durationMs: Math.max(0, Number(draft?.durationMs) || 0),
        waveform: Array.isArray(draft?.waveform) ? draft.waveform.slice(0, 128) : null,
        editDocument: draft?.editDocument || null,
        sourceFile: draft?.file,
        prepared: null,
        multipartSession: null,
        resultAttachment: null,
        state: 'queued',
        progress: 0,
      })),
    });
    if (queuedJob?.storageWarning) options?.onStorageWarning?.(queuedJob.storageWarning);
    projectAttachmentUploadJob(queuedJob);
    options?.onPreparationProgress?.({ completed: drafts.length, total: drafts.length });
    void requestMessengerV2AttachmentBackgroundSync();
    if (options?.background === true) {
      window.setTimeout(() => {
        void runAttachmentUploadJobRef.current?.(clientMsgId).catch(() => null);
      }, 0);
      return {
        clientMsgId,
        conversationId: sendContext.activeConversationId,
        queued: true,
        state: 'queued',
      };
    }
    return runAttachmentUploadJob(clientMsgId);
  };
  runAttachmentUploadJobRef.current = runAttachmentUploadJob;

  useEffect(() => {
    if (
      !hydrationReady ||
      !isAuthorized ||
      !currentUserId ||
      state.bootstrap.phase !== 'ready' ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    let disposed = false;
    const resumePendingUploads = async ({ includeFailed = true } = {}) => {
      if (disposed || document.visibilityState === 'hidden') return;
      const jobs = await listMessengerV2AttachmentUploadJobs();
      jobs
        .filter(
          (job) =>
            job?.cancelled !== true &&
            job?.paused !== true &&
            job?.state !== 'paused' &&
            (includeFailed || job?.state !== 'failed') &&
            (!job?.userId || String(job.userId) === String(currentUserId)),
        )
        .forEach((job) => {
          void (async () => {
            const clientMsgId = toStringOrNull(job?.clientMsgId);
            if (!clientMsgId) return;

            // The browser can reconnect while the final failed attempt is still
            // unwinding. Wait for that single-flight job to leave the runtime map
            // before starting the durable job again, otherwise the online event
            // only observes the already-failing promise and the retry is lost.
            const activeJob = attachmentUploadRuntimeRef.current.activeJobs.get(clientMsgId);
            if (activeJob) await activeJob.catch(() => null);
            if (disposed) return;

            const latestJob = await getMessengerV2AttachmentUploadJob(clientMsgId);
            if (
              !latestJob ||
              latestJob.cancelled === true ||
              latestJob.paused === true ||
              latestJob.state === 'paused' ||
              (!includeFailed && latestJob.state === 'failed')
            ) {
              return;
            }
            await runAttachmentUploadJobRef.current?.(clientMsgId);
          })().catch(() => null);
        });
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void requestMessengerV2AttachmentBackgroundSync({ processNow: true });
      } else {
        void resumePendingUploads({ includeFailed: true });
      }
    };
    const handleWorkerMessage = (event) => {
      if (
        event?.data?.type === 'MESSENGER_V2_UPLOAD_QUEUE_UPDATED' &&
        event?.data?.state === 'uploaded'
      ) {
        void resumePendingUploads({ includeFailed: false });
      }
    };

    const handleOnline = () => void resumePendingUploads({ includeFailed: true });
    const handlePageShow = () => void resumePendingUploads({ includeFailed: true });

    void resumePendingUploads({ includeFailed: true });
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);
    navigator.serviceWorker?.addEventListener?.('message', handleWorkerMessage);

    return () => {
      disposed = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
      navigator.serviceWorker?.removeEventListener?.('message', handleWorkerMessage);
    };
  }, [currentUserId, hydrationReady, isAuthorized, state.bootstrap.phase]);

  const editThreadMessage = async (item, plaintext, options = {}) => {
    if (isMessengerV2CallMessage(item)) throw new Error('Call history cannot be edited');
    const normalizedPlaintext = typeof plaintext === 'string' ? plaintext.trim() : '';
    const linkPreview = options?.linkPreview || null;
    const envelope = options?.envelope || null;
    if (!normalizedPlaintext) return null;
    if (!state.activeConversationId || !activeThreadGroupId || !currentUserId || !item?.mine) {
      throw new Error('Active V2 thread is not ready for edit');
    }

    const target = {
      id: item?.id ?? null,
      clientMsgId: item?.clientMsgId ?? item?.client_msg_id ?? null,
      serverMsgId: item?.serverMsgId ?? item?.server_msg_id ?? null,
      serverSeq: item?.serverSeq ?? item?.server_seq ?? null,
    };

    dispatch({ type: 'composer:sendStart' });
    try {
      const preparedSend = await ensureMessengerV2SendReady({
        conversationId: state.activeConversationId,
        conversation: activeControllerConversation,
        groupId: activeThreadGroupId,
        currentUserId,
        currentUserName,
        clientId: state.bootstrap.clientId,
      });

      const response = await editMessengerV2Message({
        conversationId: state.activeConversationId,
        conversation: activeControllerConversation,
        groupId: activeThreadGroupId,
        currentUserId,
        currentUserName,
        clientId: state.bootstrap.clientId,
        target,
        plaintext: normalizedPlaintext,
        clientMsgId: randomUuid(),
        preparedSend,
        linkPreview,
        envelope:
          envelope && envelope.kind === 'edit'
            ? envelope
            : createMessengerV2EditEnvelope({
                target,
                text: normalizedPlaintext,
                preview: linkPreview,
              }),
      });

      dispatch({ type: 'composer:sendSuccess' });
      dispatch({
        type: 'timeline:applyEdit',
        conversationId: state.activeConversationId,
        target,
        body: normalizedPlaintext,
        linkPreview,
        editedAt: response?.createdAt ?? new Date().toISOString(),
      });

      return response;
    } catch (error) {
      dispatch({ type: 'composer:sendError', error });
      throw error;
    }
  };

  const deleteThreadMessage = async (item, { mode = 'everyone' } = {}) => {
    if (!state.activeConversationId || !currentUserId) {
      throw new Error('Active V2 thread is not ready for delete');
    }
    const target = {
      id: item?.id ?? null,
      clientMsgId: item?.clientMsgId ?? item?.client_msg_id ?? null,
      serverMsgId: item?.serverMsgId ?? item?.server_msg_id ?? null,
      serverSeq: item?.serverSeq ?? item?.server_seq ?? null,
    };

    if (mode === 'me') {
      dispatch({
        type: 'timeline:deleteForMe',
        conversationId: state.activeConversationId,
        target,
      });
      return { localOnly: true };
    }

    if (!activeThreadGroupId || !item?.mine) {
      throw new Error('Active V2 thread is not ready for delete');
    }

    dispatch({ type: 'composer:sendStart' });
    try {
      const preparedSend = await ensureMessengerV2SendReady({
        conversationId: state.activeConversationId,
        conversation: activeControllerConversation,
        groupId: activeThreadGroupId,
        currentUserId,
        currentUserName,
        clientId: state.bootstrap.clientId,
      });

      const response = await deleteMessengerV2Message({
        conversationId: state.activeConversationId,
        conversation: activeControllerConversation,
        groupId: activeThreadGroupId,
        currentUserId,
        currentUserName,
        clientId: state.bootstrap.clientId,
        target,
        clientMsgId: randomUuid(),
        preparedSend,
      });

      dispatch({ type: 'composer:sendSuccess' });
      dispatch({
        type: 'timeline:applyDelete',
        conversationId: state.activeConversationId,
        target,
      });

      return response;
    } catch (error) {
      dispatch({ type: 'composer:sendError', error });
      throw error;
    }
  };

  const sendReactionOutboxEntry = async (entry = null) => {
    const operationId = toStringOrNull(entry?.clientMsgId ?? null);
    const conversationId = toStringOrNull(entry?.conversationId ?? null);
    const target = entry?.target && typeof entry.target === 'object' ? entry.target : null;
    const reaction = entry?.reaction && typeof entry.reaction === 'object' ? entry.reaction : null;
    if (!operationId || !conversationId || !target || !reaction) return null;

    const sendSnapshot = stateRef.current || state;
    const conversation = findCanonicalMessengerV2Conversation(sendSnapshot, conversationId);
    const groupId = toStringOrNull(
      resolveMessengerV2ConversationGroupId(conversation) ?? entry?.groupId ?? null,
    );
    const reactionAction = { conversationId, target, operationId };
    const sendingEntry = {
      ...entry,
      kind: 'reaction',
      clientMsgId: operationId,
      conversationId,
      groupId,
      phase: 'sending',
      retryAt: null,
      waitForOnline: false,
    };
    dispatch({ type: 'outbox:enqueue', entry: sendingEntry });
    dispatch({
      type: 'timeline:markReactionState',
      ...reactionAction,
      state: 'sending',
      retryable: true,
    });

    try {
      if (!conversation || !groupId || !currentUserId) {
        throw createMessengerV2SendNotReadyError('Encrypted reaction metadata is synchronizing');
      }
      const preparedSend = await ensureMessengerV2SendReady({
        conversationId,
        conversation,
        groupId,
        currentUserId,
        currentUserName,
        clientId: sendSnapshot.bootstrap?.clientId ?? null,
      });
      const response = await sendMessengerV2Message({
        conversationId,
        conversation,
        groupId,
        currentUserId,
        currentUserName,
        clientId: sendSnapshot.bootstrap?.clientId ?? null,
        plaintext: '',
        clientMsgId: operationId,
        preparedSend,
        envelope: entry.envelope || createMessengerV2ReactionEnvelope({ target, reaction }),
      });
      dispatch({ type: 'timeline:settleReaction', ...reactionAction });
      dispatch({ type: 'outbox:remove', clientMsgId: operationId });
      return response;
    } catch (error) {
      const failure = resolveMessengerV2OutboxFailure({
        error,
        attempts: Math.max(0, Number(entry?.attempts) || 0),
        online: typeof navigator === 'undefined' || navigator.onLine !== false,
      });
      if (failure.automatic) {
        dispatch({
          type: 'outbox:defer',
          clientMsgId: operationId,
          error,
          retryAt: failure.retryAt,
          waitForOnline: failure.waitForOnline,
        });
        dispatch({
          type: 'timeline:markReactionState',
          ...reactionAction,
          state: 'queued',
          retryable: true,
          error,
        });
        return {
          clientMsgId: operationId,
          conversationId,
          queued: true,
          state: 'queued',
        };
      }
      if (failure.retryable) {
        dispatch({ type: 'outbox:fail', clientMsgId: operationId, error, retryable: true });
        dispatch({
          type: 'timeline:markReactionState',
          ...reactionAction,
          state: 'failed',
          retryable: true,
          error,
        });
      } else {
        dispatch({ type: 'outbox:remove', clientMsgId: operationId });
        dispatch({ type: 'timeline:settleReaction', ...reactionAction, rollback: true });
      }
      throw error;
    }
  };
  sendReactionOutboxEntryRef.current = sendReactionOutboxEntry;

  const retryReactionMessage = async (itemOrOperationId = null) => {
    const operationId = toStringOrNull(
      typeof itemOrOperationId === 'object'
        ? (itemOrOperationId?.pendingReactionOperations || []).find(
            (operation) => operation?.state === 'failed' && operation?.retryable !== false,
          )?.id ??
            itemOrOperationId?.clientMsgId ??
            itemOrOperationId?.client_msg_id
        : itemOrOperationId,
    );
    if (!operationId) return null;
    const entry = stateRef.current.outboxByClientMsgId?.[operationId] || null;
    if (!entry || entry.kind !== 'reaction' || entry.retryable === false) return null;
    const existingFlight = outboundRetryFlightsRef.current.get(operationId);
    if (existingFlight) return existingFlight;
    const retryPromise = sendReactionOutboxEntryRef.current?.(entry)?.finally(() => {
      if (outboundRetryFlightsRef.current.get(operationId) === retryPromise) {
        outboundRetryFlightsRef.current.delete(operationId);
      }
    });
    if (!retryPromise) return null;
    outboundRetryFlightsRef.current.set(operationId, retryPromise);
    return retryPromise;
  };

  retryOutboxEntryRef.current = (entry = null) =>
    entry?.kind === 'reaction'
      ? retryReactionMessage(entry.clientMsgId)
      : retryThreadMessage(entry?.clientMsgId);

  const reactToThreadMessage = async (item, emoji) => {
    const normalizedEmoji = toStringOrNull(emoji);
    const sendSnapshot = stateRef.current || state;
    const sendContext = resolveMessengerV2SendContext(sendSnapshot);
    if (
      !normalizedEmoji ||
      !sendContext.activeConversationId ||
      sendContext.threadConversationId !== sendContext.activeConversationId ||
      !sendContext.groupId ||
      !currentUserId ||
      !item
    ) {
      throw createMessengerV2SendNotReadyError();
    }

    const target = {
      id: item?.id ?? null,
      clientMsgId: item?.clientMsgId ?? item?.client_msg_id ?? null,
      serverMsgId: item?.serverMsgId ?? item?.server_msg_id ?? null,
      serverSeq: item?.serverSeq ?? item?.server_seq ?? null,
    };
    const matchingReaction = (Array.isArray(item?.reactions) ? item.reactions : []).find(
      (candidate) =>
        toStringOrNull(candidate?.emoji ?? candidate?.type ?? null) === normalizedEmoji,
    );
    const reaction = { emoji: normalizedEmoji, action: matchingReaction?.mine ? 'remove' : 'add' };
    const latestVisibleMessage = sendSnapshot.timeline?.items?.at(-1);
    const reactionFallbackText = String(latestVisibleMessage?.body || latestVisibleMessage?.rawText || '')
      .replace(/\s+/g, ' ').slice(0, 160);
    const operationId = randomUuid();
    const outboxEntry = {
      kind: 'reaction',
      clientMsgId: operationId,
      conversationId: sendContext.activeConversationId,
      groupId: sendContext.groupId,
      target,
      reaction,
      envelope: createMessengerV2ReactionEnvelope({ target, reaction, fallbackText: reactionFallbackText, quote: {
        messageId: item.id,
        body: resolveMessengerV2EnvelopeDisplayText({
          kind: item.messageKind || 'text', text: item.body || item.rawText,
          caption: item.caption, attachments: item.attachments, callEvent: item.callEvent,
        }).replace(/\s+/g, ' ').slice(0, 160),
      } }),
      plaintext: '',
      createdAt: new Date().toISOString(),
      attempts: 0,
      phase: 'sending',
      retryAt: null,
    };
    dispatch({ type: 'outbox:enqueue', entry: outboxEntry });
    dispatch({
      type: 'timeline:applyReaction',
      conversationId: sendContext.activeConversationId,
      target,
      operationId,
      reaction,
      authorId: currentUserId,
      currentUserId,
    });

    return sendReactionOutboxEntry(outboxEntry);
  };

  const createVoiceCallSession = async ({
    callId,
    conversationId,
    direction,
    mode = 'voice',
    bufferIceUntilInvite = false,
    mediaEncryption = null,
  } = {}) => {
    const config = await loadMessengerV2VoiceCallConfig({ conversationId });
    let shouldBufferIce = bufferIceUntilInvite === true;
    const bufferedIceSignals = [];
    const session = new MessengerV2VoiceCallSession({
      callId,
      conversationId,
      direction,
      mode,
      iceServers: config.iceServers,
      mediaEncryption: {
        required: true,
        role: direction === 'incoming' ? 'responder' : 'initiator',
        ...(mediaEncryption && typeof mediaEncryption === 'object' ? mediaEncryption : {}),
      },
      signal: (signalType, payload) => {
        if (shouldBufferIce && signalType === 'ice') {
          bufferedIceSignals.push(payload);
          return;
        }
        const activeCall = voiceCallRuntimeRef.current.state || {};
        sendVoiceCallSignalQuietly(signalType, payload, {
          callId,
          conversationId,
          peerUserId: activeCall.callId === callId ? activeCall.peerUserId : null,
          peerClientId: activeCall.callId === callId ? activeCall.peerClientId : null,
        });
      },
      onLocalStream: (localStream) => {
        setVoiceCall((current) =>
          current.callId === callId
            ? {
                ...current,
                localStream,
              }
            : current,
        );
      },
      onRemoteStream: (remoteStream) => {
        setVoiceCall((current) =>
          current.callId === callId
            ? {
                ...current,
                remoteStream,
              }
            : current,
        );
      },
      onConnectionState: (connectionState) => {
        const normalizedState = String(connectionState || '').toLowerCase();
        traceMessengerV2Call('peer_connection_state', {
          callId,
          conversationId,
          direction,
          state: normalizedState,
          currentStatus: voiceCallRuntimeRef.current.state?.status || 'idle',
        });
        if (['connected', 'completed'].includes(normalizedState)) {
          clearVoiceCallSetupTimer();
          stopVoiceCallProgressTone();
          setVoiceCall((current) =>
            current.callId === callId
              ? {
                  ...current,
                  status: 'active',
                  connectedAt: current.connectedAt || new Date().toISOString(),
                }
              : current,
          );
        }
        if (normalizedState === 'failed') {
          const call = voiceCallRuntimeRef.current.state || {};
          if (call.callId === callId && call.status && call.status !== 'idle') {
            finishVoiceCall({
              status: 'failed',
              sendSignal: true,
              writeHistory: true,
              error: new Error('Voice call connection failed'),
            });
          }
          return;
        }
        if (normalizedState === 'disconnected') {
          setVoiceCall((current) =>
            current.callId === callId && current.status === 'active'
              ? {
                  ...current,
                  status: 'reconnecting',
                }
              : current,
          );
          const call = voiceCallRuntimeRef.current.state || {};
          if (call.callId === callId && ['active', 'reconnecting'].includes(call.status)) {
            armVoiceCallSetupTimer({ callId, reason: 'reconnect_timeout' });
          }
        }
      },
    });
    session.flushBufferedIceSignals = () => {
      shouldBufferIce = false;
      const pending = bufferedIceSignals.splice(0);
      pending.forEach((payload) => {
        const activeCall = voiceCallRuntimeRef.current.state || {};
        sendVoiceCallSignalQuietly('ice', payload, {
          callId,
          conversationId,
          peerUserId: activeCall.callId === callId ? activeCall.peerUserId : null,
          peerClientId: activeCall.callId === callId ? activeCall.peerClientId : null,
        });
      });
    };
    voiceCallRuntimeRef.current.session = session;
    flushRemoteVoiceCallIceCandidates(callId, session);
    return session;
  };

  const createSfuCallSession = async ({
    callId,
    conversationId,
    livekitUrl,
    token,
    mode = 'voice',
    mediaKey = null,
  } = {}) => {
    const resolveSfuConversation = () => {
      const snapshot = stateRef.current || {};
      return findCanonicalMessengerV2Conversation(snapshot, conversationId);
    };
    const refreshSfuParticipants = (room = null, extra = {}) => {
      if (!room) return;
      const participants = buildSfuParticipantSnapshots({
        room,
        conversation: resolveSfuConversation(),
        currentUserId,
        currentUserName,
      });
      const localStream =
        typeof session?.collectLocalMediaStream === 'function'
          ? session.collectLocalMediaStream()
          : null;
      setVoiceCall((current) =>
        current.callId === callId
          ? {
              ...current,
              participants,
              localStream: localStream || current.localStream,
              ...extra,
            }
          : current,
      );
    };
    const scheduleSfuParticipantsRefresh = (room = null, extra = {}) => {
      if (!room) return;
      refreshSfuParticipants(room, extra);
      if (typeof window === 'undefined') return;
      [250, 900].forEach((delayMs) => {
        window.setTimeout(() => {
          refreshSfuParticipants(room);
        }, delayMs);
      });
    };
    const scheduleSfuAutoLeaveWhenAlone = (room = null) => {
      if (!room || typeof window === 'undefined') return;
      if (resolveSfuConversation()?.kind === 'group') return;
      if ((room.remoteParticipants?.size ?? 0) > 0) return;
      window.setTimeout(() => {
        const activeCall = voiceCallRuntimeRef.current.state || {};
        if (
          activeCall.callId !== callId ||
          activeCall.provider !== LIVEKIT_CALL_PROVIDER ||
          !['active', 'reconnecting'].includes(String(activeCall.status || '').toLowerCase())
        ) {
          return;
        }
        if ((room.remoteParticipants?.size ?? 0) > 0) return;
        finishVoiceCall({
          status: 'ended',
          sendSignal: false,
          endForEveryone: false,
          writeHistory: true,
        });
      }, 1200);
    };
    const resolveSfuConnectedState = (current, room = null) => {
      const conversation = resolveSfuConversation();
      const hasRemoteParticipant = (room?.remoteParticipants?.size ?? 0) > 0;
      const isStillWaitingForParticipant = !hasRemoteParticipant && !current.rejoining && !current.connectedAt;

      if (isStillWaitingForParticipant) {
        return {
          status: ['ringing', 'outgoing', 'connecting'].includes(current.status)
            ? current.status
            : conversation?.kind === 'direct' && current.direction === 'outgoing'
              ? 'outgoing'
              : 'connecting',
          connectedAt: null,
        };
      }

      return {
        status: 'active',
        connectedAt: current.connectedAt || new Date().toISOString(),
      };
    };
    const promoteSfuCallWhenParticipantJoins = (room = null) => {
      if ((room?.remoteParticipants?.size ?? 0) < 1) return;
      clearVoiceCallRingTimer();
      clearVoiceCallSetupTimer();
      stopVoiceCallProgressTone();
      refreshSfuParticipants(room, {
        status: 'active',
        connectedAt: voiceCallRuntimeRef.current.state?.connectedAt || new Date().toISOString(),
        error: null,
      });
      publishSfuPresence();
    };
    const publishSfuPresence = () => {
      const call = voiceCallRuntimeRef.current.state || {};
      if (call.callId !== callId || call.status !== 'active') return;
      const clientId = resolveMessengerV2CurrentClientId(stateRef.current || {}, conversationId);
      if (!clientId) return;
      void transitionMessengerV2SfuCall({
        conversationId, callId, action: 'heartbeat', clientId,
        payload: call.connectionId ? { connection_id: call.connectionId } : {},
      }).catch(error => traceMessengerV2Call('call_heartbeat_failed', { callId, error: String(error?.message || error) }));
    };
    const session = new MessengerV2SfuCallSession({
      callId,
      conversationId,
      livekitUrl,
      token,
      mode,
      mediaKey,
      onScreenShareStateChanged: ({ status, error }) => {
        if (voiceCallRuntimeRef.current.session !== session) return;
        setVoiceCall((current) => current.callId === callId
          ? { ...current, screenShareStatus: status, screenShareError: error }
          : current);
      },
      onConnected: ({ room }) => {
        clearVoiceCallSetupTimer();
        stopVoiceCallProgressTone();
        const localStream =
          typeof session.collectLocalMediaStream === 'function'
            ? session.collectLocalMediaStream()
            : null;
        setVoiceCall((current) =>
          current.callId === callId
            ? {
                ...current,
                ...resolveSfuConnectedState(current, room),
                localStream: localStream || current.localStream,
                participants: buildSfuParticipantSnapshots({
                  room,
                  conversation: resolveSfuConversation(),
                  currentUserId,
                  currentUserName,
                }),
                error: null,
              }
            : current,
        );
        traceMessengerV2Call('sfu_connected', {
          callId,
          conversationId,
          roomName: room?.name ?? null,
        });
        scheduleSfuParticipantsRefresh(room);
      },
      onDisconnected: ({ reason }) => {
        traceMessengerV2Call('sfu_disconnected', {
          callId,
          conversationId,
          reason: String(reason || ''),
        });
        if (voiceCallRuntimeRef.current.session !== session) return;
        const activeCall = voiceCallRuntimeRef.current.state || {};
        if (activeCall.callId === callId && activeCall.status && activeCall.status !== 'idle') {
          finishVoiceCall({
            status: 'failed',
            sendSignal: false,
            endForEveryone: false,
            writeHistory: true,
            error: new Error('Call media connection was lost'),
            connectionLost: true,
          });
        }
      },
      onParticipantConnected: ({ room }) => {
        promoteSfuCallWhenParticipantJoins(room);
        scheduleSfuParticipantsRefresh(room);
      },
      onParticipantDisconnected: ({ room }) => {
        scheduleSfuParticipantsRefresh(room);
        scheduleSfuAutoLeaveWhenAlone(room);
      },
      onParticipantUpdated: ({ room }) => {
        scheduleSfuParticipantsRefresh(room);
      },
      onActiveSpeakersChanged: ({ room }) => {
        refreshSfuParticipants(room);
      },
      onConnectionStateChanged: ({ state: connectionState }) => {
        const normalizedState = String(connectionState || '').toLowerCase();
        if (normalizedState === 'reconnecting') {
          setVoiceCall((current) =>
            current.callId === callId && current.status === 'active'
              ? { ...current, status: 'reconnecting' }
              : current,
          );
          armVoiceCallSetupTimer({ callId, reason: 'sfu_reconnect_timeout' });
        } else if (normalizedState === 'connected') {
          clearVoiceCallSetupTimer();
          setVoiceCall((current) =>
            current.callId === callId && current.status === 'reconnecting'
              ? {
                  ...current,
                  ...resolveSfuConnectedState(current, session?.room),
                  error: null,
                }
              : current,
          );
        }
      },
      onTrackSubscribed: ({ room, track }) => {
        const mediaTrack = getMediaStreamTrackFromLiveKitTrack(track);
        setVoiceCall((current) =>
          current.callId === callId
            ? {
                ...current,
                remoteStream: addTrackToMediaStream(current.remoteStream, mediaTrack),
                participants: buildSfuParticipantSnapshots({
                  room,
                  conversation: resolveSfuConversation(),
                  currentUserId,
                  currentUserName,
                }),
              }
            : current,
        );
        scheduleSfuParticipantsRefresh(room);
      },
      onTrackUnsubscribed: ({ room, track }) => {
        const mediaTrack = getMediaStreamTrackFromLiveKitTrack(track);
        setVoiceCall((current) =>
          current.callId === callId
            ? {
                ...current,
                remoteStream: removeTrackFromMediaStream(current.remoteStream, mediaTrack),
                participants: buildSfuParticipantSnapshots({
                  room,
                  conversation: resolveSfuConversation(),
                  currentUserId,
                  currentUserName,
                }),
              }
            : current,
        );
        scheduleSfuParticipantsRefresh(room);
      },
      onLocalTrackPublished: ({ room }) => {
        const localStream =
          typeof session.collectLocalMediaStream === 'function'
            ? session.collectLocalMediaStream()
            : null;
        if (!localStream) {
          scheduleSfuParticipantsRefresh(room);
          return;
        }
        setVoiceCall((current) =>
          current.callId === callId
            ? {
                ...current,
                localStream,
                participants: buildSfuParticipantSnapshots({
                  room,
                  conversation: resolveSfuConversation(),
                  currentUserId,
                  currentUserName,
                }),
              }
            : current,
        );
        scheduleSfuParticipantsRefresh(room);
      },
    });
    voiceCallRuntimeRef.current.session = session;
    await withMessengerV2Timeout(
      session.connect(),
      VOICE_CALL_SETUP_TIMEOUT_MS,
      mode === 'video'
        ? 'Video call setup timed out'
        : 'Voice call setup timed out',
    );
    const mediaSnapshot =
      typeof session.recoverMedia === 'function'
        ? await session.recoverMedia({ microphoneEnabled: true }).catch(() => null)
        : null;
    if (mediaSnapshot) {
      applyVoiceCallMediaSnapshot(callId, mediaSnapshot);
    }
    publishSfuPresence();
    return session;
  };

  const runSfuCallStart = async ({ mode = 'voice', returnPath = null } = {}) => {
    await voiceCallRuntimeRef.current.cleanupPromise?.catch(() => null);
    const callMode = mode === 'video' ? 'video' : 'voice';
    const activeConversationId = toStringOrNull(stateRef.current.activeConversationId);
    const conversation = findCanonicalMessengerV2Conversation(
      stateRef.current,
      activeConversationId,
    );
    const conversationId = toStringOrNull(conversation?.id ?? activeConversationId);
    const isGroupConversation = isConversationGroupRecord(conversation);
    const isDirectConversation = conversation?.kind === 'direct';
    if (!conversationId || (!isGroupConversation && !isDirectConversation)) {
      throw new Error('Calls are available in direct, group, or Room conversations only');
    }
    if (conversation?.e2ee_protocol && conversation.e2ee_protocol !== 'mls') {
      throw new Error('Calls require a messages-v2 encrypted conversation');
    }
    const currentCall = voiceCallRuntimeRef.current.state || {};
    if (currentCall.status && currentCall.status !== 'idle') {
      throw new Error('A call is already in progress');
    }
    const remoteParticipant = isDirectConversation
      ? resolveRemoteConversationParticipant(conversation, currentUserId)
      : null;
    const peerUserId = isDirectConversation
      ? toStringOrNull(remoteParticipant?.id ?? remoteParticipant?.user_id ?? null)
      : null;
    if (isDirectConversation && !peerUserId) {
      throw new Error('Call recipient is unavailable');
    }

    const startedAt = new Date().toISOString();
    const startupAttemptId = randomUuid();
    const mediaSecretB64 = createLiveKitMediaSecretB64();
    const mediaE2ee = createLiveKitMediaE2eeOffer(mediaSecretB64);
    const mlsGroupId = toStringOrNull(
      conversation?.mls_group_id ??
        conversation?.mlsGroupId ??
        conversation?.meta?.mls_group_id ??
        activeThreadGroupId ??
        null,
    );
    if (!mlsGroupId) {
      throw new Error('Encrypted conversation setup is still in progress');
    }
    const peerName = isGroupConversation
      ? toStringOrNull(conversation?.title ?? conversation?.name ?? null) || 'Group call'
      : resolveParticipantDisplayName(remoteParticipant, 'Contact');
    const fallbackCallInitiator = {
      id: currentUserId,
      userId: currentUserId,
      name: currentUserName,
      avatarUrl: toStringOrNull(
        currentUser?.avatar_url ??
          currentUser?.avatarUrl ??
          currentUser?.avatar ??
          currentUser?.display_picture ??
          currentUser?.displayPicture ??
          null,
      ),
      initials: toStringOrNull(currentUserName)?.slice(0, 2).toUpperCase() || null,
    };
    setVoiceCall({
      status: 'connecting',
      callId: null,
      conversationId,
      provider: LIVEKIT_CALL_PROVIDER,
      livekitUrl: null,
      mode: callMode,
      presentation: 'foreground',
      returnPath: toStringOrNull(returnPath),
      isGroupCall: isGroupConversation,
      direction: 'outgoing',
      peerUserId,
      peerName,
      targetDeviceCount: 0,
      declinedClientIds: [],
      startedByUserId: fallbackCallInitiator.id || fallbackCallInitiator.userId || currentUserId,
      startedByName: fallbackCallInitiator.name || currentUserName,
      startedByAvatarUrl: fallbackCallInitiator.avatarUrl || null,
      startedByInitials:
        fallbackCallInitiator.initials ||
        toStringOrNull(currentUserName)?.slice(0, 2).toUpperCase() ||
        null,
      muted: false,
      cameraEnabled: callMode === 'video',
      soundMuted: false,
      soundMutePending: false,
      remoteMuted: false,
      remoteCameraEnabled: callMode === 'video',
      videoBackgroundEffect: 'none',
      videoBackgroundEffectPending: false,
      videoBackgroundEffectError: null,
      localStream: null,
      remoteStream: null,
      participants: [],
      startedAt,
      connectedAt: null,
      endedAt: null,
      error: null,
      mediaE2ee,
      startupAttemptId,
      connectionId: startupAttemptId,
      rejoining: false,
    });
    startVoiceCallProgressTone('calling');

    let created = null;
    const assertStartupIsCurrent = () => {
      const activeCall = voiceCallRuntimeRef.current.state || {};
      if (
        activeCall.status === 'idle' ||
        toStringOrNull(activeCall.startupAttemptId) !== startupAttemptId ||
        (created?.callId &&
          activeCall.callId &&
          toStringOrNull(activeCall.callId) !== toStringOrNull(created.callId))
      ) {
        const error = new Error('Call startup was cancelled');
        error.code = 'CALL_START_CANCELLED';
        throw error;
      }
      return activeCall;
    };

    try {
      const readinessStartedAt = Date.now();
      const [preparedSend, directCallTarget] = await withMessengerV2Timeout(
        Promise.resolve()
          .then(() => ensureFreshAuthToken({ minTtlSeconds: 180 }).catch(() => null))
          .then(() =>
            Promise.all([
              ensureMessengerV2SendReady({
                conversationId,
                conversation,
                groupId: mlsGroupId,
                currentUserId,
                currentUserName,
                clientId: stateRef.current.bootstrap?.clientId ?? null,
              }),
              isDirectConversation
                ? fetchMessengerV2CallTargetClient({
                    groupId: mlsGroupId,
                    targetUserId: peerUserId,
                    conversationId,
                  })
                : Promise.resolve(null),
            ]),
          ),
        VOICE_CALL_START_READINESS_TIMEOUT_MS,
        'Encrypted call setup timed out',
      );
      traceMessengerV2Call('startup_readiness_done', {
        conversationId,
        startupAttemptId,
        durationMs: Date.now() - readinessStartedAt,
      });
      assertStartupIsCurrent();

      if (isDirectConversation && directCallTarget?.available === false) {
        throw createCallRecipientDeviceUnavailableError();
      }
      const callClientId =
        toStringOrNull(preparedSend?.senderClientId ?? preparedSend?.identityId ?? null) ||
        resolveMessengerV2CurrentClientId(stateRef.current || {}, conversationId) ||
        (await refreshMessengerV2CallClientIdentity({
          force: false,
          conversationId,
        }));
      if (!callClientId) {
        throw new Error('Call device identity is not ready');
      }
      const targetDeviceCount = isDirectConversation
        ? Math.max(
            1,
            Array.isArray(directCallTarget?.devices)
              ? directCallTarget.devices.length
              : directCallTarget?.client_id
                ? 1
                : 0,
          )
        : 0;
      created = normalizeSfuCallPayload(
        await createMessengerV2SfuCall({
          conversationId,
          mode: callMode,
          clientId: callClientId,
          connectionId: startupAttemptId,
          idempotencyKey: `call-attempt:${startupAttemptId}`,
        }),
      );
      if (!created.callId || !created.livekitUrl || !created.token) {
        throw new Error('LiveKit call setup did not return a room token');
      }
      try {
        assertStartupIsCurrent();
      } catch (error) {
        await transitionMessengerV2SfuCall({
          conversationId,
          callId: created.callId,
          action: 'cancel',
        }).catch(() => null);
        throw error;
      }

      const callInitiator = created.initiator || fallbackCallInitiator;
      setVoiceCall((current) =>
        toStringOrNull(current.startupAttemptId) === startupAttemptId
          ? {
              ...current,
              status: isDirectConversation ? 'outgoing' : 'connecting',
              callId: created.callId,
              lifecycleVersion: created.lifecycleVersion,
              livekitUrl: created.livekitUrl,
              targetDeviceCount,
              startedByUserId: callInitiator.id || callInitiator.userId || currentUserId,
              startedByName: callInitiator.name || currentUserName,
              startedByAvatarUrl: callInitiator.avatarUrl || null,
              startedByInitials:
                callInitiator.initials ||
                toStringOrNull(currentUserName)?.slice(0, 2).toUpperCase() ||
                null,
            }
          : current,
      );

      const mediaConnectionPromise = createSfuCallSession({
        callId: created.callId,
        conversationId,
        livekitUrl: created.livekitUrl,
        token: created.token,
        mode: callMode,
        mediaKey: mediaSecretB64,
      });
      void mediaConnectionPromise.catch((error) => {
        const activeCall = voiceCallRuntimeRef.current.state || {};
        if (
          toStringOrNull(activeCall.callId) === toStringOrNull(created.callId) &&
          activeCall.status !== 'idle'
        ) {
          finishVoiceCall({
            status: 'failed',
            sendSignal: false,
            writeHistory: false,
            error,
          });
        }
      });
      const mediaFailurePromise = mediaConnectionPromise.then(
        () => new Promise(() => {}),
        (error) => Promise.reject(error),
      );

      const inviteAndRingPromise = sendVoiceCallSignal(
        'sfu-invite',
        {
          provider: LIVEKIT_CALL_PROVIDER,
          callMode,
          mode: callMode,
          conversationKind: conversation?.kind ?? null,
          groupCall: isGroupConversation,
          conversationTitle:
            toStringOrNull(conversation?.title ?? conversation?.name ?? null) ?? null,
          startedByUserId: callInitiator.id || callInitiator.userId || currentUserId,
          startedByName: callInitiator.name || currentUserName,
          startedByAvatarUrl: callInitiator.avatarUrl || null,
          startedByInitials:
            callInitiator.initials ||
            toStringOrNull(currentUserName)?.slice(0, 2).toUpperCase() ||
            null,
          groupId: isGroupConversation
            ? toStringOrNull(conversation?.group_id ?? conversation?.groupId ?? null)
            : null,
          mlsGroupId,
          mediaE2ee,
          livekit: {
            callId: created.callId,
          },
        },
        {
          callId: created.callId,
          conversationId,
          lifecycleVersion: created.lifecycleVersion,
          peerUserId,
          provider: LIVEKIT_CALL_PROVIDER,
        },
        {
          authReady: true,
          preparedSend,
          conversation,
          groupId: mlsGroupId,
          clientId: callClientId,
          callTarget: directCallTarget,
        },
      ).then(async (inviteRecord) => {
        assertStartupIsCurrent();
        const inviteServerMsgId = toStringOrNull(
          inviteRecord?.serverMsgId ??
            inviteRecord?.server_msg_id ??
            inviteRecord?.id ??
            inviteRecord?.message?.id ??
            null,
        );
        if (!inviteServerMsgId) {
          throw new Error('Encrypted call invite did not return a durable server reference');
        }
        const ringing = normalizeSfuCallPayload(
          await transitionMessengerV2SfuCall({
            conversationId,
            callId: created.callId,
            action: 'ring',
            payload: { invite_server_msg_id: inviteServerMsgId },
          }),
        );
        setVoiceCall((current) =>
          toStringOrNull(current.callId) === toStringOrNull(created.callId)
            ? {
                ...current,
                startupAttemptId: null,
                lifecycleVersion: ringing.lifecycleVersion ?? current.lifecycleVersion ?? null,
              }
            : current,
        );
        traceMessengerV2Call('startup_ringing', {
          callId: created.callId,
          conversationId,
          startupAttemptId,
          durationMs: Date.now() - new Date(startedAt).getTime(),
        });
        return inviteServerMsgId;
      });

      await Promise.race([
        withMessengerV2Timeout(
          inviteAndRingPromise,
          VOICE_CALL_SIGNAL_SEND_TIMEOUT_MS,
          'Encrypted call invite timed out',
        ),
        mediaFailurePromise,
      ]);
      clearVoiceCallRingTimer();
      voiceCallRuntimeRef.current.ringTimer = window.setTimeout(() => {
        const call = voiceCallRuntimeRef.current.state || {};
        const participantCount = Array.isArray(call.participants) ? call.participants.length : 0;
        if (
          call.callId === created.callId &&
          ['outgoing', 'ringing', 'connecting', 'active'].includes(String(call.status || '')) &&
          participantCount < 2
        ) {
          finishVoiceCall({ status: 'missed', sendSignal: true, writeHistory: true });
        }
      }, VOICE_CALL_RING_TIMEOUT_MS);
      return { callId: created.callId };
    } catch (error) {
      const activeCall = voiceCallRuntimeRef.current.state || {};
      if (
        toStringOrNull(activeCall.startupAttemptId) === startupAttemptId &&
        activeCall.status !== 'idle'
      ) {
        finishVoiceCall({ status: 'failed', sendSignal: false, writeHistory: false, error });
      }
      throw error;
    }
  };

  const startSfuCall = (options = {}) => {
    const activeStart = voiceCallRuntimeRef.current.startPromise;
    if (activeStart) return activeStart;

    const startPromise = runSfuCallStart(options).finally(() => {
      if (voiceCallRuntimeRef.current.startPromise === startPromise) {
        voiceCallRuntimeRef.current.startPromise = null;
      }
    });
    voiceCallRuntimeRef.current.startPromise = startPromise;
    return startPromise;
  };

  const runJoinSfuCall = async (call = null) => {
    const source = call || voiceCallRuntimeRef.current.state || {};
    const connectionId = randomUuid();
    const conversationId = toStringOrNull(source.conversationId);
    const callId = toStringOrNull(source.callId);
    if (!conversationId || !callId) return null;
    const mediaKey = resolveLiveKitMediaSecretB64(source.mediaE2ee);
    if (!mediaKey) {
      throw new Error('LiveKit media encryption key is missing');
    }
    const clientId =
      resolveMessengerV2CurrentClientId(stateRef.current || {}, conversationId) ||
      (await refreshMessengerV2CallClientIdentity({
        force: false,
        conversationId,
      }));
    if (!clientId) {
      throw new Error('Call device identity is not ready');
    }

    clearVoiceCallRingTimer();
    stopIncomingVoiceCallAlerts();
    setVoiceCall((current) => ({
      ...current,
      status: 'connecting',
      provider: LIVEKIT_CALL_PROVIDER,
      connectionId,
      mediaE2ee: source.mediaE2ee || current.mediaE2ee || null,
      answerRequested: false,
      error: null,
    }));
    armVoiceCallSetupTimer({ callId, reason: 'sfu_join_timeout' });

    try {
      const joinedPayload = await joinMessengerV2SfuCall({ conversationId, callId, clientId, connectionId });
      const joined = normalizeSfuCallPayload(joinedPayload);
      const current = voiceCallRuntimeRef.current.state || {};
      if (current.callId !== callId || current.connectionId !== connectionId || current.status === 'idle') {
        await transitionMessengerV2SfuCall({ conversationId, callId, action: 'leave', clientId, payload: { connection_id: connectionId } });
        return null;
      }
      if (!joined.livekitUrl || !joined.token) {
        throw new Error('LiveKit join did not return a room token');
      }
      callLifecycleCoordinatorRef.current?.resumeJoinedGroupCall(joinedPayload);
      setVoiceCall((current) =>
        current.callId === callId
          ? {
              ...current,
              sfuJoined: true,
              lifecycleVersion: joined.lifecycleVersion ?? current.lifecycleVersion ?? null,
            }
          : current,
      );
      await createSfuCallSession({
        callId,
        conversationId,
        livekitUrl: joined.livekitUrl,
        token: joined.token,
        mode: source.mode === 'video' ? 'video' : 'voice',
        mediaKey,
      });
      setVoiceCall((current) =>
        current.callId === callId
          ? {
              ...current,
              pendingOffer: null,
              lifecycleVersion: joined.lifecycleVersion ?? current.lifecycleVersion ?? null,
              mediaE2ee: source.mediaE2ee || current.mediaE2ee || null,
              cameraEnabled: current.mode === 'video' ? true : current.cameraEnabled,
              videoBackgroundEffect: 'none',
              videoBackgroundEffectPending: false,
              videoBackgroundEffectError: null,
            }
          : current,
      );
      return { callId };
    } catch (error) {
      if (
        error?.code === 'call_answered_elsewhere' ||
        error?.payload?.error?.code === 'call_answered_elsewhere'
      ) {
        finishVoiceCall({
          status: 'answered_elsewhere',
          notifySfuServer: false,
          sendSignal: false,
          writeHistory: false,
          playEndTone: false,
        });
        return { callId, answeredElsewhere: true };
      }
      finishVoiceCall({ status: 'failed', sendSignal: false, endForEveryone: source.isGroupCall ? false : null, error });
      throw error;
    }
  };

  const joinSfuCall = (call = null) => {
    const activeJoin = voiceCallRuntimeRef.current.joinPromise;
    if (activeJoin) return activeJoin;

    const joinPromise = runJoinSfuCall(call).finally(() => {
      if (voiceCallRuntimeRef.current.joinPromise === joinPromise) {
        voiceCallRuntimeRef.current.joinPromise = null;
      }
    });
    voiceCallRuntimeRef.current.joinPromise = joinPromise;
    return joinPromise;
  };
  joinSfuCallRef.current = joinSfuCall;

  const runJoinOngoingGroupCall = async ({ callId, conversationId, returnPath = null } = {}) => {
    const current = voiceCallRuntimeRef.current.state || {};
    if (current.callId === String(callId) && ['active', 'connecting', 'reconnecting'].includes(current.status)) {
      setVoiceCallPresentation({ presentation: 'foreground', returnPath });
      return { callId: current.callId };
    }
    if (current.status && current.status !== 'idle' && !(current.callId === String(callId) && ['incoming', 'securing'].includes(current.status))) throw new Error('A call is already in progress on this device');
    await voiceCallRuntimeRef.current.cleanupPromise;
    const payload = await loadMessengerV2SfuCall({ conversationId, callId });
    const authoritative = payload?.conversation_call;
    if (authoritative?.conversation_kind !== 'group' || !['ringing', 'active'].includes(authoritative?.status)) {
      await recoverActiveCallsRef.current?.({ force: true });
      throw new Error('This group call has ended');
    }
    if (isMessengerV2CallOwnedElsewhere(authoritative, currentUserId, current)) {
      return { callId: String(callId), answeredElsewhere: true };
    }
    await runActiveThreadSync({ conversationId });
    const invite = await loadMessengerV2SfuCallInvite({ conversationId, callId });
    const signal = await handleEncryptedVoiceCallSignalRecord({ payload: invite.message, recoverCall: authoritative });
    if (!signal) throw new Error('The encrypted call invite could not be recovered. Please retry when encrypted chat is ready.');
    const normalized = normalizeSfuCallPayload(payload);
    const conversation = findCanonicalMessengerV2Conversation(stateRef.current, conversationId);
    const nextCall = {
      ...createInitialMessengerV2CallState(), ...normalized,
      callId: String(callId), conversationId: String(conversationId),
      provider: LIVEKIT_CALL_PROVIDER, isGroupCall: true, status: 'connecting',
      rejoining: true,
      mode: authoritative.mode, presentation: 'foreground', returnPath,
      direction: String(authoritative.initiator_user_id) === String(currentUserId) ? 'outgoing' : 'incoming',
      peerName: conversation?.title || conversation?.name || '',
      mediaE2ee: signal.payload.mediaE2ee ?? signal.payload.media_e2ee,
      pendingOffer: { provider: LIVEKIT_CALL_PROVIDER }, secureSignalReady: true,
    };
    setVoiceCall(nextCall);
    try {
      return await joinSfuCall(nextCall);
    } finally {
      void recoverActiveCallsRef.current?.({ force: true, queueIfRunning: true });
    }
  };

  const joinOngoingGroupCall = (options) => {
    if (voiceCallRuntimeRef.current.rejoinPromise) return voiceCallRuntimeRef.current.rejoinPromise;
    const promise = runJoinOngoingGroupCall(options).finally(() => {
      if (voiceCallRuntimeRef.current.rejoinPromise === promise) voiceCallRuntimeRef.current.rejoinPromise = null;
    });
    voiceCallRuntimeRef.current.rejoinPromise = promise;
    return promise;
  };

  const startVoiceCall = async ({ mode = 'voice', returnPath = null } = {}) => {
    const callMode = mode === 'video' ? 'video' : 'voice';
    if (!isMessengerV2VoiceCallSupported()) {
      throw new Error('Calls are not supported by this browser');
    }
    const activeConversationId = toStringOrNull(stateRef.current.activeConversationId);
    const conversation = findCanonicalMessengerV2Conversation(
      stateRef.current,
      activeConversationId,
    );
    const conversationId = toStringOrNull(conversation?.id ?? activeConversationId);
    if (
      !conversationId ||
      (!isConversationGroupRecord(conversation) && conversation?.kind !== 'direct')
    ) {
      throw new Error('Calls are available in direct, group, or Room conversations only');
    }
    if (conversation?.e2ee_protocol && conversation.e2ee_protocol !== 'mls') {
      throw new Error('Calls require a messages-v2 encrypted conversation');
    }
    if (conversation?.kind === 'group') {
      const payload = await loadActiveMessengerV2SfuCalls();
      const existing = payload?.conversation_calls?.find(call => String(call.conversation_id) === conversationId && ['ringing', 'active'].includes(call.status));
      if (existing) return joinOngoingGroupCall({ callId: existing.id, conversationId, returnPath });
    }
    return startSfuCall({ mode: callMode, returnPath });
  };

  const startVideoCall = async (options = {}) => startVoiceCall({ ...options, mode: 'video' });

  const acceptVoiceCall = async ({ returnPath = undefined } = {}) => {
    const call = voiceCallRuntimeRef.current.state || {};
    if (call.status !== 'incoming' || !call.callId) {
      return null;
    }
    setVoiceCallPresentation({
      presentation: 'foreground',
      returnPath: returnPath === undefined ? call.returnPath : returnPath,
    });
    if (call.provider === LIVEKIT_CALL_PROVIDER) {
      if (!call.pendingOffer || !resolveLiveKitMediaSecretB64(call.mediaE2ee)) {
        stopIncomingVoiceCallAlerts();
        setVoiceCall((current) =>
          toStringOrNull(current.callId) === toStringOrNull(call.callId)
            ? {
                ...current,
                status: 'securing',
                answerRequested: true,
                error: null,
              }
            : current,
        );
        traceMessengerV2Call('secure_answer_waiting_for_invite', {
          callId: call.callId,
          conversationId: call.conversationId,
        });
        return { callId: call.callId, waitingForSecureSignal: true };
      }
      return joinSfuCall(call);
    }
    if (!isMessengerV2VoiceCallPqE2eeSupported()) {
      throw new Error('Voice call PQ media encryption is not supported by this browser');
    }
    clearVoiceCallRingTimer();
    stopIncomingVoiceCallAlerts();
    setVoiceCall((current) => ({
      ...current,
      status: 'connecting',
      error: null,
    }));
    armVoiceCallSetupTimer({ callId: call.callId, reason: 'accept_connect_timeout' });
    try {
      const session = await createVoiceCallSession({
        callId: call.callId,
        conversationId: call.conversationId,
        direction: 'incoming',
        mode: call.mode === 'video' ? 'video' : 'voice',
        mediaEncryption: {
          required: true,
          secretB64: call.mediaE2ee?.secretB64 ?? call.mediaE2ee?.secret_b64 ?? null,
        },
      });
      const answer = await withMessengerV2Timeout(
        session.acceptOffer(call.pendingOffer),
        VOICE_CALL_SETUP_TIMEOUT_MS,
        call.mode === 'video'
          ? 'Camera or microphone permission timed out'
          : 'Microphone permission timed out',
      );
      traceMessengerV2Call('offer_accepted', { callId: call.callId });
      await sendVoiceCallSignal(
        'accept',
        { answer, mediaE2ee: session.getMediaEncryptionAnswer() },
        call,
      );
      setVoiceCall((current) => ({
        ...current,
        pendingOffer: null,
        cameraEnabled: current.mode === 'video' ? true : current.cameraEnabled,
        videoBackgroundEffect: 'none',
        videoBackgroundEffectPending: false,
        videoBackgroundEffectError: null,
      }));
      sendVoiceCallSignalQuietly(
        'media-state',
        {
          muted: false,
          cameraEnabled: call.mode === 'video',
        },
        call,
      );
      return { callId: call.callId };
    } catch (error) {
      traceMessengerV2Call('accept_failed', {
        callId: call.callId,
        error: String(error?.message || error),
      });
      try {
        await sendVoiceCallSignal('reject', { reason: 'failed' }, call);
      } catch {}
      finishVoiceCall({ status: 'failed', error });
      throw error;
    }
  };

  const rejectVoiceCall = () => {
    const call = voiceCallRuntimeRef.current.state || {};
    if (!['incoming', 'securing'].includes(call.status)) return;
    if (call.provider === LIVEKIT_CALL_PROVIDER && call.isGroupCall === true) {
      finishVoiceCall({
        status: 'declined',
        notifySfuServer: false,
      });
      return;
    }
    sendVoiceCallSignalQuietly('reject', { reason: 'declined' }, call);
    finishVoiceCall({ status: 'declined' });
  };

  const cancelVoiceCall = () => {
    const call = voiceCallRuntimeRef.current.state || {};
    if (['incoming', 'securing'].includes(call.status)) {
      rejectVoiceCall();
      return;
    }
    if (call.status && call.status !== 'idle') {
      const hadConnected = Boolean(call.connectedAt);
      if (call.provider === LIVEKIT_CALL_PROVIDER && (hadConnected || call.rejoining || call.sfuJoined)) {
        finishVoiceCall({
          status: 'ended',
          sendSignal: false,
          endForEveryone: call.isGroupCall && (call.startedByUserId
            ? String(call.startedByUserId) === String(currentUserId)
            : call.direction === 'outgoing'),
          writeHistory: true,
        });
        return;
      }
      finishVoiceCall({
        status: hadConnected ? 'ended' : 'cancelled',
        sendSignal: true,
        writeHistory: true,
      });
    }
  };

  const toggleVoiceCallMute = async () => {
    const session = voiceCallRuntimeRef.current.session;
    const call = voiceCallRuntimeRef.current.state || {};
    const nextMuted = !(call.muted === true);
    if (!session?.setMuted) return call.muted === true;
    try {
      await session.setMuted(nextMuted);
    } catch (error) {
      traceMessengerV2Call('mute_update_failed', {
        callId: call.callId ?? null,
        error: String(error?.message || error),
      });
      setVoiceCall((current) =>
        current.callId === call.callId
          ? { ...current, error: String(error?.message || error) }
          : current,
      );
      return call.muted === true;
    }
    setVoiceCall((current) => ({
      ...current,
      muted: nextMuted,
      error: null,
    }));
    if (call.callId && call.status && call.status !== 'idle') {
      sendVoiceCallSignalQuietly(
        'media-state',
        {
          muted: nextMuted,
          cameraEnabled: call.mode === 'video' ? call.cameraEnabled === true : false,
        },
        call,
      );
    }
    return nextMuted;
  };

  const toggleVoiceCallSound = async () => {
    const { session, state: call } = voiceCallRuntimeRef.current;
    if (!session || !call?.callId || call.status === 'idle' || call.soundMutePending) return;
    const soundMuted = !call.soundMuted;
    // Silence playback immediately; publishing the peer indicator must not delay local mute.
    setVoiceCall(current => ({ ...current, soundMuted, soundMutePending: true, error: null }));
    try {
      await session.setSoundMuted(soundMuted);
    } catch (error) {
      if (voiceCallRuntimeRef.current.session === session) {
        setVoiceCall(current => ({ ...current, error: String(error?.message || error) }));
      }
    } finally {
      if (voiceCallRuntimeRef.current.session === session) {
        setVoiceCall(current => ({ ...current, soundMutePending: false }));
      }
    }
  };

  const cycleVoiceCallAudioOutput = async () => {
    const session = voiceCallRuntimeRef.current.session;
    const call = voiceCallRuntimeRef.current.state || {};
    if (!session?.setAudioOutput || !call.callId || call.status === 'idle') return false;

    const snapshot = await session
      .recoverMedia({ microphoneEnabled: call.muted !== true })
      .catch(() => null);
    const devices = snapshot?.audioOutputDevices ?? call.audioOutputDevices ?? [];
    if (devices.length < 2) {
      applyVoiceCallMediaSnapshot(call.callId, {
        ...snapshot,
        audioOutputDevices: devices,
      });
      return false;
    }

    const currentDeviceId =
      snapshot?.audioOutputDeviceId ?? call.audioOutputDeviceId ?? devices[0]?.deviceId;
    const currentIndex = Math.max(
      0,
      devices.findIndex((device) => device.deviceId === currentDeviceId),
    );
    const nextDevice = devices[(currentIndex + 1) % devices.length];
    const changed = await session.setAudioOutput(nextDevice.deviceId);
    if (changed) {
      setVoiceCall((current) =>
        current.callId === call.callId
          ? {
              ...current,
              audioOutputDevices: devices,
              audioOutputDeviceId: nextDevice.deviceId,
              audioOutputSupported: true,
            }
          : current,
      );
    }
    return changed;
  };

  const toggleVoiceCallScreenShare = () => {
    const { session, state: call } = voiceCallRuntimeRef.current;
    if (!session?.startScreenShare || !call?.callId) return Promise.resolve(false);
    if (session.screenShareOperation) return session.stopScreenShare();
    if (call.status !== 'active') return Promise.resolve(false);
    return session.startScreenShare();
  };

  const toggleVoiceCallCamera = async () => {
    const session = voiceCallRuntimeRef.current.session;
    const call = voiceCallRuntimeRef.current.state || {};
    const nextEnabled = !(call.cameraEnabled === true);
    if (!session?.setCameraEnabled) return call.cameraEnabled === true;
    try {
      await session.setCameraEnabled(nextEnabled);
    } catch (error) {
      traceMessengerV2Call('camera_update_failed', {
        callId: call.callId ?? null,
        error: String(error?.message || error),
      });
      setVoiceCall((current) =>
        current.callId === call.callId
          ? { ...current, error: String(error?.message || error) }
          : current,
      );
      return call.cameraEnabled === true;
    }
    setVoiceCall((current) => ({
      ...current,
      cameraEnabled: nextEnabled,
      error: null,
    }));
    if (call.callId && call.status && call.status !== 'idle') {
      sendVoiceCallSignalQuietly(
        'media-state',
        {
          muted: call.muted === true,
          cameraEnabled: nextEnabled,
        },
        call,
      );
    }
    return nextEnabled;
  };

  const toggleVoiceCallBackgroundBlur = async () => {
    const session = voiceCallRuntimeRef.current.session;
    const call = voiceCallRuntimeRef.current.state || {};
    if (!session || call.mode !== 'video' || !call.callId || call.status === 'idle') {
      return 'none';
    }
    const nextEffect = call.videoBackgroundEffect === 'blur' ? 'none' : 'blur';
    setVoiceCall((current) =>
      current.callId === call.callId
        ? {
            ...current,
            videoBackgroundEffectPending: true,
            videoBackgroundEffectError: null,
          }
        : current,
    );
    try {
      const appliedEffect = await session.setVideoBackgroundEffect(nextEffect);
      setVoiceCall((current) =>
        current.callId === call.callId
          ? {
              ...current,
              videoBackgroundEffect: appliedEffect,
              videoBackgroundEffectPending: false,
              videoBackgroundEffectError: null,
              localStream: session.localStream || current.localStream,
            }
          : current,
      );
      return appliedEffect;
    } catch (error) {
      setVoiceCall((current) =>
        current.callId === call.callId
          ? {
              ...current,
              videoBackgroundEffectPending: false,
              videoBackgroundEffectError: error,
            }
          : current,
      );
      throw error;
    }
  };

  const publishComposerActivity = ({ conversationId = null, activity = null } = {}) => {
    const normalizedConversationId =
      toStringOrNull(conversationId) ??
      toStringOrNull(stateRef.current.activeConversationId ?? null);
    const normalizedCurrentUserId = toStringOrNull(currentUserId);
    localComposerActivityPublisherRef.current?.publish({
      userId: normalizedCurrentUserId,
      conversationId: normalizedConversationId,
      activity,
    });
  };

  const refreshConversationMetadata = async ({ conversationId = null } = {}) => {
    const normalizedConversationId =
      toStringOrNull(conversationId) ??
      toStringOrNull(stateRef.current.activeConversationId ?? null);
    if (!normalizedConversationId) {
      return null;
    }

    const conversation = await fetchMessengerV2Conversation({
      conversationId: normalizedConversationId,
    });
    const normalizedConversation = normalizeMessengerV2Conversation(conversation);
    if (!normalizedConversation?.id) {
      return null;
    }

    dispatch({
      type: 'conversation:metadata',
      conversation: normalizedConversation,
    });

    return normalizedConversation;
  };

  const applyConversationMetadata = (conversation = null) => {
    const normalizedConversation = normalizeMessengerV2Conversation(conversation);
    if (!normalizedConversation?.id) {
      return null;
    }

    dispatch({
      type: 'conversation:metadata',
      conversation: normalizedConversation,
    });

    return normalizedConversation;
  };

  const clearThreadConversation = async ({ conversationId = null } = {}) => {
    const normalizedConversationId =
      toStringOrNull(conversationId) ??
      toStringOrNull(stateRef.current.activeConversationId ?? null);
    if (!normalizedConversationId) {
      throw new Error('Active V2 thread is not ready to clear');
    }

    const snapshot = stateRef.current || {};
    const currentTimelineRecord =
      toStringOrNull(snapshot.activeConversationId ?? null) === normalizedConversationId
        ? snapshot.timeline
        : (snapshot.timelinesByConversation?.[normalizedConversationId] ?? null);
    const result = await clearMessengerV2Conversation({ conversationId: normalizedConversationId });
    dispatch({
      type: 'timeline:clearConversation',
      conversationId: normalizedConversationId,
      groupId: currentTimelineRecord?.groupId ?? null,
    });
    return result;
  };

  const deleteDirectThreadConversation = async ({ conversationId = null } = {}) => {
    const normalizedConversationId =
      toStringOrNull(conversationId) ??
      toStringOrNull(stateRef.current.activeConversationId ?? null);
    if (!normalizedConversationId) {
      throw new Error('Active V2 thread is not ready to delete');
    }

    const snapshot = stateRef.current || {};
    const conversation = (Array.isArray(snapshot.conversations) ? snapshot.conversations : []).find(
      (item) => toStringOrNull(item?.id ?? null) === normalizedConversationId,
    );
    if (conversation?.kind && conversation.kind !== 'direct') {
      throw new Error('Only direct V2 conversations can be deleted here');
    }

    const result = await deleteMessengerV2DirectConversation({
      conversationId: normalizedConversationId,
    });
    dispatch({ type: 'conversation:hide', conversationId: normalizedConversationId });
    return result;
  };

  const hideThreadConversation = ({ conversationId = null } = {}) => {
    const normalizedConversationId =
      toStringOrNull(conversationId) ??
      toStringOrNull(stateRef.current.activeConversationId ?? null);
    if (!normalizedConversationId) {
      return;
    }

    dispatch({ type: 'conversation:hide', conversationId: normalizedConversationId });
  };

  useEffect(() => {
    const voiceCallRuntime = voiceCallRuntimeRef.current;
    const remoteIceBuffer = voiceCallRuntime.remoteIceBuffer;
    const processedSignalKeys = voiceCallRuntime.processedSignalKeys;
    const silentSignalFrontiers = voiceCallRuntime.silentSignalFrontiers;
    const pendingSignalTimers = voiceCallRuntime.pendingEncryptedSignalTimers;
    const pendingSignals = voiceCallRuntime.pendingEncryptedSignals;

    return () => {
      clearVoiceCallRingTimer();
      clearVoiceCallSetupTimer();
      closeVoiceCallSession();
      stopVoiceCallProgressTone();
      remoteIceBuffer.clear();
      processedSignalKeys.clear();
      voiceCallRuntime.processedSignalOrder = [];
      silentSignalFrontiers.clear();
      pendingSignalTimers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      pendingSignalTimers.clear();
      pendingSignals.clear();
    };
  }, [
    clearVoiceCallRingTimer,
    clearVoiceCallSetupTimer,
    closeVoiceCallSession,
    stopVoiceCallProgressTone,
  ]);

  return {
    currentUser,
    authSnapshot,
    isAuthorized,
    state,
    updateViewField,
    configureView,
    retryBootstrap,
    reloadContacts,
    startConversation,
    openGroupConversation,
    adoptConversation,
    selectConversation,
    reloadActiveThread,
    loadOlderTimelineMessages,
    voiceCall,
    ongoingGroupCalls: state.ongoingGroupCalls || [],
    joinOngoingGroupCall,
    startVoiceCall,
    startVideoCall,
    acceptVoiceCall,
    rejectVoiceCall,
    cancelVoiceCall,
    toggleVoiceCallMute,
    toggleVoiceCallSound,
    cycleVoiceCallAudioOutput,
    toggleVoiceCallCamera,
    toggleVoiceCallScreenShare,
    toggleVoiceCallBackgroundBlur,
    setVoiceCallPresentation,
    publishComposerActivity,
    appendLocalTimelineItem,
    markLocalTimelineItemFailed,
    retryThreadMessage,
    retryReactionMessage,
    retryAttachmentMessage,
    pauseAttachmentMessage,
    resumeAttachmentMessage,
    sendAttachmentMessage,
    sendThreadMessage,
    editThreadMessage,
    deleteThreadMessage,
    reactToThreadMessage,
    recordEmojiUse,
    clearThreadConversation,
    deleteDirectThreadConversation,
    hideThreadConversation,
    refreshConversationMetadata,
    applyConversationMetadata,
  };
};

export default useMessengerV2Controller;
